import { PDFDocument, degrees, PDFName, rgb } from 'pdf-lib';
import {
  Document as DocxDocument,
  Packer as DocxPacker,
  Paragraph as DocxParagraph,
  TextRun as DocxTextRun,
  Table as DocxTable,
  TableRow as DocxTableRow,
  TableCell as DocxTableCell,
  WidthType as DocxWidthType,
  AlignmentType as DocxAlignmentType,
  BorderStyle as DocxBorderStyle
} from 'docx';
import { byId, isAr, formatBytes, saveBlob } from './extra-common.js';
import { trackEvent } from '../core/analytics.js';
import { pdfjs, getPdfDocumentParams } from './pdf-engine.js';
import { showAlert, showConfirm, showPrompt } from '../core/modal.js';

// DOM References
const dropzone = byId('pe-dropzone');
const fileInput = byId('pe-file-input');
const dropzoneDefault = byId('pe-dropzone-default');
const dropzoneLoading = byId('pe-dropzone-loading');
const loadingFilename = byId('pe-loading-filename');
const progressBar = byId('pe-progress-bar');
const loadingStatus = byId('pe-loading-status');
const loadingPct = byId('pe-loading-pct');

const workspace = byId('pe-workspace');
const docTitle = byId('pe-doc-title');
const docSize = byId('pe-doc-size');
const undoBtn = byId('pe-undo-btn');
const redoBtn = byId('pe-redo-btn');
const resetBtn = byId('pe-reset-btn');
const exportBtn = byId('pe-export-btn');
const exportBtnText = byId('pe-export-btn-text');
const exportDocxBtn = byId('pe-export-docx-btn');
const exportDocxBtnText = byId('pe-export-docx-btn-text');

// Mode Switcher Controls
const modeWordBtn = byId('pe-mode-word-btn');
const modeCanvasBtn = byId('pe-mode-canvas-btn');
const docPage = byId('pe-doc-page');

// Word-Style Tabbed Ribbon
const ribbonTabs = document.querySelectorAll('.pe-ribbon-tab');
const ribbonPanes = {
  home: byId('pe-pane-home'),
  insert: byId('pe-pane-insert'),
  draw: byId('pe-pane-draw'),
  pages: byId('pe-pane-pages')
};

function switchRibbonTab(tabName) {
  if (!tabName) return;
  ribbonTabs.forEach((tab) => {
    const isActive = tab.dataset.ribbon === tabName;
    tab.classList.toggle('active', isActive);
    tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });
  Object.entries(ribbonPanes).forEach(([name, pane]) => {
    if (!pane) return;
    if (name === tabName) {
      pane.classList.add('active');
      pane.removeAttribute('hidden');
    } else {
      pane.classList.remove('active');
      pane.setAttribute('hidden', '');
    }
  });
}

ribbonTabs.forEach((tab) => {
  tab.addEventListener('click', () => switchRibbonTab(tab.dataset.ribbon));
});

// Tools Ribbon & Context Options
const primaryTools = document.querySelectorAll('.pe-tool-btn[data-tool]');
const optionsEditText = byId('pe-options-edit-text');
const optionsSelected = byId('pe-options-selected');
const optionsStamp = byId('pe-options-stamp');
const optionsMark = byId('pe-options-mark');
const optionsText = byId('pe-options-text');
const optionsDraw = byId('pe-options-draw');
const optionsHighlight = byId('pe-options-highlight');
const optionsShape = byId('pe-options-shape');
const optionsNote = byId('pe-options-note');
const optionsRedact = byId('pe-options-redact');
const optionsEraser = byId('pe-options-eraser');

// Selected Element Controls
const selectedLabel = byId('pe-selected-label');
const selectedOpacityInput = byId('pe-selected-opacity');
const selectedOpacityVal = byId('pe-selected-opacity-val');
const selectedBringFrontBtn = byId('pe-selected-bring-front');
const selectedSendBackBtn = byId('pe-selected-send-back');
const selectedDuplicateBtn = byId('pe-selected-duplicate');
const deleteSelectedBtn = byId('pe-delete-selected');

// In-Place Word-Like Text Formatting Toolbar
const detectedFontSelect = byId('pe-detected-font');
const loadLocalFontsBtn = byId('pe-load-local-fonts');
const fontSizeDecBtn = byId('pe-font-size-dec');
const fontSizeIncBtn = byId('pe-font-size-inc');
const detectedSizeInput = byId('pe-detected-size');
const detectedBoldBtn = byId('pe-detected-bold');
const detectedItalicBtn = byId('pe-detected-italic');
const detectedUnderlineBtn = byId('pe-detected-underline');
const detectedStrikeBtn = byId('pe-detected-strike');
const alignRightBtn = byId('pe-align-right');
const alignCenterBtn = byId('pe-align-center');
const alignLeftBtn = byId('pe-align-left');
const detectedColorInput = byId('pe-detected-color');
const detectedBgInput = byId('pe-detected-bg');
const tableControls = byId('pe-table-controls');
const tableAddRowBtn = byId('pe-table-add-row');
const tableDelRowBtn = byId('pe-table-del-row');
const deleteCurrentTextBtn = byId('pe-delete-current-text');
const restoreTextsBtn = byId('pe-restore-texts');

// Stamps Controls
const stampChips = document.querySelectorAll('.pe-stamp-chip');
const customStampInput = byId('pe-custom-stamp-input');
const customStampColor = byId('pe-custom-stamp-color');
const insertCustomStampBtn = byId('pe-insert-custom-stamp');

// Form Marks Controls
const markChips = document.querySelectorAll('.pe-mark-chip');
const markColorInput = byId('pe-mark-color');
const markSizeSelect = byId('pe-mark-size');

// Text Options (for adding new text box)
const fontFamilySelect = byId('pe-font-family');
const fontSizeSelect = byId('pe-font-size');
const textColorInput = byId('pe-text-color');
const textBoldBtn = byId('pe-text-bold');
const textItalicBtn = byId('pe-text-italic');

// Draw Options
const drawColorInput = byId('pe-draw-color');
const drawSizeBtns = document.querySelectorAll('.pe-size-btn');

// Highlight Options
const highlightThicknessSelect = byId('pe-highlight-thickness');
const highlightColorChips = document.querySelectorAll('.pe-color-chip[data-color]');

// Shape Options
const shapeTypeSelect = byId('pe-shape-type');
const shapeStrokeInput = byId('pe-shape-stroke');
const shapeWidthSelect = byId('pe-shape-width');
const shapeFillSelect = byId('pe-shape-fill');
const shapeDashSelect = byId('pe-shape-dash');

// Page Management Controls
const rotatePageBtn = byId('pe-rotate-page-btn');
const duplicatePageBtn = byId('pe-duplicate-page-btn');
const addPageBtn = byId('pe-add-page-btn');
const deletePageBtn = byId('pe-delete-page-btn');

// Saved Signature Controls
const sigSaveStorageCheckbox = byId('pe-sig-save-storage');
const quickSigBtn = byId('pe-quick-sig-btn');

// Sticky Note Options
const noteColorChips = document.querySelectorAll('.pe-color-chip[data-note-color]');
const addNoteBtn = byId('pe-add-note-btn');

// Redact Options
const redactBtns = document.querySelectorAll('.pe-redact-btn');

// Image Upload
const imageUploadInput = byId('pe-image-upload');

// Sidebar & Viewport
const sidebar = byId('pe-sidebar');
const sidebarTotal = byId('pe-sidebar-total');
const sidebarCloseBtn = byId('pe-sidebar-close');
const toggleSidebarBtn = byId('pe-toggle-sidebar');
const thumbnailsList = byId('pe-thumbnails-list');

const viewportContainer = byId('pe-viewport-container');
const stage = byId('pe-stage');
const pdfCanvas = byId('pe-pdf-canvas');
const textLayer = byId('pe-text-layer');
const drawCanvas = byId('pe-draw-canvas');
const overlay = byId('pe-overlay');

// Bottom Bar Controls
const prevBtn = byId('pe-prev-btn');
const nextBtn = byId('pe-next-btn');
const currentPageInput = byId('pe-current-page-input');
const totalPagesDisplay = byId('pe-total-pages-display');
const fitWidthBtn = byId('pe-fit-width');
const fitPageBtn = byId('pe-fit-page');
const zoomOutBtn = byId('pe-zoom-out');
const zoomInBtn = byId('pe-zoom-in');
const zoomValueLabel = byId('pe-zoom-value');
const fullscreenBtn = byId('pe-fullscreen-btn');

// Signature Dialog
const sigDialog = byId('pe-sig-dialog');
const sigCloseBtn = byId('pe-sig-close');
const sigTabBtns = document.querySelectorAll('.pe-tab-btn');
const sigTabDraw = byId('pe-sig-tab-draw');
const sigTabType = byId('pe-sig-tab-type');
const sigCanvas = byId('pe-sig-canvas');
const sigClearBtn = byId('pe-sig-clear');
const sigColorChips = document.querySelectorAll('.pe-color-chip[data-sig-color]');
const sigTypeInput = byId('pe-sig-type-input');
const sigTypePreview = byId('pe-sig-type-preview');
const sigInsertBtn = byId('pe-sig-insert');

// State Variables
let currentPdf = null;
let originalBytes = null;
let currentFilename = 'document.pdf';
let totalPages = 1;
let currentPageNumber = 1;
let currentZoom = 1.0;
let currentRenderTask = null;
let activeTool = 'edit-text'; // Default to in-place text editing

let viewMode = 'canvas'; // 'canvas' | 'word' - Defaults to 100% authentic PDF canvas view
let currentActiveCell = null;
let currentPageObj = null;
const pageDocData = new Map(); // pageNum -> { blocks: [], stageWidth, stageHeight }

// In-Place Text Modifications: pageNum -> Map(id -> modObject)
const pageModifiedTexts = new Map();
let currentActiveTextId = null;
let activeTextEditContext = null;
let isSyncingToolbar = false;

// Overlay Annotations: pageNum -> Array<ann>
const pageAnnotations = new Map();

// History stack
let undoStack = [];
let redoStack = [];
let selectedAnnotationId = null;
let docEmbeddedFontCatalog = [];
const docFontResourceMap = new Map();
const cachedPageFonts = new Map();
let defaultExtractedFont = '';
const pageDetectedImages = new Map();

// Interaction State
let isInteracting = false;
let startX = 0;
let startY = 0;
let currentStroke = null;
let activeDrag = null;

// Current Active Settings
let activeMarkSymbol = '✓';
let activeMarkColor = '#059669';
let activeMarkSize = 26;
let activeNoteColor = '#fef08a';

const textSettings = {
  fontFamily: 'Cairo, sans-serif',
  fontSize: 16,
  color: '#111827',
  bold: false,
  italic: false
};

const drawSettings = {
  size: 2,
  color: '#2563eb'
};

const highlightSettings = {
  color: 'rgba(250, 204, 21, 0.45)',
  thickness: 22
};

const shapeSettings = {
  type: 'rect',
  strokeColor: '#ef4444',
  strokeWidth: 4,
  fill: 'none',
  dash: 'solid'
};

let redactColor = '#000000';
let sigColor = '#000000';
let sigTab = 'draw';
let isSigDrawing = false;
let sigLastX = 0;
let sigLastY = 0;

// ==========================================
// File Loading & Dropzone Handlers
// ==========================================

function updateLoadingProgress(pct, statusText) {
  if (progressBar) progressBar.style.width = `${pct}%`;
  if (loadingPct) loadingPct.textContent = `${pct}%`;
  if (loadingStatus && statusText) loadingStatus.textContent = statusText;
}

// ==========================================
// CTM Matrix & PDF Embedded Font / Image Extraction Helpers
// ==========================================

function multiplyMatrices(m1, m2) {
  return [
    m1[0] * m2[0] + m1[2] * m2[1],
    m1[1] * m2[0] + m1[3] * m2[1],
    m1[0] * m2[2] + m1[2] * m2[3],
    m1[1] * m2[2] + m1[3] * m2[3],
    m1[0] * m2[4] + m1[2] * m2[5] + m1[4],
    m1[1] * m2[4] + m1[3] * m2[5] + m1[5]
  ];
}

function applyTransform(p, m) {
  return [
    p[0] * m[0] + p[1] * m[2] + m[4],
    p[0] * m[1] + p[1] * m[3] + m[5]
  ];
}

const isInternal = (str) => !str || /^g_d\d+_f\d+$/i.test(str) || /^f\d+$/i.test(str) || /^font\d+$/i.test(str);

function cleanPdfFontName(raw) {
  if (!raw || typeof raw !== 'string') return '';
  let str = raw.replace(/^\//, '').replace(/^[A-Z]{6}\+/, '').trim();
  str = str.replace(/(?:PSMT|BoldMT|ItalicMT|MT|LT|PS)$/i, '');
  let base = str.split(/[-_,]/)[0].trim();
  base = base.replace(/(?:ExtraBold|UltraBold|SemiBold|DemiBold|Demibold|Bold|Italic|Oblique|Black|Heavy|Light|Regular|Medium)$/i, '').trim();
  base = base.replace(/PS$/i, '').trim();
  if (!base) base = str.split(/[-_,]/)[0].trim();
  const spaced = base.replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  return spaced || base || str;
}

function normalizeHexColor(col, fallback = '#111827') {
  if (!col || typeof col !== 'string') return fallback;
  const s = col.trim();
  if (/^#[0-9a-fA-F]{6}$/i.test(s)) return s.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/i.test(s)) {
    return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`.toLowerCase();
  }
  const rgbMatch = s.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    const r = Math.min(255, parseInt(rgbMatch[1], 10)).toString(16).padStart(2, '0');
    const g = Math.min(255, parseInt(rgbMatch[2], 10)).toString(16).padStart(2, '0');
    const b = Math.min(255, parseInt(rgbMatch[3], 10)).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`.toLowerCase();
  }
  return fallback;
}

function extractFontsFromPdfDoc(pDoc) {
  const fNames = new Set();
  docFontResourceMap.clear();
  const isInternal = (str) => !str || /^g_d\d+_f\d+$/i.test(str) || /^f\d+$/i.test(str) || /^font\d+$/i.test(str);

  const addFontCandidate = (raw, resKey = null) => {
    if (!raw || typeof raw !== 'string') return;
    const clean = raw.replace(/^\//, '').trim();
    if (clean && !isInternal(clean)) {
      fNames.add(clean);
      if (resKey) {
        const kClean = String(resKey).replace(/^\//, '').toLowerCase();
        docFontResourceMap.set(kClean, clean);
      }
    }
  };

  const inspectFontDict = (fDict, resKey = null) => {
    if (!fDict) return;
    const resolved = (typeof pDoc.context?.lookup === 'function') ? pDoc.context.lookup(fDict) : fDict;
    if (!resolved || typeof resolved.lookup !== 'function') return;

    const baseFont = resolved.lookup(PDFName.of('BaseFont'))?.decodeText?.() || resolved.lookup(PDFName.of('BaseFont'))?.asString?.();
    const fontName = resolved.lookup(PDFName.of('FontName'))?.decodeText?.() || resolved.lookup(PDFName.of('FontName'))?.asString?.();
    const fontFamily = resolved.lookup(PDFName.of('FontFamily'))?.decodeText?.() || resolved.lookup(PDFName.of('FontFamily'))?.asString?.();

    if (baseFont) addFontCandidate(baseFont, resKey);
    if (fontName) addFontCandidate(fontName, resKey);
    if (fontFamily) addFontCandidate(fontFamily, resKey);

    const fontDesc = resolved.lookup(PDFName.of('FontDescriptor'));
    if (fontDesc) {
      const fd = (typeof pDoc.context?.lookup === 'function') ? pDoc.context.lookup(fontDesc) : fontDesc;
      if (fd && typeof fd.lookup === 'function') {
        const fdName = fd.lookup(PDFName.of('FontName'))?.decodeText?.() || fd.lookup(PDFName.of('FontName'))?.asString?.();
        const fdFamily = fd.lookup(PDFName.of('FontFamily'))?.decodeText?.() || fd.lookup(PDFName.of('FontFamily'))?.asString?.();
        if (fdName) addFontCandidate(fdName, resKey);
        if (fdFamily) addFontCandidate(fdFamily, resKey);
      }
    }

    const desc = resolved.lookup(PDFName.of('DescendantFonts'));
    if (desc) {
      const descResolved = (typeof pDoc.context?.lookup === 'function') ? pDoc.context.lookup(desc) : desc;
      const arr = Array.isArray(descResolved?.array) ? descResolved.array : (Array.isArray(descResolved) ? descResolved : []);
      arr.forEach((dfRef) => {
        const df = (typeof pDoc.context?.lookup === 'function') ? pDoc.context.lookup(dfRef) : dfRef;
        if (df && typeof df.lookup === 'function') {
          const dfBase = df.lookup(PDFName.of('BaseFont'))?.decodeText?.() || df.lookup(PDFName.of('BaseFont'))?.asString?.();
          const dfName = df.lookup(PDFName.of('FontName'))?.decodeText?.() || df.lookup(PDFName.of('FontName'))?.asString?.();
          if (dfBase) addFontCandidate(dfBase, resKey);
          if (dfName) addFontCandidate(dfName, resKey);
          const dfDesc = df.lookup(PDFName.of('FontDescriptor'));
          if (dfDesc) {
            const dfd = (typeof pDoc.context?.lookup === 'function') ? pDoc.context.lookup(dfDesc) : dfDesc;
            if (dfd && typeof dfd.lookup === 'function') {
              const dfdName = dfd.lookup(PDFName.of('FontName'))?.decodeText?.() || dfd.lookup(PDFName.of('FontName'))?.asString?.();
              const dfdFamily = dfd.lookup(PDFName.of('FontFamily'))?.decodeText?.() || dfd.lookup(PDFName.of('FontFamily'))?.asString?.();
              if (dfdName) addFontCandidate(dfdName, resKey);
              if (dfdFamily) addFontCandidate(dfdFamily, resKey);
            }
          }
        }
      });
    }
  };

  try {
    pDoc.getPages().forEach((p) => {
      const rawRes = p.node.get(PDFName.of('Resources'));
      const res = (typeof pDoc.context?.lookup === 'function') ? pDoc.context.lookup(rawRes) : rawRes;
      if (res && typeof res.lookup === 'function') {
        const fDictRef = res.lookup(PDFName.of('Font'));
        const fDict = (typeof pDoc.context?.lookup === 'function') ? pDoc.context.lookup(fDictRef) : fDictRef;
        if (fDict && typeof fDict.entries === 'function') {
          for (const [k] of fDict.entries()) {
            const keyStr = k.decodeText ? k.decodeText() : (k.asString ? k.asString() : String(k));
            inspectFontDict(fDict.lookup(k), keyStr);
          }
        }

        const xDictRef = res.lookup(PDFName.of('XObject'));
        const xDict = (typeof pDoc.context?.lookup === 'function') ? pDoc.context.lookup(xDictRef) : xDictRef;
        if (xDict && typeof xDict.entries === 'function') {
          for (const [xk] of xDict.entries()) {
            const xObj = (typeof pDoc.context?.lookup === 'function') ? pDoc.context.lookup(xDict.lookup(xk)) : xDict.lookup(xk);
            const subResRef = xObj?.lookup?.(PDFName.of('Resources'));
            const subRes = (typeof pDoc.context?.lookup === 'function') ? pDoc.context.lookup(subResRef) : subResRef;
            if (subRes && typeof subRes.lookup === 'function') {
              const subFDictRef = subRes.lookup(PDFName.of('Font'));
              const subFDict = (typeof pDoc.context?.lookup === 'function') ? pDoc.context.lookup(subFDictRef) : subFDictRef;
              if (subFDict && typeof subFDict.entries === 'function') {
                for (const [sk] of subFDict.entries()) {
                  const subKeyStr = sk.decodeText ? sk.decodeText() : (sk.asString ? sk.asString() : String(sk));
                  inspectFontDict(subFDict.lookup(sk), subKeyStr);
                }
              }
            }
          }
        }
      }
    });

    if (pDoc.context && typeof pDoc.context.enumerateIndirectObjects === 'function') {
      pDoc.context.enumerateIndirectObjects().forEach(([ref, obj]) => {
        if (obj && typeof obj.lookup === 'function') {
          inspectFontDict(obj, null);
        }
      });
    }
  } catch (err) {
    console.warn('Font extraction non-fatal notice:', err);
  }

  return Array.from(fNames);
}

function populateExtractedFontsInUI() {
  if (!docEmbeddedFontCatalog || !docEmbeddedFontCatalog.length) return;

  const cleanFonts = [];
  const seen = new Set();
  const isInternal = (str) => !str || /^g_d\d+_f\d+$/i.test(str) || /^f\d+$/i.test(str) || /^font\d+$/i.test(str);

  for (const raw of docEmbeddedFontCatalog) {
    const clean = cleanPdfFontName(raw);
    if (clean && !seen.has(clean.toLowerCase()) && !isInternal(clean)) {
      seen.add(clean.toLowerCase());
      const rawClean = raw.replace(/^[A-Z]{6}\+/, '').replace(/^\//, '');
      cleanFonts.push({
        raw,
        clean,
        cssVal: `'${clean}', '${rawClean}', system-ui, sans-serif`
      });
    }
  }

  if (!cleanFonts.length) return;

  [detectedFontSelect, fontFamilySelect].forEach((select) => {
    if (!select) return;
    let group = select.querySelector('optgroup[data-group="extracted-pdf"]');
    if (!group) {
      group = document.createElement('optgroup');
      group.dataset.group = 'extracted-pdf';
      group.label = isAr ? '🎯 خطوط مستخرجة من ملف PDF' : '🎯 Fonts Extracted from PDF';
      const firstOptgroup = select.querySelector('optgroup');
      if (firstOptgroup) {
        select.insertBefore(group, firstOptgroup);
      } else {
        select.prepend(group);
      }
    }
    group.replaceChildren();

    cleanFonts.forEach((cf, idx) => {
      const opt = document.createElement('option');
      opt.value = cf.cssVal;
      opt.textContent = `🎯 ${cf.clean} (${isAr ? 'مستخرج من PDF' : 'from PDF'})${idx === 0 ? ` [${isAr ? 'افتراضي' : 'Default'}]` : ''}`;
      group.append(opt);
    });
  });

  const primary = cleanFonts[0];
  if (primary) {
    defaultExtractedFont = primary.cssVal;
    if (detectedFontSelect) detectedFontSelect.value = primary.cssVal;
    if (fontFamilySelect) fontFamilySelect.value = primary.cssVal;
    textSettings.fontFamily = primary.cssVal;

    [detectedFontSelect, fontFamilySelect].forEach((select) => {
      if (select && select._kzCustomSelect && typeof select._kzCustomSelect.update === 'function') {
        select._kzCustomSelect.update();
      }
    });

    const wrapper = detectedFontSelect?.closest('.kz-select-wrapper');
    const valSpan = wrapper?.querySelector('.kz-select-value');
    if (valSpan) {
      valSpan.textContent = primary.clean;
      valSpan.style.fontFamily = primary.cssVal;
    }
  }
}

function extractPageImageBounds(opList, viewport, stageWidth, stageHeight) {
  if (!opList || !opList.fnArray || !opList.argsArray) return [];

  const images = [];
  const matrixStack = [[1, 0, 0, 1, 0, 0]];
  let currentMatrix = [1, 0, 0, 1, 0, 0];

  const fnArray = opList.fnArray;
  const argsArray = opList.argsArray;

  for (let i = 0; i < fnArray.length; i++) {
    const fn = fnArray[i];
    const args = argsArray[i];

    if (fn === pdfjs.OPS.save) {
      matrixStack.push([...currentMatrix]);
    } else if (fn === pdfjs.OPS.restore) {
      if (matrixStack.length > 1) {
        currentMatrix = matrixStack.pop();
      } else {
        currentMatrix = [1, 0, 0, 1, 0, 0];
      }
    } else if (fn === pdfjs.OPS.transform) {
      if (args && args.length === 6) {
        currentMatrix = multiplyMatrices(currentMatrix, args);
      }
    } else if (
      fn === pdfjs.OPS.paintImageXObject ||
      fn === pdfjs.OPS.paintInlineImageXObject ||
      fn === pdfjs.OPS.paintImageMaskXObject
    ) {
      const p0 = applyTransform([0, 0], currentMatrix);
      const p1 = applyTransform([1, 0], currentMatrix);
      const p2 = applyTransform([1, 1], currentMatrix);
      const p3 = applyTransform([0, 1], currentMatrix);

      const minPdfX = Math.min(p0[0], p1[0], p2[0], p3[0]);
      const maxPdfX = Math.max(p0[0], p1[0], p2[0], p3[0]);
      const minPdfY = Math.min(p0[1], p1[1], p2[1], p3[1]);
      const maxPdfY = Math.max(p0[1], p1[1], p2[1], p3[1]);

      const ptTopLeft = viewport.convertToViewportPoint(minPdfX, maxPdfY);
      const ptBottomRight = viewport.convertToViewportPoint(maxPdfX, minPdfY);

      const left = Math.min(ptTopLeft[0], ptBottomRight[0]);
      const top = Math.min(ptTopLeft[1], ptBottomRight[1]);
      const width = Math.abs(ptBottomRight[0] - ptTopLeft[0]);
      const height = Math.abs(ptBottomRight[1] - ptTopLeft[1]);

      const isFullBackground = (width >= stageWidth * 0.92 && height >= stageHeight * 0.92);

      if (width >= 10 && height >= 10 && !isFullBackground) {
        images.push({
          id: `det-img-${i}`,
          left,
          top,
          width,
          height,
          normX: left / stageWidth,
          normY: top / stageHeight,
          normW: width / stageWidth,
          normH: height / stageHeight
        });
      }
    }
  }

  const uniqueImages = [];
  for (const img of images) {
    const dup = uniqueImages.find((u) =>
      Math.abs(u.left - img.left) < 6 &&
      Math.abs(u.top - img.top) < 6 &&
      Math.abs(u.width - img.width) < 8 &&
      Math.abs(u.height - img.height) < 8
    );
    if (!dup) uniqueImages.push(img);
  }

  return uniqueImages;
}

function liftAndMoveDetectedImage(dImg, pointerEvent, deleteImmediately = false) {
  const stageWidth = stage.clientWidth;
  const stageHeight = stage.clientHeight;
  const pxX = Math.round(dImg.normX * stageWidth);
  const pxY = Math.round(dImg.normY * stageHeight);
  const pxW = Math.round(dImg.normW * stageWidth);
  const pxH = Math.round(dImg.normH * stageHeight);

  const sampledBg = sampleCanvasBackgroundColor(pxX, pxY, pxW, pxH);
  const dpr = pdfCanvas.width / stageWidth;

  const maskAnno = {
    id: `mask-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    type: 'shape',
    shapeType: 'rect',
    x: dImg.normX,
    y: dImg.normY,
    width: dImg.normW,
    height: dImg.normH,
    strokeColor: 'transparent',
    strokeWidth: 0,
    fill: 'solid',
    fillColor: sampledBg || '#ffffff',
    opacity: 1,
    isOriginalMask: true
  };

  if (deleteImmediately) {
    // Immediately paint over the original logo on the canvas
    const pCtx = pdfCanvas.getContext('2d');
    pCtx.fillStyle = sampledBg || '#ffffff';
    pCtx.fillRect(
      Math.round(pxX * dpr),
      Math.round(pxY * dpr),
      Math.max(1, Math.round(pxW * dpr)),
      Math.max(1, Math.round(pxH * dpr))
    );

    if (!pageAnnotations.has(currentPageNumber)) {
      pageAnnotations.set(currentPageNumber, []);
    }
    const pageAnns = pageAnnotations.get(currentPageNumber);
    pageAnns.push(maskAnno);
    recordHistory({
      type: 'add',
      page: currentPageNumber,
      ann: JSON.parse(JSON.stringify(maskAnno))
    });
    renderOverlayAnnotations();
    return;
  }

  const sliceCanvas = document.createElement('canvas');
  sliceCanvas.width = Math.max(1, Math.round(pxW * dpr));
  sliceCanvas.height = Math.max(1, Math.round(pxH * dpr));
  const sCtx = sliceCanvas.getContext('2d');
  sCtx.drawImage(
    pdfCanvas,
    Math.round(pxX * dpr),
    Math.round(pxY * dpr),
    sliceCanvas.width,
    sliceCanvas.height,
    0,
    0,
    sliceCanvas.width,
    sliceCanvas.height
  );
  makeCanvasBackgroundTransparent(sliceCanvas, sampledBg || '#ffffff');
  const dataUrl = sliceCanvas.toDataURL('image/png');

  // Immediately paint over original logo pixels directly on the underlying PDF canvas
  // so the original image vanishes from its initial position immediately when lifted
  const pCtx = pdfCanvas.getContext('2d');
  pCtx.fillStyle = sampledBg || '#ffffff';
  pCtx.fillRect(
    Math.round(pxX * dpr),
    Math.round(pxY * dpr),
    sliceCanvas.width,
    sliceCanvas.height
  );

  const imageAnno = {
    id: `img-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    type: 'image',
    src: dataUrl,
    x: dImg.normX,
    y: dImg.normY,
    width: dImg.normW,
    height: dImg.normH,
    opacity: 1,
    rotation: 0,
    isFromSlice: true
  };

  if (!pageAnnotations.has(currentPageNumber)) {
    pageAnnotations.set(currentPageNumber, []);
  }
  const pageAnns = pageAnnotations.get(currentPageNumber);
  pageAnns.push(maskAnno);
  pageAnns.push(imageAnno);

  recordHistory({
    type: 'snip-move-object',
    page: currentPageNumber,
    maskAnn: maskAnno,
    imageAnn: imageAnno
  });

  renderOverlayAnnotations();
  selectAnnotation(imageAnno.id);

  if (pointerEvent) {
    const newEl = overlay.querySelector(`.pe-annotation[data-id="${imageAnno.id}"]`);
    if (newEl) {
      activeDrag = {
        id: imageAnno.id,
        startX: pointerEvent.clientX,
        startY: pointerEvent.clientY,
        origX: imageAnno.x,
        origY: imageAnno.y,
        origW: imageAnno.width,
        origH: imageAnno.height,
        isResize: false
      };
      try {
        newEl.setPointerCapture(pointerEvent.pointerId);
      } catch (err) {}
    }
  }
}

function attachDetectedImageEvents(imgEl, dImg) {
  let isDown = false;
  let startClientX = 0;
  let startClientY = 0;

  imgEl.addEventListener('pointerdown', (e) => {
    if (typeof finalizeActiveTextEdit === 'function' && activeTextEditContext) {
      finalizeActiveTextEdit();
    }
    if (activeTool === 'eraser') {
      liftAndMoveDetectedImage(dImg, null, true);
      e.stopPropagation();
      return;
    }

    if (activeTool !== 'select' && activeTool !== 'edit-text' && activeTool !== 'snip-object') return;

    isDown = true;
    startClientX = e.clientX;
    startClientY = e.clientY;
    try { imgEl.setPointerCapture(e.pointerId); } catch (err) {}
    e.stopPropagation();
  });

  imgEl.addEventListener('pointermove', (e) => {
    if (!isDown) return;
    const dist = Math.hypot(e.clientX - startClientX, e.clientY - startClientY);
    if (dist > 3) {
      isDown = false;
      try { imgEl.releasePointerCapture(e.pointerId); } catch (err) {}
      liftAndMoveDetectedImage(dImg, e);
    }
  });

  imgEl.addEventListener('pointerup', (e) => {
    if (!isDown) return;
    isDown = false;
    try { imgEl.releasePointerCapture(e.pointerId); } catch (err) {}
    liftAndMoveDetectedImage(dImg, null);
    e.stopPropagation();
  });
}

async function loadPdfFile(file) {
  if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
    showAlert(isAr ? 'يرجى اختيار ملف PDF صالح.' : 'Please select a valid PDF file.', { type: 'warning' });
    return;
  }

  try {
    dropzoneDefault.style.display = 'none';
    dropzoneLoading.style.display = 'flex';
    loadingFilename.textContent = file.name;
    updateLoadingProgress(15, isAr ? 'جارٍ قراءة الملف...' : 'Reading file...');

    const arrayBuffer = await file.arrayBuffer();
    originalBytes = new Uint8Array(arrayBuffer);
    currentFilename = file.name;

    try {
      const pDoc = await PDFDocument.load(originalBytes, { ignoreEncryption: true });
      docEmbeddedFontCatalog = extractFontsFromPdfDoc(pDoc);
    } catch (e) {
      console.warn('PDF font catalog extraction notice:', e);
      docEmbeddedFontCatalog = [];
    }

    updateLoadingProgress(45, isAr ? 'جارٍ فحص المستند واستخراج النصوص والخطوط...' : 'Scanning document and extracting text...');

    const docParams = getPdfDocumentParams(originalBytes);
    currentPdf = await pdfjs.getDocument(docParams).promise;
    totalPages = currentPdf.numPages;

    updateLoadingProgress(85, isAr ? 'جارٍ تجهيز مساحة العمل...' : 'Preparing workspace...');

    // Reset state
    pageAnnotations.clear();
    pageModifiedTexts.clear();
    pageDocData.clear();
    pageDetectedImages.clear();
    cachedPageFonts.clear();
    currentActiveCell = null;
    currentActiveTextId = null;
    undoStack = [];
    redoStack = [];
    selectedAnnotationId = null;
    currentPageNumber = 1;

    // Update UI headers
    docTitle.textContent = currentFilename;
    docSize.textContent = formatBytes(file.size);
    if (sidebarTotal) sidebarTotal.textContent = totalPages;
    totalPagesDisplay.textContent = totalPages;
    currentPageInput.max = totalPages;
    currentPageInput.value = 1;

    dropzone.hidden = true;
    workspace.hidden = false;

    if (window.innerWidth <= 840 && sidebar) {
      sidebar.classList.add('is-collapsed');
    }

    // Render initial page and thumbnails
    await renderThumbnails();
    await fitWidth();
    populateExtractedFontsInUI();
    setTool('edit-text');

    updateUndoRedoButtons();
    trackEvent('tool_started', 'pdf-editor', { pages: totalPages, size: file.size });
  } catch (err) {
    console.error('Failed to load PDF:', err);
    showAlert(isAr ? `تعذر فتح الملف: ${err.message}` : `Failed to open PDF: ${err.message}`, { type: 'error' });
    dropzoneLoading.style.display = 'none';
    dropzoneDefault.style.display = 'flex';
  }
}

// Drag & drop listeners
dropzone.addEventListener('click', () => fileInput.click());
dropzone.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    fileInput.click();
  }
});
fileInput.addEventListener('change', (e) => {
  if (e.target.files && e.target.files[0]) {
    loadPdfFile(e.target.files[0]);
  }
});

dropzone.addEventListener('dragover', (e) => {
  e.preventDefault();
  dropzone.classList.add('dragover');
});
dropzone.addEventListener('dragleave', () => dropzone.classList.remove('dragover'));
dropzone.addEventListener('drop', (e) => {
  e.preventDefault();
  dropzone.classList.remove('dragover');
  if (e.dataTransfer.files && e.dataTransfer.files[0]) {
    loadPdfFile(e.dataTransfer.files[0]);
  }
});

resetBtn.addEventListener('click', async () => {
  const confirmed = await showConfirm(
    isAr ? 'هل أنت متأكد من تغيير الملف؟ سيتم فقد أي تعديلات غير محفوظة.' : 'Change file? Unsaved edits will be lost.',
    {
      type: 'warning',
      confirmVariant: 'danger',
      confirmText: isAr ? 'تغيير الملف' : 'Change File'
    }
  );
  if (confirmed) {
    if (currentRenderTask) {
      currentRenderTask.cancel();
      currentRenderTask = null;
    }
    workspace.hidden = true;
    dropzone.hidden = false;
    dropzoneLoading.style.display = 'none';
    dropzoneDefault.style.display = 'flex';
    fileInput.value = '';
    currentPdf = null;
    originalBytes = null;
    pageAnnotations.clear();
    pageModifiedTexts.clear();
    pageDocData.clear();
    pageDetectedImages.clear();
    currentActiveCell = null;
    currentActiveTextId = null;
    currentPageObj = null;
    viewMode = 'canvas';
  }
});

// ==========================================
// Mode Switching (Word Document vs Canvas)
// ==========================================

function setViewMode(mode) {
  viewMode = mode;
  if (modeWordBtn) {
    modeWordBtn.classList.toggle('active', mode === 'word');
    modeWordBtn.setAttribute('aria-selected', mode === 'word' ? 'true' : 'false');
  }
  if (modeCanvasBtn) {
    modeCanvasBtn.classList.toggle('active', mode === 'canvas');
    modeCanvasBtn.setAttribute('aria-selected', mode === 'canvas' ? 'true' : 'false');
  }

  // CRITICAL: The authentic canvas stage must NEVER be hidden in any mode!
  // It contains 100% of the logos, images, vector ribbons, borders, and graphics!
  if (stage) stage.hidden = false;
  if (docPage) docPage.hidden = true;

  if (mode === 'word') {
    stage.classList.add('is-word-mode');
    setTool('edit-text');
  } else {
    stage.classList.remove('is-word-mode');
  }
}

if (modeWordBtn) modeWordBtn.addEventListener('click', () => setViewMode('word'));
if (modeCanvasBtn) modeCanvasBtn.addEventListener('click', () => setViewMode('canvas'));

// ==========================================
// Rendering Engine & Dynamic Page Height
// ==========================================

async function renderCurrentPage() {
  if (!currentPdf) return;
  if (typeof finalizeActiveTextEdit === 'function' && activeTextEditContext) {
    finalizeActiveTextEdit();
  }

  if (currentRenderTask) {
    currentRenderTask.cancel();
    currentRenderTask = null;
  }

  const page = await currentPdf.getPage(currentPageNumber);
  currentPageObj = page;
  const viewport = page.getViewport({ scale: currentZoom });

  const stageWidth = Math.round(viewport.width);
  const stageHeight = Math.round(viewport.height);

  stage.style.width = `${stageWidth}px`;
  stage.style.height = `${stageHeight}px`;

  if (docPage) {
    docPage.style.width = `${stageWidth}px`;
    docPage.style.minHeight = `${stageHeight}px`;
    docPage.style.maxHeight = `${stageHeight}px`;
    docPage.style.height = `${stageHeight}px`;
  }

  // Base PDF Canvas
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  pdfCanvas.width = Math.floor(stageWidth * dpr);
  pdfCanvas.height = Math.floor(stageHeight * dpr);
  pdfCanvas.style.width = `${stageWidth}px`;
  pdfCanvas.style.height = `${stageHeight}px`;

  const ctx = pdfCanvas.getContext('2d');
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, pdfCanvas.width, pdfCanvas.height);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, pdfCanvas.width, pdfCanvas.height);
  ctx.scale(dpr, dpr);

  currentRenderTask = page.render({
    canvasContext: ctx,
    viewport: viewport
  });

  try {
    await currentRenderTask.promise;
  } catch (err) {
    if (err.name !== 'RenderingCancelledException') {
      console.error('Render error:', err);
    }
  }

  // Draw Canvas for strokes & highlights
  drawCanvas.width = Math.floor(stageWidth * dpr);
  drawCanvas.height = Math.floor(stageHeight * dpr);
  drawCanvas.style.width = `${stageWidth}px`;
  drawCanvas.style.height = `${stageHeight}px`;

  // Render Word Document Sheet (DOM Table Flow Model)
  await renderWordDocumentSheet(page, viewport, stageWidth, stageHeight);

  // Render unified In-Place Text Lines & Cells (for canvas mode)
  await renderInPlaceTextLayer(page, viewport);

  // Extract embedded images from operator list for direct Word-style moving
  try {
    const opList = await page.getOperatorList();
    const imgs = extractPageImageBounds(opList, viewport, stageWidth, stageHeight);
    pageDetectedImages.set(currentPageNumber, imgs);
  } catch (err) {
    console.warn('Operator list image extraction notice:', err);
  }

  renderDrawCanvas();
  renderOverlayAnnotations();
  updatePageNavControls();
  highlightActiveThumbnail();

  // Apply active mode visibility
  setViewMode(viewMode);
}

// ==========================================
// Text Line & Table Cell Grouping Engine
// ==========================================

function detectFontProperties(fontName, styles, page = currentPageObj, sampleText = '', resolvedFonts = null) {
  let fo = resolvedFonts?.get(fontName) || cachedPageFonts.get(fontName) || null;
  if (!fo) {
    try {
      if (page?.commonObjs?.has(fontName)) {
        fo = page.commonObjs.get(fontName);
      } else if (page?.commonObjs?.has('font_' + fontName)) {
        fo = page.commonObjs.get('font_' + fontName);
      } else if (page?.objs?.has(fontName)) {
        fo = page.objs.get(fontName);
      } else if (page?.objs?.has('font_' + fontName)) {
        fo = page.objs.get('font_' + fontName);
      }
    } catch (e) {}
  }
  if (fo && !cachedPageFonts.has(fontName)) {
    cachedPageFonts.set(fontName, fo);
  }

  const style = styles && styles[fontName] ? styles[fontName] : {};
  const isInternal = (str) => !str || /^g_d\d+_f\d+$/i.test(str) || /^f\d+$/i.test(str) || /^font\d+$/i.test(str);

  // Determine actual font name for THIS item
  let rawName = '';
  if (fo?.name && !isInternal(fo.name)) {
    rawName = fo.name;
  } else if (fo?.fontDescriptor?.fontName && !isInternal(fo.fontDescriptor.fontName)) {
    rawName = fo.fontDescriptor.fontName;
  } else if (fo?.fontDescriptor?.fontFamily && !isInternal(fo.fontDescriptor.fontFamily)) {
    rawName = fo.fontDescriptor.fontFamily;
  } else if (fo?.cssFontInfo?.fontFamily && !isInternal(fo.cssFontInfo.fontFamily)) {
    rawName = fo.cssFontInfo.fontFamily;
  }

  // Exact match via docFontResourceMap (e.g. 'g_d0_f1' -> resource key 'f1')
  if (!rawName) {
    const fKeyMatch = (fontName || '').match(/([a-zA-Z]*\d+)$/);
    if (fKeyMatch && docFontResourceMap.has(fKeyMatch[1].toLowerCase())) {
      rawName = docFontResourceMap.get(fKeyMatch[1].toLowerCase());
    }
  }

  if (!rawName && fo?.loadedName && !isInternal(fo.loadedName)) {
    rawName = fo.loadedName;
  }

  if (!rawName) {
    rawName = fo?.loadedName || fontName || '';
  }

  // Clean subset prefix like ABCDEF+ or XYZABC+
  const cleanName = rawName.replace(/^\//, '').replace(/^[A-Z]{6}\+/, '').trim();

  // Aggregate candidate strings specifically for THIS font to check bold / italic traits
  const fontSpecificCandidates = [
    cleanName,
    rawName,
    fo?.name,
    fo?.loadedName,
    fo?.fontDescriptor?.fontName,
    fo?.fontDescriptor?.fontFamily,
    fo?.cssFontInfo?.fontWeight,
    fo?.cssFontInfo?.fontFamily,
    style?.fontFamily
  ].filter(Boolean).join(' ').toLowerCase();

  const hasBoldKeyword = /(?:bold|black|heavy|semibold|demi(?:bold)?|w[6-9]|700|800|900|(?:^|[-_,\s/])bd(?:[-_,\s/]|$))/i.test(fontSpecificCandidates);

  let isBold = false;
  if (typeof fo?.bold === 'boolean' && fo.bold) {
    isBold = true;
  } else if (typeof fo?.black === 'boolean' && fo.black) {
    isBold = true;
  } else if (typeof fo?.weight === 'number' && fo.weight >= 600) {
    isBold = true;
  } else if (typeof fo?.weight === 'string' && (/bold|black|heavy|700|800|900/i.test(fo.weight) || parseInt(fo.weight, 10) >= 600)) {
    isBold = true;
  } else if (typeof fo?.fontDescriptor?.fontWeight === 'number' && fo.fontDescriptor.fontWeight >= 600) {
    isBold = true;
  } else if (typeof fo?.cssFontInfo?.fontWeight === 'string' && (/bold|black|heavy|700|800|900/i.test(fo.cssFontInfo.fontWeight) || parseInt(fo.cssFontInfo.fontWeight, 10) >= 600)) {
    isBold = true;
  } else if (fo?.fontDescriptor?.flags && (fo.fontDescriptor.flags & 262144)) {
    isBold = true;
  } else if (hasBoldKeyword) {
    isBold = true;
  } else if (style?.bold) {
    isBold = true;
  }

  const hasItalicKeyword = /(?:italic|oblique|slanted|(?:^|[-_,\s/])it(?:[-_,\s/]|$))/i.test(fontSpecificCandidates);
  let isItalic = false;
  if (typeof fo?.italic === 'boolean' && fo.italic) {
    isItalic = true;
  } else if (fo?.fontDescriptor?.flags && (fo.fontDescriptor.flags & 64)) {
    isItalic = true;
  } else if (typeof fo?.fontDescriptor?.italicAngle === 'number' && fo.fontDescriptor.italicAngle !== 0) {
    isItalic = true;
  } else if (typeof fo?.cssFontInfo?.fontStyle === 'string' && /italic|oblique/i.test(fo.cssFontInfo.fontStyle)) {
    isItalic = true;
  } else if (hasItalicKeyword) {
    isItalic = true;
  } else if (style?.italic) {
    isItalic = true;
  }

  // Extract clean family name
  let cleanFamily = cleanPdfFontName(cleanName);
  const isArabic = /[\u0600-\u06FF]/.test(sampleText);

  if (!cleanFamily || isInternal(cleanFamily)) {
    if (docEmbeddedFontCatalog && docEmbeddedFontCatalog.length === 1) {
      cleanFamily = cleanPdfFontName(docEmbeddedFontCatalog[0]);
    } else {
      const famLower = (style.fontFamily || fo?.fallbackName || '').toLowerCase();
      if (famLower.includes('serif')) {
        cleanFamily = isArabic ? 'Amiri' : 'Times New Roman';
      } else if (famLower.includes('mono')) {
        cleanFamily = 'Courier New';
      } else {
        cleanFamily = isArabic ? 'Cairo' : 'Arial';
      }
    }
  }

  // Build mappedFont
  let mappedFont = isArabic ? "'Simplified Arabic', 'Cairo', sans-serif" : "'Segoe UI', Arial, sans-serif";
  let docxFont = cleanFamily || (isArabic ? 'Cairo' : 'Arial');

  const lowerFam = cleanFamily.toLowerCase();
  const lowerFallback = (style.fontFamily || fo?.fallbackName || '').toLowerCase();

  if (lowerFam.includes('traditional') || lowerFam.includes('tradarabic') || lowerFam.includes('trad_arabic')) {
    mappedFont = "'Traditional Arabic', 'Amiri', serif";
    docxFont = 'Traditional Arabic';
  } else if (lowerFam.includes('simplified') || lowerFam.includes('simparabic') || lowerFam.includes('simp_arabic')) {
    mappedFont = "'Simplified Arabic', 'Cairo', sans-serif";
    docxFont = 'Simplified Arabic';
  } else if (lowerFam.includes('majalla') || lowerFam.includes('sakkal')) {
    mappedFont = "'Sakkal Majalla', 'Tajawal', sans-serif";
    docxFont = 'Sakkal Majalla';
  } else if (lowerFam.includes('typesetting')) {
    mappedFont = "'Arabic Typesetting', serif";
    docxFont = 'Arabic Typesetting';
  } else if (lowerFam.includes('andalus')) {
    mappedFont = "Andalus, serif";
    docxFont = 'Andalus';
  } else if (lowerFam.includes('dubai')) {
    mappedFont = "Dubai, 'Segoe UI', sans-serif";
    docxFont = 'Dubai';
  } else if (lowerFam.includes('tahoma')) {
    mappedFont = 'Tahoma, Arial, sans-serif';
    docxFont = 'Tahoma';
  } else if (lowerFam.includes('calibri')) {
    mappedFont = "'Calibri', 'Segoe UI', sans-serif";
    docxFont = 'Calibri';
  } else if (lowerFam.includes('segoe')) {
    mappedFont = "'Segoe UI', Tahoma, Arial, sans-serif";
    docxFont = 'Segoe UI';
  } else if (lowerFam.includes('helvetica')) {
    mappedFont = "'Helvetica Neue', Helvetica, Arial, sans-serif";
    docxFont = 'Helvetica';
  } else if (lowerFam.includes('times')) {
    mappedFont = "'Times New Roman', serif";
    docxFont = 'Times New Roman';
  } else if (lowerFam.includes('arial')) {
    mappedFont = 'Arial, sans-serif';
    docxFont = 'Arial';
  } else if (lowerFam.includes('georgia')) {
    mappedFont = 'Georgia, serif';
    docxFont = 'Georgia';
  } else if (lowerFam.includes('verdana')) {
    mappedFont = 'Verdana, Geneva, sans-serif';
    docxFont = 'Verdana';
  } else if (lowerFam.includes('cairo')) {
    mappedFont = 'Cairo, sans-serif';
    docxFont = 'Cairo';
  } else if (lowerFam.includes('amiri')) {
    mappedFont = 'Amiri, serif';
    docxFont = 'Amiri';
  } else if (lowerFam.includes('tajawal')) {
    mappedFont = 'Tajawal, sans-serif';
    docxFont = 'Tajawal';
  } else if (lowerFam.includes('almarai')) {
    mappedFont = "'Almarai', sans-serif";
    docxFont = 'Almarai';
  } else if (lowerFam.includes('courier') || lowerFallback.includes('mono')) {
    mappedFont = "'Courier New', monospace";
    docxFont = 'Courier New';
  } else if (cleanFamily && cleanFamily.length > 1) {
    const fallback = lowerFallback.includes('serif')
      ? (isArabic ? "'Traditional Arabic', 'Amiri', serif" : "'Times New Roman', serif")
      : (isArabic ? "'Cairo', sans-serif" : "'Segoe UI', Arial, sans-serif");
    const loadedFamily = fo?.loadedName ? `"${fo.loadedName}", ` : '';
    mappedFont = `${loadedFamily}'${cleanFamily}', ${fallback}`;
    docxFont = cleanFamily;
  } else if (lowerFallback.includes('serif')) {
    mappedFont = isArabic ? "'Traditional Arabic', 'Amiri', serif" : "'Times New Roman', serif";
    docxFont = isArabic ? 'Traditional Arabic' : 'Times New Roman';
  } else if (lowerFallback.includes('sans')) {
    mappedFont = isArabic ? 'Cairo, sans-serif' : 'Arial, sans-serif';
    docxFont = isArabic ? 'Cairo' : 'Arial';
  } else {
    mappedFont = isArabic ? "'Traditional Arabic', 'Amiri', serif" : "'Segoe UI', Arial, sans-serif";
    docxFont = isArabic ? 'Traditional Arabic' : 'Arial';
  }

  return { isBold, isItalic, mappedFont, docxFont, cleanFamily };
}

function sampleCanvasBackgroundColor(screenLeft, screenTop, width = 24, height = 18) {
  try {
    const ctx = pdfCanvas.getContext('2d');
    const stageWidth = stage.clientWidth || 1;
    const stageHeight = stage.clientHeight || 1;
    const scaleX = pdfCanvas.width / stageWidth;
    const scaleY = pdfCanvas.height / stageHeight;

    // Sample points safely along the perimeter outside the text bounding box
    const samplePoints = [
      [screenLeft + width * 0.2, screenTop - 8],
      [screenLeft + width * 0.5, screenTop - 8],
      [screenLeft + width * 0.8, screenTop - 8],
      [screenLeft + width * 0.2, screenTop + height + 8],
      [screenLeft + width * 0.5, screenTop + height + 8],
      [screenLeft + width * 0.8, screenTop + height + 8],
      [screenLeft - 10, screenTop + height * 0.3],
      [screenLeft - 10, screenTop + height * 0.7],
      [screenLeft + width + 10, screenTop + height * 0.3],
      [screenLeft + width + 10, screenTop + height * 0.7],
      [screenLeft - 15, screenTop - 6],
      [screenLeft + width + 15, screenTop - 6]
    ];
    const colorVotes = new Map();
    for (const [px, py] of samplePoints) {
      const sx = Math.floor(px * scaleX);
      const sy = Math.floor(py * scaleY);
      if (sx >= 0 && sx < pdfCanvas.width && sy >= 0 && sy < pdfCanvas.height) {
        const d = ctx.getImageData(sx, sy, 1, 1).data;
        if (!d || d[3] < 30 || (d[0] >= 238 && d[1] >= 238 && d[2] >= 238)) {
          colorVotes.set('#ffffff', (colorVotes.get('#ffffff') || 0) + 1);
        } else {
          const hex = `#${d[0].toString(16).padStart(2, '0')}${d[1].toString(16).padStart(2, '0')}${d[2].toString(16).padStart(2, '0')}`;
          colorVotes.set(hex, (colorVotes.get(hex) || 0) + 1);
        }
      }
    }
    let maxVotes = 0;
    let majorityColor = '#ffffff';
    for (const [col, count] of colorVotes.entries()) {
      if (count > maxVotes) {
        maxVotes = count;
        majorityColor = col;
      }
    }
    if (majorityColor !== '#ffffff') {
      const r = parseInt(majorityColor.slice(1, 3), 16);
      const g = parseInt(majorityColor.slice(3, 5), 16);
      const b = parseInt(majorityColor.slice(5, 7), 16);
      if (r >= 238 && g >= 238 && b >= 238) {
        majorityColor = '#ffffff';
      }
    }
    return majorityColor;
  } catch (e) {
    // Non-fatal if canvas reading is restricted
  }
  return '#ffffff';
}

function makeCanvasBackgroundTransparent(canvas, targetHex = '#ffffff', tolerance = 22) {
  try {
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    if (w <= 0 || h <= 0) return;

    const imgData = ctx.getImageData(0, 0, w, h);
    const data = imgData.data;

    let tr = 255, tg = 255, tb = 255;
    if (targetHex && targetHex.startsWith('#') && targetHex.length >= 7) {
      tr = parseInt(targetHex.slice(1, 3), 16);
      tg = parseInt(targetHex.slice(3, 5), 16);
      tb = parseInt(targetHex.slice(5, 7), 16);
    }
    if (isNaN(tr)) tr = 255;
    if (isNaN(tg)) tg = 255;
    if (isNaN(tb)) tb = 255;

    function isBg(idx) {
      const a = data[idx + 3];
      if (a < 20) return true;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const dist = Math.hypot(r - tr, g - tg, b - tb);
      if (dist <= tolerance) return true;
      if (tr >= 235 && tg >= 235 && tb >= 235 && r >= 235 && g >= 235 && b >= 235) return true;
      return false;
    }

    const visited = new Uint8Array(w * h);
    const queue = [];

    for (let x = 0; x < w; x++) {
      if (isBg(x * 4)) { visited[x] = 1; queue.push(x, 0); }
      const botPos = (h - 1) * w + x;
      if (!visited[botPos] && isBg(botPos * 4)) { visited[botPos] = 1; queue.push(x, h - 1); }
    }
    for (let y = 0; y < h; y++) {
      const lPos = y * w;
      if (!visited[lPos] && isBg(lPos * 4)) { visited[lPos] = 1; queue.push(0, y); }
      const rPos = y * w + w - 1;
      if (!visited[rPos] && isBg(rPos * 4)) { visited[rPos] = 1; queue.push(w - 1, y); }
    }

    let head = 0;
    while (head < queue.length) {
      const cx = queue[head++];
      const cy = queue[head++];
      const pIdx = (cy * w + cx) * 4;
      data[pIdx + 3] = 0;

      const neighbors = [
        [cx - 1, cy],
        [cx + 1, cy],
        [cx, cy - 1],
        [cx, cy + 1]
      ];
      for (const [nx, ny] of neighbors) {
        if (nx >= 0 && nx < w && ny >= 0 && ny < h) {
          const nPos = ny * w + nx;
          if (!visited[nPos]) {
            visited[nPos] = 1;
            if (isBg(nPos * 4)) {
              queue.push(nx, ny);
            }
          }
        }
      }
    }

    ctx.putImageData(imgData, 0, 0);
  } catch (err) {
    console.warn('makeCanvasBackgroundTransparent error:', err);
  }
}

async function cleanImagePerimeterBackground(src, targetHex = '#ffffff') {
  if (!src || typeof src !== 'string' || !src.startsWith('data:image/')) return src;
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth || img.width;
        c.height = img.naturalHeight || img.height;
        const ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0);
        makeCanvasBackgroundTransparent(c, targetHex);
        resolve(c.toDataURL('image/png'));
      } catch (_) {
        resolve(src);
      }
    };
    img.onerror = () => resolve(src);
    img.src = src;
  });
}

function sampleCanvasTextColor(screenLeft, screenTop, width, height, bgColor = '#ffffff') {
  try {
    const ctx = pdfCanvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let bgR = 255;
    let bgG = 255;
    let bgB = 255;
    if (bgColor && bgColor.startsWith('#') && bgColor.length === 7) {
      bgR = parseInt(bgColor.slice(1, 3), 16);
      bgG = parseInt(bgColor.slice(3, 5), 16);
      bgB = parseInt(bgColor.slice(5, 7), 16);
    }

    const sx = Math.max(0, Math.floor(screenLeft * dpr));
    const sy = Math.max(0, Math.floor(screenTop * dpr));
    const sw = Math.min(pdfCanvas.width - sx, Math.floor(width * dpr));
    const sh = Math.min(pdfCanvas.height - sy, Math.floor(height * dpr));

    if (sw <= 0 || sh <= 0) return (bgR + bgG + bgB < 380) ? '#ffffff' : '#111827';

    // Sample across multiple horizontal slices of the glyph box
    const sliceFractions = [0.28, 0.40, 0.52, 0.64, 0.76];
    let maxDiff = 0;
    let maxDiffColor = null;
    const candidates = [];

    for (const frac of sliceFractions) {
      const sliceY = Math.floor(sy + sh * frac);
      if (sliceY < 0 || sliceY >= pdfCanvas.height) continue;
      const imgData = ctx.getImageData(sx, sliceY, sw, 1).data;
      for (let i = 0; i < imgData.length; i += 4) {
        const a = imgData[i + 3];
        if (a < 140) continue;
        const r = imgData[i];
        const g = imgData[i + 1];
        const b = imgData[i + 2];
        const diff = Math.hypot(r - bgR, g - bgG, b - bgB);
        if (diff > 55) {
          const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
          candidates.push({ r, g, b, diff, hex });
          if (diff > maxDiff) {
            maxDiff = diff;
            maxDiffColor = hex;
          }
        }
      }
    }

    if (maxDiffColor && maxDiff > 55) {
      // Find the most representative color among the solid core pixels (diff >= maxDiff - 30)
      // This eliminates intermediate antialiased fringe pixels and gets the rich, solid stroke color
      const topPixels = candidates.filter((c) => c.diff >= maxDiff - 30);
      if (topPixels.length > 0) {
        const binCounts = new Map();
        for (const p of topPixels) {
          const qr = Math.min(255, Math.round(p.r / 8) * 8);
          const qg = Math.min(255, Math.round(p.g / 8) * 8);
          const qb = Math.min(255, Math.round(p.b / 8) * 8);
          const key = `#${qr.toString(16).padStart(2, '0')}${qg.toString(16).padStart(2, '0')}${qb.toString(16).padStart(2, '0')}`;
          binCounts.set(key, (binCounts.get(key) || 0) + 1);
        }
        let topBin = maxDiffColor;
        let topCount = 0;
        for (const [k, count] of binCounts.entries()) {
          if (count > topCount) {
            topCount = count;
            topBin = k;
          }
        }
        return topBin;
      }
      return maxDiffColor;
    }
  } catch (e) {
    // Non-fatal if canvas reading is restricted
  }
  const bgLuma = 0.299 * (parseInt(bgColor?.slice(1, 3) || 'ff', 16)) +
                 0.587 * (parseInt(bgColor?.slice(3, 5) || 'ff', 16)) +
                 0.114 * (parseInt(bgColor?.slice(5, 7) || 'ff', 16));
  return bgLuma < 125 ? '#ffffff' : '#111827';
}

function groupTextItemsIntoLines(rawItems, viewport) {
  if (!rawItems || !rawItems.length) return [];

  // Filter out completely empty items and keep original index
  const valid = rawItems
    .filter((it) => it.str && it.str.trim().length > 0)
    .map((it, idx) => {
      const tx = it.transform[4];
      const ty = it.transform[5];
      const rawFontSize = Math.hypot(it.transform[0], it.transform[1]) || Math.abs(it.transform[3]) || 14;
      const fontSize = Math.round(rawFontSize * 10) / 10;
      const width = it.width || (fontSize * 0.6 * it.str.length);
      return {
        origIndex: idx,
        str: it.str,
        tx,
        ty,
        width,
        fontSize,
        fontName: it.fontName,
        dir: it.dir
      };
    });

  if (!valid.length) return [];

  // Group items by baseline (ty)
  const lineBuckets = [];
  for (const item of valid) {
    let matchedBucket = null;
    for (const b of lineBuckets) {
      if (Math.abs(item.ty - b.ty) <= Math.min(item.fontSize, b.fontSize) * 0.45) {
        matchedBucket = b;
        break;
      }
    }
    if (matchedBucket) {
      matchedBucket.items.push(item);
      matchedBucket.minTx = Math.min(matchedBucket.minTx, item.tx);
      matchedBucket.maxTx = Math.max(matchedBucket.maxTx, item.tx + item.width);
      matchedBucket.fontSize = Math.max(matchedBucket.fontSize, item.fontSize);
    } else {
      lineBuckets.push({
        ty: item.ty,
        fontSize: item.fontSize,
        minTx: item.tx,
        maxTx: item.tx + item.width,
        items: [item]
      });
    }
  }

  // Sort line buckets from top of the page to bottom of the page
  lineBuckets.sort((a, b) => b.ty - a.ty);

  const finalLines = [];

  for (const bucket of lineBuckets) {
    // CRITICAL: Items inside the bucket must remain in their original stream order (origIndex)
    // to preserve Arabic reading order perfectly without reversing!
    bucket.items.sort((a, b) => a.origIndex - b.origIndex);

    let currentCell = null;
    for (const it of bucket.items) {
      if (!currentCell) {
        currentCell = {
          items: [it],
          minTx: it.tx,
          maxTx: it.tx + it.width,
          ty: bucket.ty,
          fontSize: it.fontSize,
          fontName: it.fontName,
          dir: it.dir
        };
        continue;
      }

      // Check distance to see if it's within the same cell or a different table column
      const dist = Math.min(
        Math.abs(it.tx - currentCell.maxTx),
        Math.abs(it.tx + it.width - currentCell.minTx),
        Math.abs(it.tx - currentCell.minTx)
      );

      const isSameCell = dist <= Math.max(it.fontSize * 2.2, 32);

      if (isSameCell) {
        currentCell.items.push(it);
        currentCell.minTx = Math.min(currentCell.minTx, it.tx);
        currentCell.maxTx = Math.max(currentCell.maxTx, it.tx + it.width);
        currentCell.fontSize = Math.max(currentCell.fontSize, it.fontSize);
      } else {
        finalLines.push(currentCell);
        currentCell = {
          items: [it],
          minTx: it.tx,
          maxTx: it.tx + it.width,
          ty: bucket.ty,
          fontSize: it.fontSize,
          fontName: it.fontName,
          dir: it.dir
        };
      }
    }
    if (currentCell) finalLines.push(currentCell);
  }

  return finalLines.map((cell, lineIdx) => {
    let combinedStr = '';
    for (let i = 0; i < cell.items.length; i++) {
      const curr = cell.items[i];
      if (i === 0) {
        combinedStr = curr.str;
        continue;
      }
      const prev = cell.items[i - 1];

      // If either already has whitespace, preserve it without duplicate spaces
      if (combinedStr.endsWith(' ') || curr.str.startsWith(' ')) {
        combinedStr += (combinedStr.endsWith(' ') && curr.str.startsWith(' '))
          ? curr.str.trimStart()
          : curr.str;
        continue;
      }

      // Calculate horizontal gap between consecutive items in PDF coordinates
      const dist = Math.min(
        Math.abs(curr.tx - (prev.tx + prev.width)),
        Math.abs(prev.tx - (curr.tx + curr.width))
      );

      const minFontSize = Math.min(prev.fontSize || 14, curr.fontSize || 14);
      // Only insert space between separate words when horizontal distance exceeds standard kerning threshold
      const spaceThreshold = Math.max(2.5, minFontSize * 0.22);

      if (dist >= spaceThreshold) {
        combinedStr += ' ' + curr.str;
      } else {
        combinedStr += curr.str;
      }
    }

    const totalWidth = Math.max(16, cell.maxTx - cell.minTx);
    return {
      id: `text_line_${currentPageNumber}_${lineIdx}`,
      str: combinedStr.trim(),
      tx: cell.minTx,
      ty: cell.ty,
      width: totalWidth,
      fontSize: cell.fontSize,
      fontName: cell.fontName,
      dir: cell.dir
    };
  });
}

// ==========================================
// Word Document Sheet Flow Model & In-Place Table Editor
// ==========================================

function buildFlexRowBlockModel(rowLines, type, viewport, styles, stageWidth, stageHeight, resolvedFonts = null) {
  const cells = rowLines.map((line, colIdx) => {
    const { isBold, isItalic, mappedFont, docxFont } = detectFontProperties(line.fontName, styles, currentPageObj, line.str, resolvedFonts);
    const isArabic = /[\u0600-\u06FF]/.test(line.str);

    const [screenLeft, screenTop] = viewport.convertToViewportPoint(line.tx, line.ty + line.fontSize);
    const bg = sampleCanvasBackgroundColor(
      screenLeft,
      screenTop,
      Math.max(20, line.width * viewport.scale),
      Math.max(16, line.fontSize * viewport.scale)
    );

    let textColor = '#111827';
    if (bg && bg.startsWith('#') && bg.length === 7) {
      const r = parseInt(bg.slice(1, 3), 16);
      const g = parseInt(bg.slice(3, 5), 16);
      const b = parseInt(bg.slice(5, 7), 16);
      const luma = 0.299 * r + 0.587 * g + 0.114 * b;
      if (luma < 125) textColor = '#ffffff';
    }

    return {
      id: line.id || `flex_${type}_${currentPageNumber}_${colIdx}`,
      text: line.str,
      origText: line.str,
      fontSize: Math.round(line.fontSize),
      fontFamily: mappedFont,
      docxFont,
      isBold: isBold || (type === 'header-row' && line.fontSize >= 15),
      isItalic,
      isUnderline: false,
      isStrike: false,
      align: isArabic ? (colIdx === 0 ? 'right' : (colIdx === rowLines.length - 1 ? 'left' : 'center')) : 'center',
      color: textColor,
      bg: bg || '#ffffff',
      normX: screenLeft / stageWidth,
      normY: screenTop / stageHeight,
      normW: Math.max(16, line.width * viewport.scale) / stageWidth,
      normH: Math.max(14, line.fontSize * 1.3 * viewport.scale) / stageHeight
    };
  });

  return {
    type, // 'header-row' | 'footer-row' | 'info-row'
    cells
  };
}

function buildTableBlockModel(tableRowsLines, viewport, styles, resolvedFonts = null) {
  const stageWidth = stage.clientWidth || 800;
  const stageHeight = stage.clientHeight || 1100;

  // 1. Identify distinct column bands across all table rows
  const allLines = [];
  tableRowsLines.forEach((rLines) => allLines.push(...rLines));

  const colBands = [];
  allLines.forEach((l) => {
    const minX = l.tx;
    const maxX = l.tx + l.width;
    const center = (minX + maxX) / 2;
    let matched = null;
    for (const band of colBands) {
      if (Math.abs(band.center - center) < 48 || (minX >= band.minX - 25 && maxX <= band.maxX + 25)) {
        matched = band;
        break;
      }
    }
    if (matched) {
      matched.minX = Math.min(matched.minX, minX);
      matched.maxX = Math.max(matched.maxX, maxX);
      matched.center = (matched.minX + matched.maxX) / 2;
      matched.count++;
    } else {
      colBands.push({ minX, maxX, center, count: 1 });
    }
  });

  // For Arabic RTL, columns flow right to left: descending X (b.center - a.center)
  colBands.sort((a, b) => b.center - a.center);
  const maxRowLen = Math.max(...tableRowsLines.map((r) => r.length), 1);
  const colCount = Math.max(colBands.length, maxRowLen);

  // Compute proportional widths for columns
  const rawColWidths = colBands.map((b) => Math.max(b.maxX - b.minX + 30, 40));
  while (rawColWidths.length < colCount) rawColWidths.push(60);
  const totalRaw = rawColWidths.reduce((a, b) => a + b, 0) || 1;
  const colPctList = rawColWidths.map((w) => Math.max(8, Math.round((w / totalRaw) * 100)));
  const sumPct = colPctList.reduce((a, b) => a + b, 0);
  if (sumPct > 0 && colPctList.length > 0) {
    colPctList[0] = Math.max(6, colPctList[0] + (100 - sumPct));
  }

  // 2. Map each row to columns
  const rows = tableRowsLines.map((rowLines, rowIdx) => {
    // Sort lines RTL (descending X)
    const sortedLines = [...rowLines].sort((a, b) => b.tx - a.tx);

    const assignedCells = new Array(colCount).fill(null);
    sortedLines.forEach((line) => {
      let bestColIdx = 0;
      let bestDist = Infinity;
      if (colBands.length === colCount) {
        const lineCenter = line.tx + line.width / 2;
        colBands.forEach((band, cIdx) => {
          const dist = Math.abs(band.center - lineCenter);
          if (dist < bestDist) {
            bestDist = dist;
            bestColIdx = cIdx;
          }
        });
      } else {
        bestColIdx = sortedLines.indexOf(line);
      }
      if (bestColIdx < colCount && !assignedCells[bestColIdx]) {
        assignedCells[bestColIdx] = line;
      } else {
        const emptyIdx = assignedCells.findIndex((c) => c === null);
        if (emptyIdx !== -1) assignedCells[emptyIdx] = line;
      }
    });

    const cells = assignedCells.map((line, colIdx) => {
      const colPct = colPctList[colIdx] || Math.round(100 / colCount);

      if (!line) {
        return {
          id: `cell_empty_${currentPageNumber}_${rowIdx}_${colIdx}`,
          text: '',
          origText: '',
          fontSize: 14,
          fontFamily: "'Traditional Arabic', 'Amiri', serif",
          docxFont: 'Traditional Arabic',
          isBold: rowIdx === 0,
          isItalic: false,
          isUnderline: false,
          isStrike: false,
          align: 'center',
          color: '#111827',
          bg: '#ffffff',
          colPct,
          normX: 0,
          normY: 0,
          normW: 0,
          normH: 0
        };
      }

      const { isBold, isItalic, mappedFont, docxFont } = detectFontProperties(line.fontName, styles, currentPageObj, line.str, resolvedFonts);
      const isArabic = /[\u0600-\u06FF]/.test(line.str);

      const [screenLeft, screenTop] = viewport.convertToViewportPoint(line.tx, line.ty + line.fontSize);
      const bg = sampleCanvasBackgroundColor(
        screenLeft,
        screenTop,
        Math.max(20, line.width * viewport.scale),
        Math.max(16, line.fontSize * viewport.scale)
      );

      let textColor = '#111827';
      if (bg && bg.startsWith('#') && bg.length === 7) {
        const r = parseInt(bg.slice(1, 3), 16);
        const g = parseInt(bg.slice(3, 5), 16);
        const b = parseInt(bg.slice(5, 7), 16);
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        if (luma < 125) textColor = '#ffffff';
      }

      return {
        id: line.id || `cell_${currentPageNumber}_${rowIdx}_${colIdx}`,
        text: line.str,
        origText: line.str,
        fontSize: Math.round(line.fontSize),
        fontFamily: mappedFont,
        docxFont,
        isBold: rowIdx === 0 || isBold,
        isItalic,
        isUnderline: false,
        isStrike: false,
        align: isArabic ? 'right' : 'center',
        color: textColor,
        bg: bg || '#ffffff',
        colPct,
        normX: screenLeft / stageWidth,
        normY: screenTop / stageHeight,
        normW: Math.max(16, line.width * viewport.scale) / stageWidth,
        normH: Math.max(14, line.fontSize * 1.3 * viewport.scale) / stageHeight
      };
    });

    return { cells };
  });

  return {
    type: 'table',
    maxCols: colCount,
    colPctList,
    rows
  };
}

function buildPageDocModel(rawItems, viewport, styles, stageWidth, stageHeight, resolvedFonts = null) {
  const groupedLines = groupTextItemsIntoLines(rawItems, viewport);
  if (!groupedLines.length) {
    return { blocks: [], stageWidth, stageHeight };
  }

  // 1. Group lines by baseline (ty) with tolerant matching (at least 14px or 70% of font size)
  const rowsMap = new Map();
  for (const line of groupedLines) {
    let matchedTy = null;
    for (const ty of rowsMap.keys()) {
      if (Math.abs(ty - line.ty) <= Math.max(line.fontSize, 15) * 0.70) {
        matchedTy = ty;
        break;
      }
    }
    if (matchedTy !== null) {
      rowsMap.get(matchedTy).push(line);
    } else {
      rowsMap.set(line.ty, [line]);
    }
  }

  const sortedRowKeys = Array.from(rowsMap.keys()).sort((a, b) => b - a);
  const blocks = [];
  let currentTableRows = [];

  const flushTableRows = () => {
    if (!currentTableRows.length) return;
    if (currentTableRows.length >= 2 || currentTableRows[0].length >= 3) {
      blocks.push(buildTableBlockModel(currentTableRows, viewport, styles, resolvedFonts));
    } else {
      const singleRow = currentTableRows[0];
      blocks.push(buildFlexRowBlockModel(singleRow, 'info-row', viewport, styles, stageWidth, stageHeight, resolvedFonts));
    }
    currentTableRows = [];
  };

  // 2. Identify repeating columns across multi-cell rows (Table Column Bands)
  const multiCellRows = [];
  for (const ty of sortedRowKeys) {
    const rLines = rowsMap.get(ty);
    if (rLines.length >= 2) multiCellRows.push(rLines);
  }

  const colBands = [];
  if (multiCellRows.length >= 2) {
    const cellsList = [];
    multiCellRows.forEach((rLines) => {
      rLines.forEach((l) => cellsList.push({ minX: l.tx, maxX: l.tx + l.width, line: l }));
    });
    cellsList.sort((a, b) => a.minX - b.minX);
    for (const cell of cellsList) {
      let matchedBand = null;
      for (const band of colBands) {
        if (Math.abs(band.center - (cell.minX + cell.maxX) / 2) < 48 ||
            (cell.minX >= band.minX - 20 && cell.maxX <= band.maxX + 20)) {
          matchedBand = band;
          break;
        }
      }
      if (matchedBand) {
        matchedBand.minX = Math.min(matchedBand.minX, cell.minX);
        matchedBand.maxX = Math.max(matchedBand.maxX, cell.maxX);
        matchedBand.center = (matchedBand.minX + matchedBand.maxX) / 2;
        matchedBand.count++;
      } else {
        colBands.push({
          minX: cell.minX,
          maxX: cell.maxX,
          center: (cell.minX + cell.maxX) / 2,
          count: 1
        });
      }
    }
    colBands.sort((a, b) => a.minX - b.minX);
  }

  // 3. Process Rows
  for (let r = 0; r < sortedRowKeys.length; r++) {
    const rowLines = rowsMap.get(sortedRowKeys[r]);
    // For Arabic RTL tables, columns flow right to left (b.tx - a.tx)
    rowLines.sort((a, b) => b.tx - a.tx);

    const isMultiCell = rowLines.length >= 2;
    const matchesTableBand = colBands.length >= 2 && rowLines.some((l) => {
      const centerX = l.tx + l.width / 2;
      return colBands.some((b) => Math.abs(b.center - centerX) < 55);
    });

    if (isMultiCell || (matchesTableBand && (currentTableRows.length > 0 || r + 1 < sortedRowKeys.length))) {
      currentTableRows.push(rowLines);
    } else {
      flushTableRows();

      const line = rowLines[0];
      const { isBold, isItalic, mappedFont, docxFont } = detectFontProperties(line.fontName, styles, currentPageObj, line.str, resolvedFonts);
      const isArabic = /[\u0600-\u06FF]/.test(line.str);

      const [screenLeft, screenTop] = viewport.convertToViewportPoint(line.tx, line.ty + line.fontSize);
      const bg = sampleCanvasBackgroundColor(
        screenLeft,
        screenTop,
        Math.max(20, line.width * viewport.scale),
        Math.max(16, line.fontSize * viewport.scale)
      );

      // Centered line detection for headings
      const lineMidX = screenLeft + (line.width * viewport.scale) / 2;
      const stageMidX = stageWidth / 2;
      const isCentered = Math.abs(lineMidX - stageMidX) < stageWidth * 0.15;
      const align = isCentered ? 'center' : (isArabic ? 'right' : 'left');

      const isHeading = line.fontSize >= 18 || (line.fontSize >= 15 && isBold) || isCentered;
      const normX = screenLeft / stageWidth;
      const normY = screenTop / stageHeight;
      const normW = Math.max(16, line.width * viewport.scale) / stageWidth;
      const normH = Math.max(14, line.fontSize * 1.3 * viewport.scale) / stageHeight;

      blocks.push({
        type: isHeading ? 'heading' : 'paragraph',
        id: line.id,
        text: line.str,
        origText: line.str,
        fontSize: Math.round(line.fontSize),
        fontFamily: mappedFont,
        docxFont,
        isBold,
        isItalic,
        isUnderline: false,
        isStrike: false,
        align,
        color: '#111827',
        bg: bg || '#ffffff',
        normX,
        normY,
        normW,
        normH
      });
    }
  }

  flushTableRows();

  return { blocks, stageWidth, stageHeight };
}

async function renderWordDocumentSheet(page, viewport, stageWidth, stageHeight) {
  if (!docPage) return;

  // Check if page data is already built
  let docData = pageDocData.get(currentPageNumber);

  if (!docData && page && viewport) {
    const textContent = await page.getTextContent();
    const rawItems = textContent.items || [];
    const styles = textContent.styles || {};
    docData = buildPageDocModel(rawItems, viewport, styles, stageWidth, stageHeight);
    pageDocData.set(currentPageNumber, docData);
  }

  if (!docData) return;

  // Render DOM into docPage
  docPage.replaceChildren();

  if (!docData.blocks || docData.blocks.length === 0) {
    const emptyNotice = document.createElement('div');
    emptyNotice.className = 'pe-doc-empty';
    emptyNotice.style.padding = '40px 20px';
    emptyNotice.style.textAlign = 'center';
    emptyNotice.style.color = '#64748b';
    emptyNotice.textContent = isAr
      ? 'لا توجد نصوص مستخرجة في هذه الصفحة. يمكنك استخدام وضع الرسم لإضافة محتوى.'
      : 'No text detected on this page. Switch to Canvas Mode to add annotations.';
    docPage.append(emptyNotice);
    return;
  }

  docData.blocks.forEach((block, blockIdx) => {
    if (block.type === 'table') {
      const tableWrap = document.createElement('div');
      tableWrap.className = 'pe-doc-table-wrap';

      const table = document.createElement('table');
      table.className = 'pe-doc-table';
      table.dataset.blockIdx = blockIdx;

      // Add colgroup with proportional column widths to preserve exact word spacing and layout!
      if (block.colPctList && block.colPctList.length) {
        const colgroup = document.createElement('colgroup');
        block.colPctList.forEach((pct) => {
          const col = document.createElement('col');
          col.style.width = `${pct}%`;
          colgroup.append(col);
        });
        table.append(colgroup);
      }

      block.rows.forEach((row, rowIdx) => {
        const tr = document.createElement('tr');
        tr.dataset.rowIdx = rowIdx;

        row.cells.forEach((cell, colIdx) => {
          const isHeader = rowIdx === 0 && (cell.isBold || (block.rows.length > 1 && cell.bg !== '#ffffff'));
          const cellEl = document.createElement(isHeader ? 'th' : 'td');
          cellEl.contentEditable = 'true';
          cellEl.spellcheck = false;
          cellEl.dataset.blockIdx = blockIdx;
          cellEl.dataset.rowIdx = rowIdx;
          cellEl.dataset.colIdx = colIdx;
          cellEl.dataset.id = cell.id;

          cellEl.textContent = cell.text;
          cellEl.style.fontFamily = cell.fontFamily || "'Traditional Arabic', 'Amiri', serif";
          cellEl.style.fontSize = `${cell.fontSize}px`;
          cellEl.style.fontWeight = cell.isBold ? '700' : '400';
          cellEl.style.fontStyle = cell.isItalic ? 'italic' : 'normal';

          if (cell.colPct) cellEl.style.width = `${cell.colPct}%`;

          const textDec = [];
          if (cell.isUnderline) textDec.push('underline');
          if (cell.isStrike) textDec.push('line-through');
          cellEl.style.textDecoration = textDec.length ? textDec.join(' ') : 'none';

          cellEl.style.textAlign = cell.align || 'center';
          cellEl.style.color = cell.color || '#111827';
          if (cell.bg && cell.bg !== '#ffffff') {
            cellEl.style.backgroundColor = cell.bg;
          }

          attachCellEvents(cellEl, cell, block, row, tr, table);
          tr.append(cellEl);
        });

        table.append(tr);
      });

      tableWrap.append(table);
      docPage.append(tableWrap);
    } else if (block.type === 'header-row' || block.type === 'footer-row' || block.type === 'info-row') {
      const rowWrap = document.createElement('div');
      rowWrap.className = `pe-doc-${block.type}`;
      rowWrap.dataset.blockIdx = blockIdx;

      block.cells.forEach((cell, cellIdx) => {
        const cellEl = document.createElement('div');
        cellEl.className = 'pe-doc-flex-cell';
        cellEl.contentEditable = 'true';
        cellEl.spellcheck = false;
        cellEl.dataset.blockIdx = blockIdx;
        cellEl.dataset.colIdx = cellIdx;
        cellEl.dataset.id = cell.id;

        cellEl.textContent = cell.text;
        cellEl.style.fontFamily = cell.fontFamily || "'Traditional Arabic', 'Amiri', serif";
        cellEl.style.fontSize = `${cell.fontSize}px`;
        cellEl.style.fontWeight = cell.isBold ? '700' : '400';
        cellEl.style.fontStyle = cell.isItalic ? 'italic' : 'normal';

        const textDec = [];
        if (cell.isUnderline) textDec.push('underline');
        if (cell.isStrike) textDec.push('line-through');
        cellEl.style.textDecoration = textDec.length ? textDec.join(' ') : 'none';

        cellEl.style.textAlign = cell.align || 'right';
        cellEl.style.color = cell.color || '#111827';
        if (cell.bg && cell.bg !== '#ffffff') cellEl.style.backgroundColor = cell.bg;

        attachParagraphEvents(cellEl, cell);
        rowWrap.append(cellEl);
      });

      docPage.append(rowWrap);
    } else if (block.type === 'heading') {
      const h2 = document.createElement('h2');
      h2.className = 'pe-doc-heading';
      h2.contentEditable = 'true';
      h2.spellcheck = false;
      h2.dataset.blockIdx = blockIdx;
      h2.dataset.id = block.id;
      h2.textContent = block.text;
      h2.style.fontFamily = block.fontFamily || "'Traditional Arabic', 'Amiri', serif";
      h2.style.fontSize = `${block.fontSize}px`;
      h2.style.fontWeight = block.isBold ? '700' : 'bold';
      h2.style.fontStyle = block.isItalic ? 'italic' : 'normal';
      h2.style.textAlign = block.align || 'center';
      h2.style.color = block.color || '#111827';
      if (block.bg && block.bg !== '#ffffff') h2.style.backgroundColor = block.bg;

      attachParagraphEvents(h2, block);
      docPage.append(h2);
    } else {
      const p = document.createElement('p');
      p.className = 'pe-doc-para';
      p.contentEditable = 'true';
      p.spellcheck = false;
      p.dataset.blockIdx = blockIdx;
      p.dataset.id = block.id;
      p.textContent = block.text;
      p.style.fontFamily = block.fontFamily || "'Traditional Arabic', 'Amiri', serif";
      p.style.fontSize = `${block.fontSize}px`;
      p.style.fontWeight = block.isBold ? '700' : '400';
      p.style.fontStyle = block.isItalic ? 'italic' : 'normal';
      p.style.textAlign = block.align || 'right';
      p.style.color = block.color || '#111827';
      if (block.bg && block.bg !== '#ffffff') p.style.backgroundColor = block.bg;

      attachParagraphEvents(p, block);
      docPage.append(p);
    }
  });
}

function attachCellEvents(cellEl, cell, block, row, tr, table) {
  cellEl.addEventListener('focus', () => {
    currentActiveCell = cellEl;
    document.querySelectorAll('.pe-doc-table tr').forEach((r) => r.classList.remove('is-selected-row'));
    tr.classList.add('is-selected-row');

    if (tableControls) tableControls.hidden = false;
    if (optionsEditText) optionsEditText.hidden = false;

    syncToolbarWithCell(cell);
  });

  cellEl.addEventListener('input', () => {
    cell.text = cellEl.textContent;
    syncCellWithPageModifiedTexts(cell);
  });

  cellEl.addEventListener('blur', () => {
    cell.text = cellEl.textContent;
    syncCellWithPageModifiedTexts(cell);
  });

  cellEl.addEventListener('keydown', (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const allCells = Array.from(table.querySelectorAll('th, td'));
      const idx = allCells.indexOf(cellEl);
      if (e.shiftKey) {
        if (idx > 0) allCells[idx - 1].focus();
      } else {
        if (idx < allCells.length - 1) {
          allCells[idx + 1].focus();
        } else {
          // Reached the end of the table! Automatically add a new row below!
          insertTableRowAfter(tr, table, block);
        }
      }
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Move to same column in the row below
      const currentRowIdx = Number(tr.dataset.rowIdx);
      const currentColIdx = Number(cellEl.dataset.colIdx);
      const nextRow = table.querySelector(`tr[data-row-idx="${currentRowIdx + 1}"]`);
      if (nextRow) {
        const nextCell = nextRow.querySelector(`[data-col-idx="${currentColIdx}"]`);
        if (nextCell) nextCell.focus();
      } else {
        // Last row! Add new row and focus same column
        insertTableRowAfter(tr, table, block, currentColIdx);
      }
    }
  });
}

function attachParagraphEvents(el, block) {
  el.addEventListener('focus', () => {
    currentActiveCell = el;
    document.querySelectorAll('.pe-doc-table tr').forEach((r) => r.classList.remove('is-selected-row'));
    if (tableControls) tableControls.hidden = true;
    if (optionsEditText) optionsEditText.hidden = false;

    syncToolbarWithCell(block);
  });

  el.addEventListener('input', () => {
    block.text = el.textContent;
    syncCellWithPageModifiedTexts(block);
  });

  el.addEventListener('blur', () => {
    block.text = el.textContent;
    syncCellWithPageModifiedTexts(block);
  });
}

function insertTableRowAfter(tr, table, block, focusColIdx = 0) {
  const rowIdx = Number(tr.dataset.rowIdx);
  const templateRow = block.rows[rowIdx] || block.rows[block.rows.length - 1];
  const numCols = templateRow.cells.length;

  const newCells = [];
  for (let c = 0; c < numCols; c++) {
    const templCell = templateRow.cells[c];
    newCells.push({
      id: `cell_new_${Date.now()}_${c}`,
      text: '',
      origText: '',
      fontSize: templCell.fontSize || 14,
      fontFamily: templCell.fontFamily || 'Cairo, sans-serif',
      isBold: false,
      isItalic: false,
      isUnderline: false,
      isStrike: false,
      align: templCell.align || 'center',
      color: '#111827',
      bg: '#ffffff',
      normX: templCell.normX,
      normY: (templCell.normY || 0) + (templCell.normH || 0.03),
      normW: templCell.normW,
      normH: templCell.normH
    });
  }

  const newRow = { cells: newCells };
  block.rows.splice(rowIdx + 1, 0, newRow);

  // Re-render Word document sheet
  renderWordDocumentSheet(null, null, stage.clientWidth, stage.clientHeight);

  // Focus the newly added row
  const targetTr = docPage.querySelector(`table[data-block-idx="${table.dataset.blockIdx}"] tr[data-row-idx="${rowIdx + 1}"]`);
  if (targetTr) {
    const cellToFocus = targetTr.querySelector(`[data-col-idx="${focusColIdx}"]`) || targetTr.querySelector('td, th');
    if (cellToFocus) cellToFocus.focus();
  }
}

function deleteTableRow() {
  if (!currentActiveCell) return;
  const tr = currentActiveCell.closest('tr');
  const table = tr?.closest('table');
  if (!tr || !table) return;

  const blockIdx = Number(table.dataset.blockIdx);
  const rowIdx = Number(tr.dataset.rowIdx);
  const docData = pageDocData.get(currentPageNumber);
  const block = docData?.blocks[blockIdx];

  if (block && block.rows && block.rows.length > 1) {
    block.rows.splice(rowIdx, 1);
    renderWordDocumentSheet(null, null, stage.clientWidth, stage.clientHeight);
    const targetBlock = docPage.querySelector(`table[data-block-idx="${blockIdx}"]`);
    const prevRow = targetBlock?.querySelector(`tr[data-row-idx="${Math.max(0, rowIdx - 1)}"]`);
    if (prevRow) {
      const cell = prevRow.querySelector('td, th');
      if (cell) cell.focus();
    }
  }
}

function syncFontPickerInToolbar(fontFamilyCss, cleanFamily = '') {
  if (!detectedFontSelect) return;
  const displayName = cleanFamily || cleanPdfFontName(fontFamilyCss) || 'PDF Font';
  const lowerClean = displayName.toLowerCase();

  // Find an existing option whose value or text matches
  let matchedOpt = Array.from(detectedFontSelect.options).find((opt) => {
    if (!opt.value) return false;
    const optVal = opt.value.toLowerCase();
    const optTxt = opt.text.toLowerCase();
    return optVal === (fontFamilyCss || '').toLowerCase() ||
           optVal.includes(`'${lowerClean}'`) ||
           optVal.includes(`"${lowerClean}"`) ||
           optVal.includes(lowerClean) ||
           optTxt.includes(lowerClean);
  });

  if (!matchedOpt) {
    let group = detectedFontSelect.querySelector('optgroup[data-group="extracted-pdf"]');
    if (!group) {
      group = document.createElement('optgroup');
      group.dataset.group = 'extracted-pdf';
      group.label = isAr ? '🎯 خطوط مستخرجة من ملف PDF' : '🎯 Fonts Extracted from PDF';
      const firstOptgroup = detectedFontSelect.querySelector('optgroup');
      if (firstOptgroup) detectedFontSelect.insertBefore(group, firstOptgroup);
      else detectedFontSelect.prepend(group);
    }
    matchedOpt = document.createElement('option');
    matchedOpt.value = (fontFamilyCss && fontFamilyCss.includes(','))
      ? fontFamilyCss
      : `'${displayName}', system-ui, sans-serif`;
    matchedOpt.textContent = `🎯 ${displayName} (${isAr ? 'مستخرج من PDF' : 'from PDF'})`;
    group.prepend(matchedOpt);
  }

  detectedFontSelect.value = matchedOpt.value;

  // Sync custom-select wrapper UI so trigger text immediately reflects the font
  if (detectedFontSelect._kzCustomSelect && typeof detectedFontSelect._kzCustomSelect.update === 'function') {
    detectedFontSelect._kzCustomSelect.update();
  }

  const wrapper = detectedFontSelect.closest('.kz-select-wrapper');
  if (wrapper) {
    const valueSpan = wrapper.querySelector('.kz-select-value');
    if (valueSpan) {
      valueSpan.textContent = displayName;
      valueSpan.style.fontFamily = matchedOpt.value;
    }
  }

  // Also sync fontFamilySelect in formatting side panel if present
  if (fontFamilySelect && matchedOpt) {
    let sideOpt = Array.from(fontFamilySelect.options).find((opt) => {
      if (!opt.value) return false;
      const optVal = opt.value.toLowerCase();
      const optTxt = opt.text.toLowerCase();
      return optVal === (fontFamilyCss || '').toLowerCase() ||
             optVal.includes(`'${lowerClean}'`) ||
             optVal.includes(`"${lowerClean}"`) ||
             optVal.includes(lowerClean) ||
             optTxt.includes(lowerClean);
    });
    if (!sideOpt) {
      let sideGroup = fontFamilySelect.querySelector('optgroup[data-group="extracted-pdf"]');
      if (!sideGroup) {
        sideGroup = document.createElement('optgroup');
        sideGroup.dataset.group = 'extracted-pdf';
        sideGroup.label = isAr ? '🎯 خطوط مستخرجة من ملف PDF' : '🎯 Fonts Extracted from PDF';
        const firstOptgroup = fontFamilySelect.querySelector('optgroup');
        if (firstOptgroup) fontFamilySelect.insertBefore(sideGroup, firstOptgroup);
        else fontFamilySelect.prepend(sideGroup);
      }
      sideOpt = document.createElement('option');
      sideOpt.value = matchedOpt.value;
      sideOpt.textContent = matchedOpt.textContent;
      sideGroup.prepend(sideOpt);
    }
    fontFamilySelect.value = sideOpt.value;
    if (fontFamilySelect._kzCustomSelect && typeof fontFamilySelect._kzCustomSelect.update === 'function') {
      fontFamilySelect._kzCustomSelect.update();
    }
    const sideWrapper = fontFamilySelect.closest('.kz-select-wrapper');
    if (sideWrapper) {
      const sideValSpan = sideWrapper.querySelector('.kz-select-value');
      if (sideValSpan) {
        sideValSpan.textContent = displayName;
        sideValSpan.style.fontFamily = matchedOpt.value;
      }
    }
  }
}

function syncToolbarWithTextBlock(block, fontData = {}) {
  if (isSyncingToolbar) return;
  isSyncingToolbar = true;
  try {
    // 1. Font Family
    syncFontPickerInToolbar(fontData.fontFamily || fontData.mappedFont, fontData.cleanFamily);

    // 2. Font Size (true point size)
    const fontSize = Math.round(fontData.fontSize || 14);
    if (detectedSizeInput) detectedSizeInput.value = fontSize;

    // 3. Bold & Italic
    if (detectedBoldBtn) detectedBoldBtn.classList.toggle('active', !!fontData.isBold);
    if (detectedItalicBtn) detectedItalicBtn.classList.toggle('active', !!fontData.isItalic);
    if (detectedUnderlineBtn) detectedUnderlineBtn.classList.toggle('active', !!fontData.isUnderline);
    if (detectedStrikeBtn) detectedStrikeBtn.classList.toggle('active', !!fontData.isStrike);

    // 4. Alignment
    const align = fontData.align || 'left';
    if (alignRightBtn) alignRightBtn.classList.toggle('active', align === 'right');
    if (alignCenterBtn) alignCenterBtn.classList.toggle('active', align === 'center');
    if (alignLeftBtn) alignLeftBtn.classList.toggle('active', align === 'left');

    // 5. Colors (normalized 6-digit hex format)
    if (detectedColorInput && fontData.color) {
      detectedColorInput.value = normalizeHexColor(fontData.color, '#111827');
    }
    if (detectedBgInput && fontData.bg) {
      detectedBgInput.value = normalizeHexColor(fontData.bg, '#ffffff');
    }
  } finally {
    isSyncingToolbar = false;
  }
}

function syncToolbarWithCell(item) {
  syncToolbarWithTextBlock(null, {
    fontFamily: item.fontFamily || 'Cairo, sans-serif',
    cleanFamily: item.docxFont || cleanPdfFontName(item.fontFamily) || 'Cairo',
    fontSize: item.fontSize || 14,
    isBold: item.isBold,
    isItalic: item.isItalic,
    isUnderline: item.isUnderline,
    isStrike: item.isStrike,
    align: item.align,
    color: item.color || '#111827',
    bg: item.bg || '#ffffff'
  });
}

function getActiveCellData() {
  if (!currentActiveCell) return null;
  const docData = pageDocData.get(currentPageNumber);
  if (!docData) return null;

  const blockIdx = Number(currentActiveCell.dataset.blockIdx);
  const block = docData.blocks[blockIdx];
  if (!block) return null;

  if (block.type === 'table') {
    const rowIdx = Number(currentActiveCell.dataset.rowIdx);
    const colIdx = Number(currentActiveCell.dataset.colIdx);
    return block.rows[rowIdx]?.cells[colIdx];
  }
  if (block.type === 'header-row' || block.type === 'footer-row' || block.type === 'info-row') {
    const colIdx = Number(currentActiveCell.dataset.colIdx);
    return block.cells[colIdx];
  }
  return block;
}

function updateActiveCellStyles() {
  if (isSyncingToolbar) return;
  const data = getActiveCellData();
  if (!currentActiveCell || !data) {
    // Fall back to canvas text block update
    updateActiveTextBlockFromToolbar();
    return;
  }

  const font = detectedFontSelect ? detectedFontSelect.value : "'Traditional Arabic', 'Amiri', serif";
  const size = detectedSizeInput ? Number(detectedSizeInput.value) : 14;
  const isBold = detectedBoldBtn ? detectedBoldBtn.classList.contains('active') : false;
  const isItalic = detectedItalicBtn ? detectedItalicBtn.classList.contains('active') : false;
  const isUnderline = detectedUnderlineBtn ? detectedUnderlineBtn.classList.contains('active') : false;
  const isStrike = detectedStrikeBtn ? detectedStrikeBtn.classList.contains('active') : false;
  const color = detectedColorInput ? normalizeHexColor(detectedColorInput.value, '#111827') : '#111827';
  const bg = detectedBgInput ? normalizeHexColor(detectedBgInput.value, '#ffffff') : '#ffffff';

  let align = 'center';
  if (alignCenterBtn && alignCenterBtn.classList.contains('active')) align = 'center';
  else if (alignRightBtn && alignRightBtn.classList.contains('active')) align = 'right';
  else if (alignLeftBtn && alignLeftBtn.classList.contains('active')) align = 'left';

  // Apply to DOM element
  currentActiveCell.style.fontFamily = font;
  currentActiveCell.style.fontSize = `${size}px`;
  currentActiveCell.style.fontWeight = isBold ? '700' : '400';
  currentActiveCell.style.fontStyle = isItalic ? 'italic' : 'normal';

  const decs = [];
  if (isUnderline) decs.push('underline');
  if (isStrike) decs.push('line-through');
  currentActiveCell.style.textDecoration = decs.length ? decs.join(' ') : 'none';

  currentActiveCell.style.textAlign = align;
  currentActiveCell.style.color = color;
  currentActiveCell.style.backgroundColor = bg;

  // Apply to Data Model
  data.fontFamily = font;
  data.fontSize = size;
  data.isBold = isBold;
  data.isItalic = isItalic;
  data.isUnderline = isUnderline;
  data.isStrike = isStrike;
  data.align = align;
  data.color = color;
  data.bg = bg;

  const fontLower = font.toLowerCase();
  if (fontLower.includes('traditional')) data.docxFont = 'Traditional Arabic';
  else if (fontLower.includes('simplified')) data.docxFont = 'Simplified Arabic';
  else if (fontLower.includes('majalla')) data.docxFont = 'Sakkal Majalla';
  else if (fontLower.includes('tahoma')) data.docxFont = 'Tahoma';
  else if (fontLower.includes('calibri')) data.docxFont = 'Calibri';
  else if (fontLower.includes('segoe')) data.docxFont = 'Segoe UI';
  else if (fontLower.includes('times')) data.docxFont = 'Times New Roman';
  else if (fontLower.includes('arial')) data.docxFont = 'Arial';
  else if (fontLower.includes('cairo')) data.docxFont = 'Cairo';
  else if (fontLower.includes('amiri')) data.docxFont = 'Amiri';
  else if (fontLower.includes('tajawal')) data.docxFont = 'Tajawal';
  else if (fontLower.includes('almarai')) data.docxFont = 'Almarai';

  syncCellWithPageModifiedTexts(data);
}

function syncCellWithPageModifiedTexts(data) {
  if (!data || !data.id) return;
  if (!pageModifiedTexts.has(currentPageNumber)) {
    pageModifiedTexts.set(currentPageNumber, new Map());
  }
  const pageMods = pageModifiedTexts.get(currentPageNumber);

  if (data.text === data.origText && !data.isDeleted) {
    pageMods.delete(data.id);
  } else {
    pageMods.set(data.id, {
      id: data.id,
      origStr: data.origText || '',
      newStr: data.text || '',
      fontSize: data.fontSize || 14,
      fontFamily: data.fontFamily || 'Cairo, sans-serif',
      color: data.color || '#111827',
      bg: data.bg || '#ffffff',
      isBold: !!data.isBold,
      isItalic: !!data.isItalic,
      isUnderline: !!data.isUnderline,
      isStrike: !!data.isStrike,
      align: data.align || 'center',
      x: data.normX || 0,
      y: data.normY || 0,
      width: data.normW || 0.1,
      height: data.normH || 0.03,
      isDeleted: !data.text
    });
  }
}

function deleteTextBlock(block, itemId, origStr, normX, normY, normW, normH, fontSize, mappedFont, isBold, isItalic, cleanFamily, isCentered) {
  if (!pageModifiedTexts.has(currentPageNumber)) {
    pageModifiedTexts.set(currentPageNumber, new Map());
  }
  const pageMods = pageModifiedTexts.get(currentPageNumber);
  const existingMod = pageMods.get(itemId);
  const detectedBg = existingMod?.bg || sampleCanvasBackgroundColor(block.offsetLeft, block.offsetTop, block.clientWidth, block.clientHeight);

  const deletedMod = {
    id: itemId,
    origStr,
    newStr: '',
    x: normX,
    y: normY,
    width: normW,
    height: normH,
    fontSize,
    fontFamily: mappedFont,
    color: '#111827',
    bg: detectedBg || '#ffffff',
    isDeleted: true
  };
  pageMods.set(itemId, deletedMod);
  block.classList.add('is-deleted');
  block.classList.remove('is-modified');
  block.textContent = '';
  block.style.backgroundColor = detectedBg || '#ffffff';

  recordHistory({
    type: 'text-modify',
    page: currentPageNumber,
    itemId,
    prev: existingMod ? { ...existingMod } : null,
    next: { ...deletedMod },
    origStr,
    normX, normY, normW, normH, fontSize, mappedFont, isBold, isItalic, cleanFamily, isCentered
  });
}

async function renderInPlaceTextLayer(page, viewport) {
  if (typeof finalizeActiveTextEdit === 'function' && activeTextEditContext) {
    finalizeActiveTextEdit();
  }
  textLayer.replaceChildren();
  const [textContent] = await Promise.all([
    page.getTextContent(),
    page.getOperatorList()
  ]);
  const rawItems = textContent.items || [];
  const styles = textContent.styles || {};
  const modifiedMap = pageModifiedTexts.get(currentPageNumber) || new Map();

  const stageWidth = stage.clientWidth;
  const stageHeight = stage.clientHeight;

  // Pre-resolve all unique fonts asynchronously from PDF.js commonObjs
  const fontNames = [...new Set(rawItems.map((it) => it.fontName).filter(Boolean))];
  const resolvedFonts = new Map();
  await Promise.all(
    fontNames.map(
      (fName) =>
        new Promise((resolve) => {
          if (!page?.commonObjs) return resolve();
          let done = false;
          const finish = (font) => {
            if (!done) {
              done = true;
              if (font) {
                resolvedFonts.set(fName, font);
                cachedPageFonts.set(fName, font);
              }
              resolve();
            }
          };
          try {
            page.commonObjs.get(fName, finish);
          } catch (_) {
            finish(null);
          }
          setTimeout(() => finish(null), 100);
        })
    )
  );

  // Group contiguous words into cohesive lines/cells
  const groupedLines = groupTextItemsIntoLines(rawItems, viewport);
  if (!groupedLines.length) return;

  // 1. Group lines by vertical baseline to identify rows and tabular patterns
  const rowBuckets = [];
  for (const line of groupedLines) {
    let matched = null;
    for (const rb of rowBuckets) {
      if (Math.abs(rb.ty - line.ty) <= Math.max(line.fontSize, rb.fontSize) * 0.55) {
        matched = rb;
        break;
      }
    }
    if (matched) {
      matched.lines.push(line);
      matched.fontSize = Math.max(matched.fontSize, line.fontSize);
    } else {
      rowBuckets.push({ ty: line.ty, fontSize: line.fontSize, lines: [line] });
    }
  }

  // Calculate average row height for table rows
  rowBuckets.sort((a, b) => b.ty - a.ty);
  let avgRowHeight = 30;
  if (rowBuckets.length >= 2) {
    let diffSum = 0;
    let diffCount = 0;
    for (let i = 0; i < rowBuckets.length - 1; i++) {
      const d = rowBuckets[i].ty - rowBuckets[i + 1].ty;
      if (d > 14 && d < 75) {
        diffSum += d;
        diffCount++;
      }
    }
    if (diffCount > 0) avgRowHeight = diffSum / diffCount;
  }

  // 2. Identify repeating columns across multi-cell rows (Table Detection)
  const multiColRows = rowBuckets.filter((rb) => rb.lines.length >= 2);
  const colBands = [];
  if (multiColRows.length >= 2) {
    const cellsList = [];
    multiColRows.forEach((rb) => {
      rb.lines.forEach((l) => cellsList.push({ minX: l.tx, maxX: l.tx + l.width, line: l }));
    });
    cellsList.sort((a, b) => a.minX - b.minX);
    for (const cell of cellsList) {
      let matchedBand = null;
      for (const band of colBands) {
        if (Math.abs(band.center - (cell.minX + cell.maxX) / 2) < 48 ||
            (cell.minX >= band.minX - 20 && cell.maxX <= band.maxX + 20)) {
          matchedBand = band;
          break;
        }
      }
      if (matchedBand) {
        matchedBand.minX = Math.min(matchedBand.minX, cell.minX);
        matchedBand.maxX = Math.max(matchedBand.maxX, cell.maxX);
        matchedBand.center = (matchedBand.minX + matchedBand.maxX) / 2;
        matchedBand.count++;
      } else {
        colBands.push({
          minX: cell.minX,
          maxX: cell.maxX,
          center: (cell.minX + cell.maxX) / 2,
          count: 1
        });
      }
    }
    colBands.sort((a, b) => a.minX - b.minX);
  }

  const unscaledWidth = viewport.width / viewport.scale;
  const pageMidX = unscaledWidth / 2;

  // Detect dominant vertical center axes across all lines on the page (certificates, headers, notices)
  const centerClusters = [];
  groupedLines.forEach((l) => {
    const mid = l.tx + l.width / 2;
    let cluster = centerClusters.find((c) => Math.abs(c.center - mid) < 32);
    if (cluster) {
      cluster.count++;
      cluster.center = (cluster.center * (cluster.count - 1) + mid) / cluster.count;
    } else {
      centerClusters.push({ center: mid, count: 1 });
    }
  });
  centerClusters.sort((a, b) => b.count - a.count);
  const dominantCenterCluster = centerClusters[0] && centerClusters[0].count >= 2 ? centerClusters[0] : null;

  // 3. Render blocks: Direct 1:1 PDF text coordinates with ZERO displacement
  groupedLines.forEach((line) => {
    const itemId = line.id;
    const origStr = line.str;
    const fontSize = line.fontSize;
    const { isBold, isItalic, mappedFont, cleanFamily } = detectFontProperties(line.fontName, styles, page, origStr, resolvedFonts);
    const effectiveFont = mappedFont;

    // Exact PDF text coordinates:
    // line.tx: x start of text baseline
    // line.ty: y of text baseline
    // Top of glyphs in PDF space is line.ty + fontSize * 0.88
    const [screenLeft, screenTop] = viewport.convertToViewportPoint(line.tx, line.ty + fontSize * 0.88);
    const widthPx = Math.max(12, line.width * viewport.scale);
    const heightPx = Math.max(14, fontSize * viewport.scale * 1.2);

    const normX = screenLeft / stageWidth;
    const normY = screenTop / stageHeight;
    const normW = widthPx / stageWidth;
    const normH = heightPx / stageHeight;

    const lineMidX = line.tx + line.width / 2;
    const isDominantCentered = dominantCenterCluster && Math.abs(lineMidX - dominantCenterCluster.center) < 36;
    const isPageCentered = Math.abs(lineMidX - pageMidX) < unscaledWidth * 0.22;
    let isColBandCentered = false;
    if (colBands.length > 0) {
      for (const band of colBands) {
        if (line.tx >= band.minX - 25 && line.tx + line.width <= band.maxX + 25) {
          if (Math.abs(lineMidX - band.center) < Math.max(25, (band.maxX - band.minX) * 0.28)) {
            isColBandCentered = true;
            break;
          }
        }
      }
    }
    const isCentered = Boolean(isDominantCentered || isPageCentered || isColBandCentered);

    const isEffectiveBold = Boolean(isBold);
    const isArabic = /[\u0600-\u06FF]/.test(origStr);

    const block = document.createElement('div');
    block.className = 'pe-text-block';
    block.dataset.id = itemId;
    block.dataset.origStr = origStr;
    block.title = isAr ? 'انقر لتعديل النص، أو اسحب للتحريك' : 'Click to edit text, drag to move';
    block.style.left = `${Math.round(screenLeft)}px`;
    block.style.top = `${Math.round(screenTop)}px`;
    block.style.minWidth = `${Math.round(widthPx)}px`;
    block.style.width = isCentered ? `${Math.round(widthPx)}px` : 'max-content';
    block.style.maxWidth = '100%';
    block.style.minHeight = `${Math.round(heightPx)}px`;
    block.style.fontSize = `${Math.round(fontSize * viewport.scale)}px`;
    block.style.fontFamily = effectiveFont;
    block.style.fontWeight = isEffectiveBold ? '700' : '400';
    block.style.fontStyle = isItalic ? 'italic' : 'normal';
    block.style.direction = isArabic ? 'rtl' : 'ltr';
    if (isCentered) {
      block.style.textAlign = 'center';
    } else {
      block.style.textAlign = isArabic ? 'right' : 'left';
    }

    const mod = modifiedMap.get(itemId);
    if (mod) {
      if (mod.isMoved) {
        block.style.left = `${Math.round(mod.x * stageWidth)}px`;
        block.style.top = `${Math.round(mod.y * stageHeight)}px`;
        if (mod.origNormX !== undefined && mod.origNormY !== undefined) {
          const origMask = document.createElement('div');
          origMask.className = 'pe-text-orig-mask';
          origMask.style.position = 'absolute';
          origMask.style.left = `${Math.round(mod.origNormX * stageWidth) - 2}px`;
          origMask.style.top = `${Math.round(mod.origNormY * stageHeight) - 2}px`;
          origMask.style.width = `${Math.round((mod.origNormW || mod.width) * stageWidth) + 4}px`;
          origMask.style.height = `${Math.round((mod.origNormH || mod.height) * stageHeight) + 4}px`;
          origMask.style.backgroundColor = mod.bg || '#ffffff';
          origMask.style.pointerEvents = 'none';
          origMask.style.zIndex = '15';
          textLayer.append(origMask);
        }
      }
      if (mod.isDeleted) {
        block.classList.add('is-deleted');
        block.textContent = '';
        block.style.backgroundColor = mod.bg || '#ffffff';
      } else {
        block.classList.add('is-modified');
        block.textContent = mod.newStr;
        block.style.color = mod.color || '#111827';
        block.style.backgroundColor = mod.bg || '#ffffff';
        block.style.fontFamily = mod.fontFamily || mappedFont;
        block.style.fontSize = `${Math.round(mod.fontSize * viewport.scale)}px`;
        block.style.fontWeight = mod.isBold ? '700' : '400';
        block.style.fontStyle = mod.isItalic ? 'italic' : 'normal';
        if (mod.isUnderline) block.style.textDecoration = 'underline';
        if (mod.isStrike) block.style.textDecoration = 'line-through';
        if (mod.align) block.style.textAlign = mod.align;
      }
    } else {
      // Crisp PDF canvas shows beneath; text layer retains selection & bounds
      block.textContent = origStr;
    }

    // Dragging and editing support for text blocks
    let isBlockDragging = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let blockStartLeft = 0;
    let blockStartTop = 0;
    let hasDragged = false;

    const onBlockPointerMove = (e) => {
      if (!isBlockDragging) return;
      const dx = e.clientX - dragStartX;
      const dy = e.clientY - dragStartY;
      if (Math.hypot(dx, dy) > 12) {
        if (!hasDragged) {
          hasDragged = true;
          block.classList.add('is-dragging');
        }
        block.style.left = `${Math.round(blockStartLeft + dx)}px`;
        block.style.top = `${Math.round(blockStartTop + dy)}px`;
        if (!block.classList.contains('is-modified')) {
          const sampledBg = sampleCanvasBackgroundColor(blockStartLeft, blockStartTop, block.clientWidth, block.clientHeight);
          const sampledColor = sampleCanvasTextColor(blockStartLeft, blockStartTop, block.clientWidth, block.clientHeight, sampledBg);
          block.style.color = sampledColor || '#111827';
          block.style.backgroundColor = sampledBg || '#ffffff';
        }
      }
    };

    const onBlockPointerUp = (e) => {
      if (!isBlockDragging) return;
      isBlockDragging = false;
      block.classList.remove('is-dragging');
      window.removeEventListener('pointermove', onBlockPointerMove);
      window.removeEventListener('pointerup', onBlockPointerUp);

      if (hasDragged) {
        // Text was moved across the page: save position and history
        const finalLeft = block.offsetLeft;
        const finalTop = block.offsetTop;
        const newNormX = finalLeft / stageWidth;
        const newNormY = finalTop / stageHeight;

        if (!pageModifiedTexts.has(currentPageNumber)) {
          pageModifiedTexts.set(currentPageNumber, new Map());
        }
        const pageMods = pageModifiedTexts.get(currentPageNumber);
        const existingMod = pageMods.get(itemId);

        const sampledBg = existingMod?.bg || sampleCanvasBackgroundColor(blockStartLeft, blockStartTop, block.clientWidth, block.clientHeight);
        const sampledColor = existingMod?.color || sampleCanvasTextColor(finalLeft, finalTop, block.clientWidth, block.clientHeight, sampledBg);

        const updatedMod = {
          id: itemId,
          origStr,
          newStr: existingMod?.newStr || block.textContent || origStr,
          x: newNormX,
          y: newNormY,
          width: normW,
          height: normH,
          origNormX: normX,
          origNormY: normY,
          origNormW: normW,
          origNormH: normH,
          isMoved: true,
          fontSize: existingMod?.fontSize || fontSize,
          fontFamily: existingMod?.fontFamily || mappedFont,
          color: sampledColor || '#111827',
          bg: sampledBg || '#ffffff',
          isBold: existingMod ? Boolean(existingMod.isBold) : isEffectiveBold,
          isItalic: existingMod ? Boolean(existingMod.isItalic) : isItalic,
          isUnderline: Boolean(existingMod?.isUnderline),
          isStrike: Boolean(existingMod?.isStrike),
          align: existingMod?.align || (isCentered ? 'center' : (isArabic ? 'right' : 'left')),
          isDeleted: false
        };

        pageMods.set(itemId, updatedMod);
        block.classList.add('is-modified');
        block.style.color = updatedMod.color;
        block.style.backgroundColor = updatedMod.bg;

        recordHistory({
          type: 'text-move',
          page: currentPageNumber,
          itemId,
          prev: existingMod ? { ...existingMod } : null,
          next: { ...updatedMod },
          origLeft: blockStartLeft,
          origTop: blockStartTop
        });
      } else {
        // User clicked or tapped: activate in-place text editing immediately!
        startInPlaceEdit(block, itemId, origStr, normX, normY, normW, normH, fontSize, effectiveFont, isEffectiveBold, isItalic, cleanFamily, isCentered);
      }
      if (e) e.stopPropagation();
    };

    block.addEventListener('pointerenter', () => {
      if (activeTextEditContext) return;
      const currentBg = block._sampledBg || sampleCanvasBackgroundColor(block.offsetLeft, block.offsetTop, block.clientWidth, block.clientHeight);
      block._sampledBg = currentBg;
      const currentColor = block._sampledColor || sampleCanvasTextColor(block.offsetLeft, block.offsetTop, block.clientWidth, block.clientHeight, currentBg);
      block._sampledColor = currentColor;

      const mod = modifiedMap.get(itemId);
      syncToolbarWithTextBlock(block, {
        fontFamily: mod?.fontFamily || effectiveFont,
        cleanFamily,
        fontSize: mod?.fontSize || fontSize,
        isBold: mod ? Boolean(mod.isBold) : isEffectiveBold,
        isItalic: mod ? Boolean(mod.isItalic) : isItalic,
        isUnderline: Boolean(mod?.isUnderline),
        isStrike: Boolean(mod?.isStrike),
        align: mod?.align || (isCentered ? 'center' : (isArabic ? 'right' : 'left')),
        color: mod?.color || currentColor,
        bg: mod?.bg || currentBg
      });
    });

    block.addEventListener('pointerdown', (e) => {
      if (block.contentEditable === 'true') {
        // Already editing in-place: allow normal text cursor positioning
        return;
      }
      // Commit any previously active text edit before touching this block
      if (activeTextEditContext && activeTextEditContext.itemId !== itemId) {
        finalizeActiveTextEdit();
      }
      if (activeTool === 'eraser') {
        deleteTextBlock(block, itemId, origStr, normX, normY, normW, normH, fontSize, effectiveFont, isEffectiveBold, isItalic, cleanFamily, isCentered);
        e.stopPropagation();
        return;
      }
      isBlockDragging = true;
      hasDragged = false;
      dragStartX = e.clientX;
      dragStartY = e.clientY;
      blockStartLeft = block.offsetLeft;
      blockStartTop = block.offsetTop;
      window.addEventListener('pointermove', onBlockPointerMove);
      window.addEventListener('pointerup', onBlockPointerUp);
      e.stopPropagation();
    });

    block.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!hasDragged) {
        startInPlaceEdit(block, itemId, origStr, normX, normY, normW, normH, fontSize, effectiveFont, isEffectiveBold, isItalic, cleanFamily, isCentered);
      }
    });

    block.addEventListener('focus', () => {
      const mod = modifiedMap.get(itemId);
      const curBg = block._sampledBg || sampleCanvasBackgroundColor(block.offsetLeft, block.offsetTop, block.clientWidth, block.clientHeight);
      const curColor = block._sampledColor || sampleCanvasTextColor(block.offsetLeft, block.offsetTop, block.clientWidth, block.clientHeight, curBg);
      syncToolbarWithTextBlock(block, {
        fontFamily: mod?.fontFamily || effectiveFont,
        cleanFamily,
        fontSize: mod?.fontSize || fontSize,
        isBold: mod ? Boolean(mod.isBold) : isEffectiveBold,
        isItalic: mod ? Boolean(mod.isItalic) : isItalic,
        isUnderline: Boolean(mod?.isUnderline),
        isStrike: Boolean(mod?.isStrike),
        align: mod?.align || (isCentered ? 'center' : (isArabic ? 'right' : 'left')),
        color: mod?.color || block.style.color || curColor,
        bg: mod?.bg || block.style.backgroundColor || curBg
      });
    });

    block.addEventListener('dblclick', (e) => {
      e.stopPropagation();
      startInPlaceEdit(block, itemId, origStr, normX, normY, normW, normH, fontSize, effectiveFont, isEffectiveBold, isItalic, cleanFamily, isCentered);
    });

    textLayer.append(block);
  });
}

function finalizeActiveTextEdit() {
  if (!activeTextEditContext) return;
  const ctx = activeTextEditContext;
  activeTextEditContext = null;

  const {
    block, itemId, origStr, normX, normY, normW, normH,
    fontSize, mappedFont, isBold, isItalic, cleanFamily, isCentered,
    hasUserStyled, hasUserTyped
  } = ctx;

  block.contentEditable = 'false';
  block.classList.remove('is-active-edit');
  block.style.outline = 'none';
  block.style.border = 'none';
  block.style.boxShadow = 'none';

  if (ctx.onDocPointerDown) {
    document.removeEventListener('pointerdown', ctx.onDocPointerDown);
  }

  const updatedText = block.textContent.trim();

  if (!pageModifiedTexts.has(currentPageNumber)) {
    pageModifiedTexts.set(currentPageNumber, new Map());
  }
  const currentMods = pageModifiedTexts.get(currentPageNumber);
  const existingMod = currentMods.get(itemId);

  if (updatedText === '') {
    // Deleted text
    const deletedMod = {
      id: itemId,
      origStr,
      newStr: '',
      x: normX,
      y: normY,
      width: normW,
      height: normH,
      fontSize: existingMod?.fontSize || fontSize,
      fontFamily: existingMod?.fontFamily || mappedFont,
      color: existingMod?.color || '#111827',
      bg: existingMod?.bg || ctx.bg || '#ffffff',
      isDeleted: true
    };
    currentMods.set(itemId, deletedMod);
    block.classList.add('is-deleted');
    block.classList.remove('is-modified');
    block.textContent = '';
    block.style.backgroundColor = deletedMod.bg;

    recordHistory({
      type: 'text-modify',
      page: currentPageNumber,
      itemId,
      prev: existingMod ? { ...existingMod } : null,
      next: { ...deletedMod },
      origStr,
      normX, normY, normW, normH, fontSize, mappedFont, isBold, isItalic, cleanFamily, isCentered
    });
    return;
  }

  const cleanUpdated = updatedText.replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim();
  const cleanOrig = origStr.replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim();
  const textChanged = cleanUpdated !== cleanOrig;
  const shouldSave = textChanged || hasUserStyled || hasUserTyped || Boolean(existingMod);

  if (shouldSave) {
    const finalFont = existingMod?.fontFamily || ctx.fontFamily;
    const finalSize = existingMod?.fontSize || ctx.fontSize;
    const finalBold = existingMod ? Boolean(existingMod.isBold) : ctx.isBold;
    const finalItalic = existingMod ? Boolean(existingMod.isItalic) : ctx.isItalic;
    const finalUnderline = Boolean(existingMod?.isUnderline || ctx.isUnderline);
    const finalStrike = Boolean(existingMod?.isStrike || ctx.isStrike);
    const finalColor = existingMod?.color || ctx.color;
    const finalBg = existingMod?.bg || ctx.bg;
    const align = existingMod?.align || ctx.align;

    const updatedMod = {
      id: itemId,
      origStr,
      newStr: updatedText,
      x: normX,
      y: normY,
      width: normW,
      height: normH,
      fontSize: finalSize,
      fontFamily: finalFont,
      color: finalColor,
      bg: finalBg,
      isBold: finalBold,
      isItalic: finalItalic,
      isUnderline: finalUnderline,
      isStrike: finalStrike,
      align,
      isDeleted: false
    };

    currentMods.set(itemId, updatedMod);
    block.classList.add('is-modified');
    block.classList.remove('is-deleted');
    block.textContent = updatedText;
    block.style.color = finalColor;
    block.style.backgroundColor = finalBg;
    block.style.fontFamily = finalFont;
    block.style.fontSize = `${Math.round(finalSize * currentZoom)}px`;
    block.style.fontWeight = finalBold ? '700' : '400';
    block.style.fontStyle = finalItalic ? 'italic' : 'normal';
    block.style.textAlign = align;

    recordHistory({
      type: 'text-modify',
      page: currentPageNumber,
      itemId,
      prev: existingMod ? { ...existingMod } : null,
      next: { ...updatedMod },
      origStr,
      normX, normY, normW, normH, fontSize, mappedFont, isBold, isItalic, cleanFamily, isCentered
    });
  } else {
    // Untouched text: keep crisp authentic PDF rendering
    currentMods.delete(itemId);
    block.classList.remove('is-modified');
    block.classList.remove('is-deleted');
    block.textContent = origStr;
    block.style.color = 'transparent';
    block.style.backgroundColor = 'transparent';
  }
}

function startInPlaceEdit(block, itemId, origStr, normX, normY, normW, normH, fontSize, mappedFont, isBold, isItalic, cleanFamily = '', isCentered = false) {
  // 1. Commit and finalize any previously active text block FIRST!
  if (activeTextEditContext && activeTextEditContext.itemId !== itemId) {
    finalizeActiveTextEdit();
  } else if (activeTextEditContext && activeTextEditContext.itemId === itemId) {
    syncToolbarWithTextBlock(block, {
      fontFamily: activeTextEditContext.fontFamily,
      cleanFamily: activeTextEditContext.cleanFamily,
      fontSize: activeTextEditContext.fontSize,
      isBold: activeTextEditContext.isBold,
      isItalic: activeTextEditContext.isItalic,
      isUnderline: activeTextEditContext.isUnderline,
      isStrike: activeTextEditContext.isStrike,
      align: activeTextEditContext.align,
      color: activeTextEditContext.color,
      bg: activeTextEditContext.bg
    });
    return;
  }

  currentActiveTextId = itemId;
  if (typeof switchRibbonTab === 'function') switchRibbonTab('home');

  const modifiedMap = pageModifiedTexts.get(currentPageNumber) || new Map();
  const existingMod = modifiedMap.get(itemId);
  const currentText = existingMod ? existingMod.newStr : origStr;

  let detectedBg = existingMod?.bg || sampleCanvasBackgroundColor(block.offsetLeft, block.offsetTop, block.clientWidth, block.clientHeight);
  let detectedColor = existingMod?.color || sampleCanvasTextColor(block.offsetLeft, block.offsetTop, block.clientWidth, block.clientHeight, detectedBg);

  // Sanity check: Background and text color MUST NEVER BE THE SAME!
  if (detectedBg && detectedColor) {
    const bgR = parseInt(detectedBg.slice(1, 3) || 'ff', 16);
    const bgG = parseInt(detectedBg.slice(3, 5) || 'ff', 16);
    const bgB = parseInt(detectedBg.slice(5, 7) || 'ff', 16);
    const txR = parseInt(detectedColor.slice(1, 3) || '00', 16);
    const txG = parseInt(detectedColor.slice(3, 5) || '00', 16);
    const txB = parseInt(detectedColor.slice(5, 7) || '00', 16);
    const colorDist = Math.hypot(bgR - txR, bgG - txG, bgB - txB);
    if (colorDist < 45) {
      detectedBg = '#ffffff';
    }
  }

  const currentFont = existingMod?.fontFamily || mappedFont;
  const currentSize = Math.round(existingMod?.fontSize || fontSize);
  const currentBold = existingMod ? Boolean(existingMod.isBold) : isBold;
  const currentItalic = existingMod ? Boolean(existingMod.isItalic) : isItalic;
  const isArabic = /[\u0600-\u06FF]/.test(currentText);
  const initialAlign = existingMod?.align || (isCentered ? 'center' : (isArabic ? 'right' : 'left'));

  syncToolbarWithTextBlock(block, {
    fontFamily: currentFont,
    cleanFamily,
    fontSize: currentSize,
    isBold: currentBold,
    isItalic: currentItalic,
    isUnderline: Boolean(existingMod?.isUnderline),
    isStrike: Boolean(existingMod?.isStrike),
    align: initialAlign,
    color: detectedColor,
    bg: detectedBg
  });

  block.contentEditable = 'true';
  block.classList.add('is-active-edit');
  block.textContent = currentText;
  block.style.direction = isArabic ? 'rtl' : 'ltr';
  block.style.textAlign = initialAlign;
  block.style.cursor = 'text';

  block.style.color = detectedColor;
  block.style.backgroundColor = detectedBg || '#ffffff';
  block.style.fontFamily = currentFont;
  block.style.fontSize = `${Math.round(currentSize * currentZoom)}px`;
  block.style.fontWeight = currentBold ? '700' : '400';
  block.style.fontStyle = currentItalic ? 'italic' : 'normal';

  activeTextEditContext = {
    block,
    itemId,
    origStr,
    initialText: currentText,
    normX, normY, normW, normH,
    fontSize: currentSize,
    mappedFont,
    fontFamily: currentFont,
    color: detectedColor,
    bg: detectedBg,
    isBold: currentBold,
    isItalic: currentItalic,
    isUnderline: Boolean(existingMod?.isUnderline),
    isStrike: Boolean(existingMod?.isStrike),
    cleanFamily,
    isCentered,
    align: initialAlign,
    hasUserStyled: Boolean(existingMod),
    hasUserTyped: false,
    onDocPointerDown: null
  };

  block.focus();

  // Position cursor cleanly at end of text without selecting all text in blue
  try {
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      const textNode = block.firstChild || block;
      const range = document.createRange();
      if (textNode.nodeType === Node.TEXT_NODE) {
        range.setStart(textNode, textNode.length);
        range.setEnd(textNode, textNode.length);
      } else {
        range.selectNodeContents(block);
        range.collapse(false);
      }
      sel.addRange(range);
    }
  } catch (_) {}

  const onDocPointerDown = (evt) => {
    if (evt.target === block || block.contains(evt.target)) return;
    if (
      evt.target.closest('.pe-tools-ribbon') ||
      evt.target.closest('.kz-select-dropdown') ||
      evt.target.closest('.kz-select-wrapper') ||
      evt.target.closest('.kz-modal') ||
      evt.target.closest('.kz-modal-overlay')
    ) {
      return;
    }
    finalizeActiveTextEdit();
  };

  setTimeout(() => {
    if (activeTextEditContext && activeTextEditContext.itemId === itemId) {
      document.addEventListener('pointerdown', onDocPointerDown);
      activeTextEditContext.onDocPointerDown = onDocPointerDown;
    }
  }, 40);

  block.addEventListener('input', () => {
    if (activeTextEditContext && activeTextEditContext.itemId === itemId) {
      activeTextEditContext.hasUserTyped = true;
      block.style.color = activeTextEditContext.color;
      block.style.backgroundColor = activeTextEditContext.bg;
      block.style.fontFamily = activeTextEditContext.fontFamily;
      block.style.fontSize = `${Math.round(activeTextEditContext.fontSize * currentZoom)}px`;
      block.style.fontWeight = activeTextEditContext.isBold ? '700' : '400';
      block.style.fontStyle = activeTextEditContext.isItalic ? 'italic' : 'normal';
      block.classList.add('is-modified');
      if (activeTextEditContext.isCentered && block.scrollWidth > block.clientWidth) {
        const extra = block.scrollWidth - block.clientWidth;
        block.style.width = `${block.scrollWidth}px`;
        block.style.left = `${Math.round(block.offsetLeft - extra / 2)}px`;
      }
    }
  });

  block.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      finalizeActiveTextEdit();
    } else if (e.key === 'Escape') {
      block.textContent = currentText;
      if (!existingMod) {
        activeTextEditContext.hasUserTyped = false;
        activeTextEditContext.hasUserStyled = false;
      }
      finalizeActiveTextEdit();
    }
  });
}

// Live Toolbar Controls for Current Active Text Block
function updateActiveTextBlockFromToolbar() {
  if (isSyncingToolbar) return;
  if (!activeTextEditContext) return;
  const targetId = activeTextEditContext.itemId;
  const block = activeTextEditContext.block;
  if (!block) return;

  const font = detectedFontSelect ? detectedFontSelect.value : "'Traditional Arabic', 'Amiri', serif";
  const size = Number(detectedSizeInput ? detectedSizeInput.value : 14);
  const isBold = detectedBoldBtn ? detectedBoldBtn.classList.contains('active') : false;
  const isItalic = detectedItalicBtn ? detectedItalicBtn.classList.contains('active') : false;
  const isUnderline = detectedUnderlineBtn ? detectedUnderlineBtn.classList.contains('active') : false;
  const isStrike = detectedStrikeBtn ? detectedStrikeBtn.classList.contains('active') : false;
  const color = detectedColorInput ? normalizeHexColor(detectedColorInput.value, '#111827') : '#111827';
  const bg = detectedBgInput ? normalizeHexColor(detectedBgInput.value, '#ffffff') : '#ffffff';

  const isArabic = /[\u0600-\u06FF]/.test(block.textContent || '');
  let align = isArabic ? 'right' : 'left';
  if (alignCenterBtn && alignCenterBtn.classList.contains('active')) align = 'center';
  else if (alignRightBtn && alignRightBtn.classList.contains('active')) align = 'right';
  else if (alignLeftBtn && alignLeftBtn.classList.contains('active')) align = 'left';

  block.style.fontFamily = font;
  block.style.fontSize = `${Math.round(size * currentZoom)}px`;
  block.style.fontWeight = isBold ? '700' : '400';
  block.style.fontStyle = isItalic ? 'italic' : 'normal';

  const decs = [];
  if (isUnderline) decs.push('underline');
  if (isStrike) decs.push('line-through');
  block.style.textDecoration = decs.length ? decs.join(' ') : 'none';

  block.style.textAlign = align;
  block.style.color = color;
  block.style.backgroundColor = bg;
  block.classList.add('is-modified');

  if (activeTextEditContext && activeTextEditContext.itemId === targetId) {
    activeTextEditContext.hasUserStyled = true;
    activeTextEditContext.fontFamily = font;
    activeTextEditContext.fontSize = size;
    activeTextEditContext.isBold = isBold;
    activeTextEditContext.isItalic = isItalic;
    activeTextEditContext.isUnderline = isUnderline;
    activeTextEditContext.isStrike = isStrike;
    activeTextEditContext.color = color;
    activeTextEditContext.bg = bg;
    activeTextEditContext.align = align;
  }

  // Update or insert in pageModifiedTexts
  if (!pageModifiedTexts.has(currentPageNumber)) {
    pageModifiedTexts.set(currentPageNumber, new Map());
  }
  const pageMods = pageModifiedTexts.get(currentPageNumber);
  let mod = pageMods.get(targetId);
  if (mod) {
    mod.fontFamily = font;
    mod.fontSize = size;
    mod.isBold = isBold;
    mod.isItalic = isItalic;
    mod.isUnderline = isUnderline;
    mod.isStrike = isStrike;
    mod.color = color;
    mod.bg = bg;
    mod.align = align;
    mod.newStr = block.textContent || mod.newStr;
  } else {
    const stageWidth = stage.clientWidth;
    const stageHeight = stage.clientHeight;
    const normX = block.offsetLeft / stageWidth;
    const normY = block.offsetTop / stageHeight;
    const normW = block.clientWidth / stageWidth;
    const normH = block.clientHeight / stageHeight;
    const origStr = block.dataset.origStr || block.textContent || '';
    mod = {
      id: targetId,
      origStr,
      newStr: block.textContent || origStr,
      x: normX,
      y: normY,
      width: normW,
      height: normH,
      fontSize: size,
      fontFamily: font,
      color,
      bg,
      isBold,
      isItalic,
      isUnderline,
      isStrike,
      align,
      isDeleted: false
    };
    pageMods.set(targetId, mod);
  }
}

// ==========================================
// Windows Fonts & Google Fonts System
// ==========================================

const loadedGoogleFonts = new Set();
function loadWebFont(fontFamily) {
  if (!fontFamily) return;
  const match = fontFamily.match(/(?:'([^']+)'|([a-zA-Z\s]+))/);
  const rawName = (match ? (match[1] || match[2]) : fontFamily).trim();
  const googleFonts = [
    'Cairo', 'Amiri', 'Tajawal', 'Almarai', 'Changa', 'El Messiri', 'Lateef',
    'Mada', 'Markazi Text', 'Readex Pro', 'Reem Kufi', 'Scheherazade New',
    'Lemonada', 'Noto Sans Arabic', 'Noto Kufi Arabic', 'Noto Naskh Arabic',
    'IBM Plex Sans Arabic', 'Rubik', 'Inter', 'Roboto', 'Montserrat',
    'Poppins', 'Open Sans', 'Lato', 'Oswald', 'Raleway'
  ];
  const matchedGFont = googleFonts.find((g) => g.toLowerCase() === rawName.toLowerCase());
  if (!matchedGFont || loadedGoogleFonts.has(matchedGFont)) return;
  loadedGoogleFonts.add(matchedGFont);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(matchedGFont)}:ital,wght@0,400;0,700;1,400;1,700&display=swap`;
  document.head.appendChild(link);
}

async function loadLocalWindowsFonts() {
  if ('queryLocalFonts' in window) {
    try {
      const fonts = await window.queryLocalFonts();
      if (!fonts || !fonts.length) {
        showAlert(isAr ? 'لم يتم العثور على خطوط محلية في النظام.' : 'No local fonts found in system.', { type: 'info' });
        return;
      }
      const uniqueFamilies = Array.from(new Set(fonts.map((f) => f.family).filter(Boolean))).sort((a, b) => a.localeCompare(b));

      [detectedFontSelect, fontFamilySelect].forEach((select) => {
        if (!select) return;
        let optgroup = select.querySelector('optgroup[data-local="true"]');
        if (!optgroup) {
          optgroup = document.createElement('optgroup');
          optgroup.dataset.local = 'true';
          optgroup.label = isAr
            ? `💻 كافة خطوط ويندوز المثبتة (${uniqueFamilies.length})`
            : `💻 Installed Windows Fonts (${uniqueFamilies.length})`;
          select.insertBefore(optgroup, select.children[2] || select.firstChild);
        } else {
          optgroup.replaceChildren();
        }
        uniqueFamilies.forEach((family) => {
          const opt = document.createElement('option');
          opt.value = `'${family}', sans-serif`;
          opt.textContent = family;
          optgroup.appendChild(opt);
        });
      });

      showAlert(
        isAr
          ? `تم بنجاح استيراد كافة خطوط ويندوز من جهازك (${uniqueFamilies.length} خط)! يمكنك الآن اختيار أي خط منها مباشرة.`
          : `Successfully loaded all ${uniqueFamilies.length} fonts from your Windows system!`,
        { type: 'success' }
      );
    } catch (err) {
      if (err.name !== 'AbortError') {
        showAlert(isAr ? `تعذر استيراد الخطوط: ${err.message}` : `Failed to load local fonts: ${err.message}`, { type: 'error' });
      }
    }
  } else {
    const userFont = await showPrompt(
      isAr
        ? 'متصفحك الحالي لا يدعم الاستيراد التلقائي لكامل مكتبة الخطوط. اكتب اسم أي خط مثبت على جهازك في نظام ويندوز (مثل Traditional Arabic أو Dubai أو Lotus أو Diwani وسيعمل فوراً):'
        : 'Your browser does not support Local Font Access API. Type the name of any installed Windows font:',
      'Traditional Arabic',
      {
        title: isAr ? 'استيراد خط محلي' : 'Import Local Font',
        placeholder: 'e.g. Traditional Arabic, Dubai, Lotus'
      }
    );
    if (userFont && userFont.trim()) {
      applyCustomFont(userFont.trim());
    }
  }
}

function applyCustomFont(fontName) {
  const cleaned = fontName.replace(/["';]/g, '').trim();
  if (!cleaned) return;
  const fontVal = `'${cleaned}', sans-serif`;
  [detectedFontSelect, fontFamilySelect].forEach((select) => {
    if (!select) return;
    let customOpt = select.querySelector(`option[value="${fontVal}"]`);
    if (!customOpt) {
      customOpt = document.createElement('option');
      customOpt.value = fontVal;
      customOpt.textContent = `✍️ ${cleaned} (${isAr ? 'مخصص' : 'Custom'})`;
      let customGroup = select.querySelector('optgroup[data-group="custom-fonts"]');
      if (!customGroup) {
        customGroup = document.createElement('optgroup');
        customGroup.dataset.group = 'custom-fonts';
        customGroup.label = isAr ? '✍️ خطوط مخصصة' : '✍️ Custom Fonts';
        const firstGroup = select.querySelector('optgroup');
        if (firstGroup) select.insertBefore(customGroup, firstGroup);
        else select.appendChild(customGroup);
      }
      customGroup.prepend(customOpt);
    }
    select.value = fontVal;
  });
  textSettings.fontFamily = fontVal;
  updateActiveCellStyles();
}

if (loadLocalFontsBtn) {
  loadLocalFontsBtn.addEventListener('click', loadLocalWindowsFonts);
}

detectedFontSelect.addEventListener('change', async (e) => {
  const val = e.target.value;
  if (val === 'custom') {
    const fontName = await showPrompt(
      isAr
        ? 'اكتب اسم أي خط مثبت على جهازك في نظام ويندوز (مثال: Lotus أو Traditional Arabic أو Dubai أو Diwani):'
        : 'Type the name of any installed Windows font:',
      'Traditional Arabic',
      {
        title: isAr ? 'خط مخصص' : 'Custom Font',
        placeholder: 'e.g. Lotus, Traditional Arabic'
      }
    );
    if (fontName && fontName.trim()) {
      applyCustomFont(fontName.trim());
    } else {
      detectedFontSelect.value = textSettings.fontFamily || "'Traditional Arabic', 'Amiri', serif";
    }
    return;
  }
  if (val === 'local-query') {
    detectedFontSelect.value = textSettings.fontFamily || "'Traditional Arabic', 'Amiri', serif";
    loadLocalWindowsFonts();
    return;
  }
  loadWebFont(val);
  updateActiveCellStyles();
});
detectedSizeInput.addEventListener('input', updateActiveCellStyles);
fontSizeDecBtn.addEventListener('click', () => {
  detectedSizeInput.value = Math.max(6, Number(detectedSizeInput.value) - 1);
  updateActiveCellStyles();
});
fontSizeIncBtn.addEventListener('click', () => {
  detectedSizeInput.value = Math.min(120, Number(detectedSizeInput.value) + 1);
  updateActiveCellStyles();
});
detectedBoldBtn.addEventListener('click', () => {
  detectedBoldBtn.classList.toggle('active');
  updateActiveCellStyles();
});
detectedItalicBtn.addEventListener('click', () => {
  detectedItalicBtn.classList.toggle('active');
  updateActiveCellStyles();
});
detectedUnderlineBtn.addEventListener('click', () => {
  detectedUnderlineBtn.classList.toggle('active');
  updateActiveCellStyles();
});
if (detectedStrikeBtn) {
  detectedStrikeBtn.addEventListener('click', () => {
    detectedStrikeBtn.classList.toggle('active');
    updateActiveCellStyles();
  });
}
if (alignRightBtn) {
  alignRightBtn.addEventListener('click', () => {
    alignRightBtn.classList.add('active');
    if (alignCenterBtn) alignCenterBtn.classList.remove('active');
    if (alignLeftBtn) alignLeftBtn.classList.remove('active');
    updateActiveCellStyles();
  });
}
if (alignCenterBtn) {
  alignCenterBtn.addEventListener('click', () => {
    alignCenterBtn.classList.add('active');
    if (alignRightBtn) alignRightBtn.classList.remove('active');
    if (alignLeftBtn) alignLeftBtn.classList.remove('active');
    updateActiveCellStyles();
  });
}
if (alignLeftBtn) {
  alignLeftBtn.addEventListener('click', () => {
    alignLeftBtn.classList.add('active');
    if (alignRightBtn) alignRightBtn.classList.remove('active');
    if (alignCenterBtn) alignCenterBtn.classList.remove('active');
    updateActiveCellStyles();
  });
}
if (detectedColorInput) {
  detectedColorInput.addEventListener('input', updateActiveCellStyles);
  detectedColorInput.addEventListener('change', updateActiveCellStyles);
}
if (detectedBgInput) {
  detectedBgInput.addEventListener('input', updateActiveCellStyles);
  detectedBgInput.addEventListener('change', updateActiveCellStyles);
}

if (tableAddRowBtn) {
  tableAddRowBtn.addEventListener('click', () => {
    const tr = currentActiveCell?.closest('tr');
    const table = tr?.closest('table');
    const blockIdx = Number(table?.dataset.blockIdx);
    const docData = pageDocData.get(currentPageNumber);
    const block = docData?.blocks[blockIdx];
    if (tr && table && block) {
      insertTableRowAfter(tr, table, block);
    }
  });
}
if (tableDelRowBtn) {
  tableDelRowBtn.addEventListener('click', deleteTableRow);
}

deleteCurrentTextBtn.addEventListener('click', () => {
  if (!currentActiveTextId) {
    showAlert(isAr ? 'يرجى النقر على السطر أو الكلمة المراد حذفها أولاً.' : 'Please click on a text block first.', { type: 'info' });
    return;
  }
  const block = textLayer.querySelector(`.pe-text-block[data-id="${currentActiveTextId}"]`);
  if (!block) return;

  if (!pageModifiedTexts.has(currentPageNumber)) {
    pageModifiedTexts.set(currentPageNumber, new Map());
  }
  const pageMods = pageModifiedTexts.get(currentPageNumber);

  const rect = block.getBoundingClientRect();
  const stageRect = stage.getBoundingClientRect();

  pageMods.set(currentActiveTextId, {
    id: currentActiveTextId,
    origStr: block.textContent,
    newStr: '',
    x: (rect.left - stageRect.left) / stageRect.width,
    y: (rect.top - stageRect.top) / stageRect.height,
    width: rect.width / stageRect.width,
    height: rect.height / stageRect.height,
    fontSize: Number(detectedSizeInput.value),
    bg: detectedBgInput.value,
    isDeleted: true
  });

  block.classList.add('is-deleted');
  block.textContent = '';
  block.style.backgroundColor = detectedBgInput.value;

  recordHistory({
    type: 'text-modify',
    page: currentPageNumber,
    itemId: currentActiveTextId
  });

  showAlert(isAr ? 'تم طمس وحذف النص بنجاح.' : 'Text deleted and masked successfully.', { type: 'success' });
});

restoreTextsBtn.addEventListener('click', async () => {
  const confirmed = await showConfirm(
    isAr ? 'هل تريد استعادة جميع نصوص هذه الصفحة لحالتها الأصلية؟' : 'Restore all original text for this page?',
    {
      type: 'warning',
      confirmVariant: 'danger',
      confirmText: isAr ? 'استعادة' : 'Restore'
    }
  );
  if (confirmed) {
    pageDocData.delete(currentPageNumber);
    if (pageModifiedTexts.has(currentPageNumber)) {
      pageModifiedTexts.get(currentPageNumber).clear();
    }
    currentActiveCell = null;
    currentActiveTextId = null;
    renderCurrentPage();
  }
});

// ==========================================
// Drawing & Annotations Engine
// ==========================================

function renderDrawCanvas() {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const ctx = drawCanvas.getContext('2d');
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, drawCanvas.width, drawCanvas.height);
  ctx.scale(dpr, dpr);

  const stageWidth = stage.clientWidth;
  const stageHeight = stage.clientHeight;
  const annotations = pageAnnotations.get(currentPageNumber) || [];

  for (const ann of annotations) {
    if (ann.type === 'stroke' || ann.type === 'highlight') {
      if (!ann.points || ann.points.length < 2) continue;
      ctx.save();
      ctx.globalAlpha = ann.opacity !== undefined ? ann.opacity : 1.0;
      ctx.beginPath();
      ctx.moveTo(ann.points[0].x * stageWidth, ann.points[0].y * stageHeight);
      for (let i = 1; i < ann.points.length; i++) {
        ctx.lineTo(ann.points[i].x * stageWidth, ann.points[i].y * stageHeight);
      }
      ctx.strokeStyle = ann.color;
      ctx.lineWidth = ann.size;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      if (ann.type === 'highlight') {
        ctx.globalCompositeOperation = 'multiply';
      }
      ctx.stroke();
      ctx.restore();
    }
  }
}

function renderOverlayAnnotations() {
  overlay.replaceChildren();
  const stageWidth = stage.clientWidth;
  const stageHeight = stage.clientHeight;
  const annotations = pageAnnotations.get(currentPageNumber) || [];

  for (const ann of annotations) {
    if (ann.type === 'stroke' || ann.type === 'highlight') continue;

    const el = document.createElement('div');
    el.className = 'pe-annotation';
    el.dataset.id = ann.id;
    if (ann.id === selectedAnnotationId) el.classList.add('selected');

    const pxX = Math.round(ann.x * stageWidth);
    const pxY = Math.round(ann.y * stageHeight);
    const pxW = Math.round(ann.width * stageWidth);
    const pxH = Math.round(ann.height * stageHeight);

    el.style.left = `${pxX}px`;
    el.style.top = `${pxY}px`;
    el.style.width = `${pxW}px`;
    el.style.height = `${pxH}px`;
    el.style.opacity = ann.opacity !== undefined ? ann.opacity : 1.0;

    if (ann.type === 'text') {
      el.classList.add('pe-annotation-text');
      el.contentEditable = activeTool === 'select' ? 'true' : 'false';
      el.spellcheck = false;
      el.style.fontFamily = ann.fontFamily;
      el.style.fontSize = `${Math.round(ann.fontSize * (stageWidth / 800))}px`;
      el.style.color = ann.color;
      el.style.fontWeight = ann.bold ? '700' : '400';
      el.style.fontStyle = ann.italic ? 'italic' : 'normal';
      el.style.direction = 'auto';
      el.innerText = ann.text;

      el.addEventListener('input', () => { ann.text = el.innerText; });
      el.addEventListener('blur', () => {
        if (!el.innerText.trim()) removeAnnotation(ann.id);
      });
    } else if (ann.type === 'stamp') {
      el.classList.add('pe-annotation-stamp');
      el.style.color = ann.color;
      el.style.borderColor = ann.color;
      el.style.backgroundColor = `${ann.color}15`;
      el.style.fontSize = `${Math.max(12, Math.round(pxH * 0.42))}px`;
      el.textContent = ann.text;
    } else if (ann.type === 'mark') {
      el.classList.add('pe-annotation-mark');
      el.style.color = ann.color;
      el.style.fontSize = `${Math.round(pxH * 0.9)}px`;
      el.textContent = ann.mark;
    } else if (ann.type === 'note') {
      el.classList.add('pe-annotation-note');
      el.style.backgroundColor = ann.color;
      el.innerHTML = `<div class="pe-note-pin"><span style="font-size:10px;opacity:0.6;">📌</span></div><div class="pe-note-content" contenteditable="${activeTool === 'select' ? 'true' : 'false'}">${ann.text}</div>`;
      const contentEl = el.querySelector('.pe-note-content');
      contentEl.addEventListener('input', () => { ann.text = contentEl.innerText; });
    } else if (ann.type === 'shape') {
      el.classList.add('pe-annotation-shape');
      if (ann.isOriginalMask) {
        el.classList.add('pe-mask-annotation');
        el.style.backgroundColor = ann.fillColor || '#ffffff';
        el.style.pointerEvents = 'none';
      }
      el.innerHTML = renderShapeSvg(ann, pxW, pxH);
    } else if (ann.type === 'redact') {
      el.classList.add('pe-annotation-redact');
      el.style.backgroundColor = ann.color;
    } else if (ann.type === 'image') {
      el.classList.add('pe-annotation-image');
      const img = document.createElement('img');
      img.src = ann.src;
      img.draggable = false;
      el.append(img);
    }

    attachAnnotationEvents(el, ann);
    overlay.append(el);
  }

  // Render interactive hover overlays for all detected embedded PDF images/logos
  const detectedImages = pageDetectedImages.get(currentPageNumber) || [];
  for (const dImg of detectedImages) {
    const isLifted = annotations.some((a) =>
      (a.isOriginalMask || a.type === 'image') &&
      Math.abs(a.x - dImg.normX) < 0.04 &&
      Math.abs(a.y - dImg.normY) < 0.04
    );
    if (isLifted) continue;

    const imgEl = document.createElement('div');
    imgEl.className = 'pe-detected-image';
    imgEl.dataset.detectedId = dImg.id;
    imgEl.title = isAr ? 'انقر أو اسحب لتحريك الصورة / الشعار بحرية' : 'Click or drag to move logo/image freely';

    const pxX = Math.round(dImg.normX * stageWidth);
    const pxY = Math.round(dImg.normY * stageHeight);
    const pxW = Math.round(dImg.normW * stageWidth);
    const pxH = Math.round(dImg.normH * stageHeight);

    imgEl.style.left = `${pxX}px`;
    imgEl.style.top = `${pxY}px`;
    imgEl.style.width = `${pxW}px`;
    imgEl.style.height = `${pxH}px`;

    const badge = document.createElement('span');
    badge.className = 'pe-detected-img-badge';
    badge.textContent = isAr ? 'شعار / صورة ✥' : 'Logo / Image ✥';
    imgEl.append(badge);

    attachDetectedImageEvents(imgEl, dImg);
    overlay.append(imgEl);
  }
}

function renderShapeSvg(ann, w, h) {
  let fillAttr = 'none';
  if (ann.fill === 'semi') fillAttr = `${ann.strokeColor}40`;
  else if (ann.fill === 'solid') fillAttr = ann.fillColor || ann.strokeColor;

  let dashAttr = '';
  if (ann.dash === 'dashed') dashAttr = 'stroke-dasharray="6,4"';
  else if (ann.dash === 'dotted') dashAttr = 'stroke-dasharray="2,3"';

  if (ann.shapeType === 'rect') {
    return `<svg width="100%" height="100%" viewBox="0 0 ${w} ${h}">
      <rect x="${ann.strokeWidth / 2}" y="${ann.strokeWidth / 2}" width="${Math.max(1, w - ann.strokeWidth)}" height="${Math.max(1, h - ann.strokeWidth)}" fill="${fillAttr}" stroke="${ann.strokeColor}" stroke-width="${ann.strokeWidth}" ${dashAttr} rx="4" />
    </svg>`;
  } else if (ann.shapeType === 'rounded-rect') {
    return `<svg width="100%" height="100%" viewBox="0 0 ${w} ${h}">
      <rect x="${ann.strokeWidth / 2}" y="${ann.strokeWidth / 2}" width="${Math.max(1, w - ann.strokeWidth)}" height="${Math.max(1, h - ann.strokeWidth)}" fill="${fillAttr}" stroke="${ann.strokeColor}" stroke-width="${ann.strokeWidth}" ${dashAttr} rx="12" />
    </svg>`;
  } else if (ann.shapeType === 'circle') {
    return `<svg width="100%" height="100%" viewBox="0 0 ${w} ${h}">
      <ellipse cx="${w / 2}" cy="${h / 2}" rx="${Math.max(1, w / 2 - ann.strokeWidth)}" ry="${Math.max(1, h / 2 - ann.strokeWidth)}" fill="${fillAttr}" stroke="${ann.strokeColor}" stroke-width="${ann.strokeWidth}" ${dashAttr} />
    </svg>`;
  } else if (ann.shapeType === 'line') {
    return `<svg width="100%" height="100%" viewBox="0 0 ${w} ${h}">
      <line x1="2" y1="${h / 2}" x2="${w - 2}" y2="${h / 2}" stroke="${ann.strokeColor}" stroke-width="${ann.strokeWidth}" ${dashAttr} stroke-linecap="round" />
    </svg>`;
  } else if (ann.shapeType === 'arrow') {
    return `<svg width="100%" height="100%" viewBox="0 0 ${w} ${h}">
      <defs>
        <marker id="arrowhead-${ann.id}" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
          <polygon points="0 0, 8 3, 0 6" fill="${ann.strokeColor}" />
        </marker>
      </defs>
      <line x1="2" y1="${h / 2}" x2="${Math.max(2, w - 8)}" y2="${h / 2}" stroke="${ann.strokeColor}" stroke-width="${ann.strokeWidth}" ${dashAttr} stroke-linecap="round" marker-end="url(#arrowhead-${ann.id})" />
    </svg>`;
  } else if (ann.shapeType === 'star') {
    const cx = w / 2, cy = h / 2;
    const outer = Math.min(w, h) / 2 - ann.strokeWidth;
    const inner = outer * 0.45;
    let pts = '';
    for (let i = 0; i < 10; i++) {
      const r = i % 2 === 0 ? outer : inner;
      const a = (i * Math.PI) / 5 - Math.PI / 2;
      pts += `${Math.round(cx + Math.cos(a) * r)},${Math.round(cy + Math.sin(a) * r)} `;
    }
    return `<svg width="100%" height="100%" viewBox="0 0 ${w} ${h}">
      <polygon points="${pts.trim()}" fill="${fillAttr}" stroke="${ann.strokeColor}" stroke-width="${ann.strokeWidth}" ${dashAttr} stroke-linejoin="round" />
    </svg>`;
  } else if (ann.shapeType === 'checkbox') {
    return `<svg width="100%" height="100%" viewBox="0 0 ${w} ${h}">
      <rect x="${ann.strokeWidth / 2}" y="${ann.strokeWidth / 2}" width="${Math.max(1, w - ann.strokeWidth)}" height="${Math.max(1, h - ann.strokeWidth)}" fill="${fillAttr}" stroke="${ann.strokeColor}" stroke-width="${ann.strokeWidth}" rx="3" />
      <polyline points="${w * 0.22},${h * 0.52} ${w * 0.44},${h * 0.76} ${w * 0.78},${h * 0.24}" fill="none" stroke="${ann.strokeColor}" stroke-width="${Math.max(2, ann.strokeWidth)}" stroke-linecap="round" stroke-linejoin="round" />
    </svg>`;
  }
  return '';
}

function attachAnnotationEvents(el, ann) {
  el.addEventListener('pointerdown', (e) => {
    if (typeof finalizeActiveTextEdit === 'function' && activeTextEditContext) {
      finalizeActiveTextEdit();
    }
    // If eraser tool is active, erase on click
    if (activeTool === 'eraser') {
      removeAnnotation(ann.id);
      e.stopPropagation();
      return;
    }

    if (activeTool !== 'select' && activeTool !== 'edit-text' && activeTool !== 'snip-object') return;

    const rect = el.getBoundingClientRect();
    const isResize = (e.clientX >= rect.right - 22) && (e.clientY >= rect.bottom - 22);

    selectAnnotation(ann.id);

    activeDrag = {
      id: ann.id,
      startX: e.clientX,
      startY: e.clientY,
      origX: ann.x,
      origY: ann.y,
      origW: ann.width,
      origH: ann.height,
      isResize
    };

    try { el.setPointerCapture(e.pointerId); } catch (err) {}
    e.stopPropagation();
  });

  el.addEventListener('pointermove', (e) => {
    if (!activeDrag || activeDrag.id !== ann.id) return;
    const stageWidth = stage.clientWidth;
    const stageHeight = stage.clientHeight;
    const deltaX = (e.clientX - activeDrag.startX) / stageWidth;
    const deltaY = (e.clientY - activeDrag.startY) / stageHeight;

    if (activeDrag.isResize) {
      ann.width = Math.max(0.015, activeDrag.origW + deltaX);
      ann.height = Math.max(0.012, activeDrag.origH + deltaY);
      el.style.width = `${Math.round(ann.width * stageWidth)}px`;
      el.style.height = `${Math.round(ann.height * stageHeight)}px`;
    } else {
      ann.x = Math.max(0, Math.min(1 - ann.width, activeDrag.origX + deltaX));
      ann.y = Math.max(0, Math.min(1 - ann.height, activeDrag.origY + deltaY));
      el.style.left = `${Math.round(ann.x * stageWidth)}px`;
      el.style.top = `${Math.round(ann.y * stageHeight)}px`;
    }
  });

  el.addEventListener('pointerup', (e) => {
    if (activeDrag && activeDrag.id === ann.id) {
      try { el.releasePointerCapture(e.pointerId); } catch (err) {}
      recordHistory({
        type: 'modify',
        page: currentPageNumber,
        prev: { x: activeDrag.origX, y: activeDrag.origY, width: activeDrag.origW, height: activeDrag.origH },
        next: { x: ann.x, y: ann.y, width: ann.width, height: ann.height },
        id: ann.id
      });
      activeDrag = null;
      renderOverlayAnnotations();
    }
  });
}

function selectAnnotation(id) {
  selectedAnnotationId = id;
  const annotations = pageAnnotations.get(currentPageNumber) || [];
  const ann = annotations.find((a) => a.id === id);

  document.querySelectorAll('.pe-annotation').forEach((el) => {
    el.classList.toggle('selected', el.dataset.id === id);
  });

  if (ann) {
    optionsSelected.hidden = false;
    const currentOpacity = ann.opacity !== undefined ? Math.round(ann.opacity * 100) : 100;
    selectedOpacityInput.value = currentOpacity;
    selectedOpacityVal.textContent = `${currentOpacity}%`;

    if (selectedLabel) {
      const typeNames = {
        image: isAr ? 'صورة' : 'Image',
        shape: isAr ? 'شكل هندسي' : 'Shape',
        stamp: isAr ? 'ختم' : 'Stamp',
        mark: isAr ? 'علامة' : 'Mark',
        note: isAr ? 'ملاحظة' : 'Note',
        text: isAr ? 'نص' : 'Text',
        redact: isAr ? 'حجب' : 'Redaction'
      };
      selectedLabel.textContent = typeNames[ann.type] || (isAr ? 'عنصر محدد' : 'Selected Item');
    }
  } else {
    optionsSelected.hidden = true;
  }
}

function deselectAll() {
  selectedAnnotationId = null;
  document.querySelectorAll('.pe-annotation').forEach((el) => el.classList.remove('selected'));
  optionsSelected.hidden = true;
}

function addAnnotation(ann) {
  if (!pageAnnotations.has(currentPageNumber)) {
    pageAnnotations.set(currentPageNumber, []);
  }
  const list = pageAnnotations.get(currentPageNumber);
  list.push(ann);

  recordHistory({
    type: 'add',
    page: currentPageNumber,
    ann: JSON.parse(JSON.stringify(ann))
  });

  if (ann.type === 'stroke' || ann.type === 'highlight') {
    renderDrawCanvas();
  } else {
    renderOverlayAnnotations();
    selectAnnotation(ann.id);
  }
}

function removeAnnotation(id) {
  const list = pageAnnotations.get(currentPageNumber) || [];
  const index = list.findIndex((a) => a.id === id);
  if (index !== -1) {
    const [removed] = list.splice(index, 1);
    recordHistory({
      type: 'delete',
      page: currentPageNumber,
      ann: removed
    });
    deselectAll();
    renderDrawCanvas();
    renderOverlayAnnotations();
  }
}

function eraseStrokeAtPoint(pxX, pxY, radius = 22) {
  const annotations = pageAnnotations.get(currentPageNumber) || [];
  const rect = overlay.getBoundingClientRect();
  if (!rect.width || !rect.height) return false;
  const normX = pxX / rect.width;
  const normY = pxY / rect.height;

  for (let i = annotations.length - 1; i >= 0; i--) {
    const ann = annotations[i];
    if (ann.type === 'stroke' || ann.type === 'highlight') {
      const hit = ann.points && ann.points.some((pt) => {
        const dx = (pt.x - normX) * rect.width;
        const dy = (pt.y - normY) * rect.height;
        return (dx * dx + dy * dy) <= (radius * radius);
      });
      if (hit) {
        removeAnnotation(ann.id);
        return true;
      }
    }
  }
  return false;
}

// Selected Element Controls (Opacity, Stacking, Duplicate, Delete)
selectedOpacityInput.addEventListener('input', (e) => {
  if (!selectedAnnotationId) return;
  const list = pageAnnotations.get(currentPageNumber) || [];
  const ann = list.find((a) => a.id === selectedAnnotationId);
  if (ann) {
    ann.opacity = Number(e.target.value) / 100;
    if (selectedOpacityVal) selectedOpacityVal.textContent = `${e.target.value}%`;
    const el = overlay.querySelector(`.pe-annotation[data-id="${selectedAnnotationId}"]`);
    if (el) el.style.opacity = ann.opacity;
    if (ann.type === 'stroke' || ann.type === 'highlight') {
      renderDrawCanvas();
    }
  }
});

selectedOpacityInput.addEventListener('change', () => {
  if (!selectedAnnotationId) return;
  recordHistory({
    type: 'modify',
    page: currentPageNumber,
    id: selectedAnnotationId
  });
});

selectedBringFrontBtn.addEventListener('click', () => {
  if (!selectedAnnotationId) return;
  const list = pageAnnotations.get(currentPageNumber) || [];
  const index = list.findIndex((a) => a.id === selectedAnnotationId);
  if (index !== -1 && index < list.length - 1) {
    const [item] = list.splice(index, 1);
    list.push(item);
    renderOverlayAnnotations();
    selectAnnotation(item.id);
  }
});

selectedSendBackBtn.addEventListener('click', () => {
  if (!selectedAnnotationId) return;
  const list = pageAnnotations.get(currentPageNumber) || [];
  const index = list.findIndex((a) => a.id === selectedAnnotationId);
  if (index > 0) {
    const [item] = list.splice(index, 1);
    list.unshift(item);
    renderOverlayAnnotations();
    selectAnnotation(item.id);
  }
});

selectedDuplicateBtn.addEventListener('click', () => {
  if (!selectedAnnotationId) return;
  const list = pageAnnotations.get(currentPageNumber) || [];
  const ann = list.find((a) => a.id === selectedAnnotationId);
  if (ann) {
    const clone = JSON.parse(JSON.stringify(ann));
    clone.id = `${ann.type}_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
    clone.x = Math.min(0.9, clone.x + 0.03);
    clone.y = Math.min(0.9, clone.y + 0.03);
    addAnnotation(clone);
  }
});

deleteSelectedBtn.addEventListener('click', () => {
  if (selectedAnnotationId) {
    removeAnnotation(selectedAnnotationId);
  }
});

// ==========================================
// History (Undo / Redo) Stack
// ==========================================

function recordHistory(action) {
  undoStack.push(action);
  redoStack = [];
  updateUndoRedoButtons();
}

function undo() {
  if (typeof finalizeActiveTextEdit === 'function' && activeTextEditContext) {
    finalizeActiveTextEdit();
  }
  if (!undoStack.length) return;
  const action = undoStack.pop();
  redoStack.push(action);

  const list = pageAnnotations.get(action.page) || [];

  if (action.type === 'add') {
    const idx = list.findIndex((a) => a.id === action.ann.id);
    if (idx !== -1) list.splice(idx, 1);
  } else if (action.type === 'delete') {
    list.push(action.ann);
  } else if (action.type === 'modify') {
    const item = list.find((a) => a.id === action.id);
    if (item) Object.assign(item, action.prev);
  } else if (action.type === 'snip-move-object') {
    const mIdx = list.findIndex((a) => a.id === action.maskAnn?.id || a.id === action.maskId);
    if (mIdx !== -1) list.splice(mIdx, 1);
    const iIdx = list.findIndex((a) => a.id === action.imageAnn?.id || a.id === action.imageId);
    if (iIdx !== -1) list.splice(iIdx, 1);
    if (selectedAnnotationId === action.imageAnn?.id || selectedAnnotationId === action.imageId) {
      deselectAll();
    }
    renderCurrentPage();
  } else if (action.type === 'text-modify') {
    const pageMods = pageModifiedTexts.get(action.page);
    const block = textLayer.querySelector(`.pe-text-block[data-id="${action.itemId}"]`);
    if (action.prev) {
      if (pageMods) pageMods.set(action.itemId, action.prev);
      if (block) {
        if (action.prev.isDeleted) {
          block.classList.add('is-deleted');
          block.classList.remove('is-modified');
          block.textContent = '';
          block.style.backgroundColor = action.prev.bg || '#ffffff';
        } else {
          block.classList.add('is-modified');
          block.classList.remove('is-deleted');
          block.textContent = action.prev.newStr;
          block.style.color = action.prev.color;
          block.style.backgroundColor = action.prev.bg;
          block.style.fontFamily = action.prev.fontFamily;
          block.style.fontSize = `${Math.round(action.prev.fontSize * currentZoom)}px`;
          block.style.fontWeight = action.prev.isBold ? '700' : '400';
          block.style.fontStyle = action.prev.isItalic ? 'italic' : 'normal';
          block.style.textAlign = action.prev.align || 'right';
        }
      }
    } else {
      if (pageMods) pageMods.delete(action.itemId);
      if (block) {
        block.classList.remove('is-modified', 'is-deleted');
        block.textContent = action.origStr;
        block.style.color = 'transparent';
        block.style.backgroundColor = 'transparent';
        block.style.fontFamily = action.mappedFont;
        block.style.fontWeight = action.isBold ? '700' : '400';
        block.style.fontStyle = action.isItalic ? 'italic' : 'normal';
        block.style.textAlign = action.isCentered ? 'center' : (/[\u0600-\u06FF]/.test(action.origStr) ? 'right' : 'left');
      }
    }
  } else if (action.type === 'text-move') {
    const pageMods = pageModifiedTexts.get(action.page);
    const block = textLayer.querySelector(`.pe-text-block[data-id="${action.itemId}"]`);
    if (action.prev) {
      if (pageMods) pageMods.set(action.itemId, action.prev);
      if (block) {
        block.style.left = `${Math.round(action.prev.x * stage.clientWidth)}px`;
        block.style.top = `${Math.round(action.prev.y * stage.clientHeight)}px`;
      }
    } else {
      if (pageMods) pageMods.delete(action.itemId);
      if (block) {
        block.classList.remove('is-modified');
        block.style.left = `${action.origLeft}px`;
        block.style.top = `${action.origTop}px`;
        block.style.color = 'transparent';
        block.style.backgroundColor = 'transparent';
      }
    }
  }

  if (currentPageNumber !== action.page) {
    currentPageNumber = action.page;
    renderCurrentPage();
  } else {
    renderDrawCanvas();
    renderOverlayAnnotations();
  }
  updateUndoRedoButtons();
}

function redo() {
  if (typeof finalizeActiveTextEdit === 'function' && activeTextEditContext) {
    finalizeActiveTextEdit();
  }
  if (!redoStack.length) return;
  const action = redoStack.pop();
  undoStack.push(action);

  const list = pageAnnotations.get(action.page) || [];

  if (action.type === 'add') {
    list.push(action.ann);
  } else if (action.type === 'delete') {
    const idx = list.findIndex((a) => a.id === action.ann.id);
    if (idx !== -1) list.splice(idx, 1);
  } else if (action.type === 'modify') {
    const item = list.find((a) => a.id === action.id);
    if (item) Object.assign(item, action.next);
  } else if (action.type === 'snip-move-object') {
    if (action.maskAnn) list.push(action.maskAnn);
    if (action.imageAnn) {
      list.push(action.imageAnn);
      selectAnnotation(action.imageAnn.id);
    }
  } else if (action.type === 'text-modify') {
    let pageMods = pageModifiedTexts.get(action.page);
    if (!pageMods) {
      pageMods = new Map();
      pageModifiedTexts.set(action.page, pageMods);
    }
    pageMods.set(action.itemId, action.next);
    const block = textLayer.querySelector(`.pe-text-block[data-id="${action.itemId}"]`);
    if (block) {
      if (action.next.isDeleted) {
        block.classList.add('is-deleted');
        block.classList.remove('is-modified');
        block.textContent = '';
        block.style.backgroundColor = action.next.bg || '#ffffff';
      } else {
        block.classList.add('is-modified');
        block.classList.remove('is-deleted');
        block.textContent = action.next.newStr;
        block.style.color = action.next.color;
        block.style.backgroundColor = action.next.bg;
        block.style.fontFamily = action.next.fontFamily;
        block.style.fontSize = `${Math.round(action.next.fontSize * currentZoom)}px`;
        block.style.fontWeight = action.next.isBold ? '700' : '400';
        block.style.fontStyle = action.next.isItalic ? 'italic' : 'normal';
        block.style.textAlign = action.next.align || 'right';
      }
    }
  } else if (action.type === 'text-move') {
    let pageMods = pageModifiedTexts.get(action.page);
    if (!pageMods) {
      pageMods = new Map();
      pageModifiedTexts.set(action.page, pageMods);
    }
    pageMods.set(action.itemId, action.next);
    const block = textLayer.querySelector(`.pe-text-block[data-id="${action.itemId}"]`);
    if (block) {
      block.classList.add('is-modified');
      block.style.left = `${Math.round(action.next.x * stage.clientWidth)}px`;
      block.style.top = `${Math.round(action.next.y * stage.clientHeight)}px`;
      block.style.color = action.next.color;
      block.style.backgroundColor = action.next.bg;
    }
  }

  if (currentPageNumber !== action.page) {
    currentPageNumber = action.page;
    renderCurrentPage();
  } else {
    renderDrawCanvas();
    renderOverlayAnnotations();
  }
  updateUndoRedoButtons();
}

function updateUndoRedoButtons() {
  undoBtn.disabled = undoStack.length === 0;
  redoBtn.disabled = redoStack.length === 0;
}

undoBtn.addEventListener('click', undo);
redoBtn.addEventListener('click', redo);

// Global Keyboard Shortcuts (Delete, Undo, Redo, Duplicate, Escape, Nudge)
window.addEventListener('keydown', (e) => {
  const activeEl = document.activeElement;
  const isInputActive = activeEl && (
    activeEl.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(activeEl.tagName)
  );

  const isCtrl = e.ctrlKey || e.metaKey;
  // KeyZ and KeyY match standard Windows/Mac physical keys, regardless of active keyboard language layout (Arabic, English, French, etc.)
  // Also explicitly support Arabic characters mapped to Z (ئ) and Y (إ)
  const isZ = e.code === 'KeyZ' || e.key === 'z' || e.key === 'Z' || e.key === 'ئ';
  const isY = e.code === 'KeyY' || e.key === 'y' || e.key === 'Y' || e.key === 'إ';

  if (isCtrl) {
    if (isZ && !e.shiftKey) {
      e.preventDefault();
      if (activeEl?.classList?.contains('pe-text-block')) {
        activeEl.blur();
      }
      undo();
      return;
    }
    if ((isZ && e.shiftKey) || isY) {
      e.preventDefault();
      if (activeEl?.classList?.contains('pe-text-block')) {
        activeEl.blur();
      }
      redo();
      return;
    }
    if ((e.code === 'KeyD' || e.key === 'd' || e.key === 'D' || e.key === 'ي') && selectedAnnotationId && !isInputActive) {
      e.preventDefault();
      selectedDuplicateBtn?.click();
      return;
    }
  }

  // Delete selected annotation (Delete / Backspace)
  if ((e.key === 'Delete' || e.key === 'Backspace') && !isInputActive) {
    if (selectedAnnotationId) {
      e.preventDefault();
      removeAnnotation(selectedAnnotationId);
    }
    return;
  }

  // Escape to deselect or revert to select tool
  if (e.key === 'Escape') {
    if (selectedAnnotationId) {
      deselectAll();
    } else if (activeTool !== 'select') {
      setTool('select');
    }
    return;
  }

  // Arrow keys to nudge selected annotation
  if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && !isInputActive && selectedAnnotationId) {
    e.preventDefault();
    const annotations = pageAnnotations.get(currentPageNumber) || [];
    const ann = annotations.find((a) => a.id === selectedAnnotationId);
    if (!ann) return;
    const step = (e.shiftKey ? 8 : 2) / Math.max(stage.clientWidth, stage.clientHeight);
    if (e.key === 'ArrowUp') ann.y = Math.max(0, ann.y - step);
    if (e.key === 'ArrowDown') ann.y = Math.min(1 - ann.height, ann.y + step);
    if (e.key === 'ArrowLeft') ann.x = Math.max(0, ann.x - step);
    if (e.key === 'ArrowRight') ann.x = Math.min(1 - ann.width, ann.x + step);
    renderOverlayAnnotations();
  }
});

// ==========================================
// Tool Switching & Context Bars
// ==========================================

function setTool(toolName) {
  activeTool = toolName;
  document.querySelectorAll('.pe-tool-btn[data-tool]').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.tool === toolName);
  });

  const studioApp = byId('pdf-editor-app');
  if (studioApp) studioApp.dataset.activeTool = toolName;

  // Auto-switch ribbon tab to match active tool
  if (['edit-text', 'select', 'snip-object'].includes(toolName)) {
    switchRibbonTab('home');
  } else if (['text', 'signature', 'note', 'stamp', 'mark', 'shape'].includes(toolName)) {
    switchRibbonTab('insert');
  } else if (['draw', 'highlight', 'eraser', 'redact'].includes(toolName)) {
    switchRibbonTab('draw');
  }

  if (toolName === 'signature') openSignatureDialog();
  if (toolName !== 'select') deselectAll();
}

document.querySelectorAll('.pe-tool-btn[data-tool]').forEach((btn) => {
  btn.addEventListener('click', () => setTool(btn.dataset.tool));
});

// Options Listeners
fontFamilySelect.addEventListener('change', async (e) => {
  const val = e.target.value;
  if (val === 'custom') {
    const fontName = await showPrompt(
      isAr
        ? 'اكتب اسم أي خط مثبت على جهازك في نظام ويندوز (مثال: Lotus أو Traditional Arabic أو Dubai أو Diwani):'
        : 'Type the name of any installed Windows font:',
      'Traditional Arabic',
      {
        title: isAr ? 'خط مخصص' : 'Custom Font',
        placeholder: 'e.g. Lotus, Traditional Arabic'
      }
    );
    if (fontName && fontName.trim()) {
      applyCustomFont(fontName.trim());
    } else {
      fontFamilySelect.value = textSettings.fontFamily || "'Traditional Arabic', 'Amiri', serif";
    }
    return;
  }
  if (val === 'local-query') {
    fontFamilySelect.value = textSettings.fontFamily || "'Traditional Arabic', 'Amiri', serif";
    loadLocalWindowsFonts();
    return;
  }
  loadWebFont(val);
  textSettings.fontFamily = val;
});
fontSizeSelect.addEventListener('change', (e) => { textSettings.fontSize = Number(e.target.value); });
textColorInput.addEventListener('input', (e) => { textSettings.color = e.target.value; });
textBoldBtn.addEventListener('click', () => {
  textSettings.bold = !textSettings.bold;
  textBoldBtn.classList.toggle('active', textSettings.bold);
});
textItalicBtn.addEventListener('click', () => {
  textSettings.italic = !textSettings.italic;
  textItalicBtn.classList.toggle('active', textSettings.italic);
});

// Draw Options Listeners
drawColorInput.addEventListener('input', (e) => { drawSettings.color = e.target.value; });
drawSizeBtns.forEach((btn) => {
  btn.addEventListener('click', () => {
    drawSizeBtns.forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
    drawSettings.size = Number(btn.dataset.size);
  });
});

// Highlight Options Listeners
highlightThicknessSelect.addEventListener('change', (e) => {
  highlightSettings.thickness = Number(e.target.value);
});
highlightColorChips.forEach((chip) => {
  chip.addEventListener('click', () => {
    highlightColorChips.forEach((c) => c.classList.remove('active'));
    chip.classList.add('active');
    highlightSettings.color = chip.dataset.color;
  });
});

// Shape Options Listeners
shapeTypeSelect.addEventListener('change', (e) => { shapeSettings.type = e.target.value; });
shapeStrokeInput.addEventListener('input', (e) => { shapeSettings.strokeColor = e.target.value; });
shapeWidthSelect.addEventListener('change', (e) => { shapeSettings.strokeWidth = Number(e.target.value); });
shapeFillSelect.addEventListener('change', (e) => { shapeSettings.fill = e.target.value; });
if (shapeDashSelect) {
  shapeDashSelect.addEventListener('change', (e) => { shapeSettings.dash = e.target.value; });
}

// Redact Options Listeners
redactBtns.forEach((btn) => {
  btn.addEventListener('click', () => {
    redactBtns.forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
    redactColor = btn.dataset.color;
  });
});

// Stamp Options Listeners
stampChips.forEach((chip) => {
  chip.addEventListener('click', () => {
    const text = chip.dataset.stamp;
    const color = chip.dataset.color || '#059669';
    insertStamp(text, color);
  });
});

if (insertCustomStampBtn) {
  insertCustomStampBtn.addEventListener('click', () => {
    const text = customStampInput?.value?.trim();
    if (!text) return;
    const color = customStampColor?.value || '#dc2626';
    insertStamp(text, color);
    customStampInput.value = '';
  });
}

if (customStampInput) {
  customStampInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      insertCustomStampBtn?.click();
    }
  });
}

function insertStamp(text, color) {
  const newStamp = {
    id: `stamp_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
    type: 'stamp',
    text,
    color,
    x: 0.35,
    y: 0.35,
    width: 0.28,
    height: 0.09,
    opacity: 0.88
  };
  addAnnotation(newStamp);
  setTool('select');
}

// Form Mark Options Listeners
markChips.forEach((chip) => {
  chip.addEventListener('click', () => {
    markChips.forEach((c) => c.classList.remove('active'));
    chip.classList.add('active');
    activeMarkSymbol = chip.dataset.mark;
  });
});
if (markColorInput) {
  markColorInput.addEventListener('input', (e) => { activeMarkColor = e.target.value; });
}
if (markSizeSelect) {
  markSizeSelect.addEventListener('change', (e) => { activeMarkSize = Number(e.target.value); });
}

// Sticky Note Options Listeners
noteColorChips.forEach((chip) => {
  chip.addEventListener('click', () => {
    noteColorChips.forEach((c) => c.classList.remove('active'));
    chip.classList.add('active');
    activeNoteColor = chip.dataset.noteColor;
  });
});
if (addNoteBtn) {
  addNoteBtn.addEventListener('click', () => {
    const newNote = {
      id: `note_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      type: 'note',
      text: isAr ? 'اكتب ملاحظتك هنا...' : 'Type note here...',
      color: activeNoteColor,
      x: 0.38,
      y: 0.32,
      width: 0.24,
      height: 0.16,
      opacity: 0.95
    };
    addAnnotation(newNote);
    setTool('select');
  });
}

// Stage background click handler - cleanly commits active text edit
if (stage) {
  stage.addEventListener('pointerdown', (e) => {
    if (e.target.closest('.pe-text-block') || e.target.closest('.pe-annotation') || e.target.closest('.pe-detected-image')) {
      return;
    }
    if (typeof finalizeActiveTextEdit === 'function' && activeTextEditContext) {
      finalizeActiveTextEdit();
    }
  });
}

// Pointer Events on Interactive Overlay
overlay.addEventListener('pointerdown', (e) => {
  if (typeof finalizeActiveTextEdit === 'function' && activeTextEditContext) {
    finalizeActiveTextEdit();
  }
  if (e.target.closest('.pe-annotation')) return;

  const rect = overlay.getBoundingClientRect();
  startX = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
  startY = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
  isInteracting = true;

  if (activeTool === 'eraser') {
    eraseStrokeAtPoint(e.clientX - rect.left, e.clientY - rect.top);
    overlay.setPointerCapture(e.pointerId);
    return;
  }

  if (activeTool === 'mark') {
    // Click to insert mark immediately
    const stageWidth = stage.clientWidth;
    const stageHeight = stage.clientHeight;
    const wNorm = activeMarkSize / stageWidth;
    const hNorm = activeMarkSize / stageHeight;

    const newMark = {
      id: `mark_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      type: 'mark',
      mark: activeMarkSymbol,
      color: activeMarkColor,
      x: Math.max(0, startX - wNorm / 2),
      y: Math.max(0, startY - hNorm / 2),
      width: wNorm,
      height: hNorm,
      opacity: 1.0
    };
    addAnnotation(newMark);
  } else if (activeTool === 'text') {
    const newText = {
      id: `text_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      type: 'text',
      x: startX,
      y: startY,
      width: 0.24,
      height: 0.06,
      text: isAr ? 'نص جديد' : 'New text',
      fontFamily: textSettings.fontFamily,
      fontSize: textSettings.fontSize,
      color: textSettings.color,
      bold: textSettings.bold,
      italic: textSettings.italic,
      opacity: 1.0
    };
    addAnnotation(newText);
    setTool('select');
  } else if (activeTool === 'draw' || activeTool === 'highlight') {
    currentStroke = {
      id: `stroke_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      type: activeTool,
      points: [{ x: startX, y: startY }],
      size: activeTool === 'draw' ? drawSettings.size : highlightSettings.thickness,
      color: activeTool === 'draw' ? drawSettings.color : highlightSettings.color,
      opacity: activeTool === 'highlight' ? 0.65 : 1.0
    };
    overlay.setPointerCapture(e.pointerId);
  } else if (activeTool === 'shape' || activeTool === 'redact' || activeTool === 'snip-object' || activeTool === 'select') {
    overlay.setPointerCapture(e.pointerId);
  }
});

overlay.addEventListener('pointermove', (e) => {
  if (!isInteracting) return;
  const rect = overlay.getBoundingClientRect();
  const currX = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
  const currY = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

  if (activeTool === 'eraser') {
    eraseStrokeAtPoint(e.clientX - rect.left, e.clientY - rect.top);
    return;
  }

  if (activeTool === 'snip-object' || activeTool === 'select') {
    const pxW = Math.abs(currX - startX) * rect.width;
    const pxH = Math.abs(currY - startY) * rect.height;
    if (pxW > 4 || pxH > 4) {
      renderDrawCanvas();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const ctx = drawCanvas.getContext('2d');
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      const pxX = Math.min(startX, currX) * rect.width;
      const pxY = Math.min(startY, currY) * rect.height;

      ctx.fillStyle = 'rgba(37, 99, 235, 0.12)';
      ctx.strokeStyle = '#2563eb';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 4]);
      ctx.fillRect(pxX, pxY, Math.max(2, pxW), Math.max(2, pxH));
      ctx.strokeRect(pxX, pxY, Math.max(2, pxW), Math.max(2, pxH));
      ctx.restore();
    }
    return;
  }

  if (activeTool === 'draw' || activeTool === 'highlight') {
    if (currentStroke) {
      currentStroke.points.push({ x: currX, y: currY });
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const ctx = drawCanvas.getContext('2d');
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      const pts = currentStroke.points;
      const p1 = pts[pts.length - 2];
      const p2 = pts[pts.length - 1];
      ctx.beginPath();
      ctx.moveTo(p1.x * rect.width, p1.y * rect.height);
      ctx.lineTo(p2.x * rect.width, p2.y * rect.height);
      ctx.strokeStyle = currentStroke.color;
      ctx.lineWidth = currentStroke.size;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      if (activeTool === 'highlight') ctx.globalCompositeOperation = 'multiply';
      ctx.stroke();
      ctx.restore();
    }
  } else if (activeTool === 'shape' || activeTool === 'redact') {
    renderDrawCanvas();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const ctx = drawCanvas.getContext('2d');
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    const pxX = Math.min(startX, currX) * rect.width;
    const pxY = Math.min(startY, currY) * rect.height;
    const pxW = Math.max(2, Math.abs(currX - startX) * rect.width);
    const pxH = Math.max(2, Math.abs(currY - startY) * rect.height);

    if (activeTool === 'redact') {
      ctx.fillStyle = redactColor === '#ffffff' ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)';
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 4]);
      ctx.fillRect(pxX, pxY, pxW, pxH);
      ctx.strokeRect(pxX, pxY, pxW, pxH);
    } else if (activeTool === 'shape') {
      ctx.strokeStyle = shapeSettings.strokeColor;
      ctx.lineWidth = shapeSettings.strokeWidth;
      ctx.setLineDash([4, 3]);
      if (shapeSettings.fill === 'semi') ctx.fillStyle = `${shapeSettings.strokeColor}40`;
      else if (shapeSettings.fill === 'solid') ctx.fillStyle = shapeSettings.strokeColor;

      if (shapeSettings.type === 'rect') {
        if (shapeSettings.fill !== 'transparent') ctx.fillRect(pxX, pxY, pxW, pxH);
        ctx.strokeRect(pxX, pxY, pxW, pxH);
      } else if (shapeSettings.type === 'circle') {
        ctx.beginPath();
        ctx.ellipse(pxX + pxW / 2, pxY + pxH / 2, Math.max(1, pxW / 2), Math.max(1, pxH / 2), 0, 0, Math.PI * 2);
        if (shapeSettings.fill !== 'transparent') ctx.fill();
        ctx.stroke();
      } else if (shapeSettings.type === 'line' || shapeSettings.type === 'arrow') {
        ctx.beginPath();
        ctx.moveTo(startX * rect.width, startY * rect.height);
        ctx.lineTo(currX * rect.width, currY * rect.height);
        ctx.stroke();
      }
    }
    ctx.restore();
  }
});

overlay.addEventListener('pointerup', (e) => {
  if (!isInteracting) return;
  isInteracting = false;
  const rect = overlay.getBoundingClientRect();
  const endX = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
  const endY = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

  if (activeTool === 'eraser') {
    overlay.releasePointerCapture(e.pointerId);
    return;
  }

  if (activeTool === 'snip-object' || activeTool === 'select') {
    renderDrawCanvas();
    overlay.releasePointerCapture(e.pointerId);

    const x = Math.min(startX, endX);
    const y = Math.min(startY, endY);
    const width = Math.abs(endX - startX);
    const height = Math.abs(endY - startY);

    if (width * rect.width > 12 && height * rect.height > 12) {
      const pdfCanvas = byId('pe-pdf-canvas');
      if (pdfCanvas) {
        const sliceCanvas = document.createElement('canvas');
        const srcX = Math.round(x * pdfCanvas.width);
        const srcY = Math.round(y * pdfCanvas.height);
        const srcW = Math.round(width * pdfCanvas.width);
        const srcH = Math.round(height * pdfCanvas.height);

        const sampleBg = sampleCanvasBackgroundColor(
          Math.round(x * rect.width),
          Math.round(y * rect.height),
          Math.round(width * rect.width),
          Math.round(height * rect.height)
        );

        sliceCanvas.width = srcW;
        sliceCanvas.height = srcH;
        const sCtx = sliceCanvas.getContext('2d');
        sCtx.drawImage(pdfCanvas, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);
        makeCanvasBackgroundTransparent(sliceCanvas, sampleBg || '#ffffff');
        const dataUrl = sliceCanvas.toDataURL('image/png');

        const maskRedact = {
          id: `redact_snip_mask_${Date.now()}`,
          type: 'redact',
          x,
          y,
          width,
          height,
          color: sampleBg || '#ffffff',
          opacity: 1.0
        };

        if (!pageAnnotations.has(currentPageNumber)) {
          pageAnnotations.set(currentPageNumber, []);
        }
        pageAnnotations.get(currentPageNumber).push(maskRedact);

        const newImageAnn = {
          id: `image_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
          type: 'image',
          src: dataUrl,
          x,
          y,
          width,
          height,
          opacity: 1.0,
          isFromSlice: true
        };
        pageAnnotations.get(currentPageNumber).push(newImageAnn);

        renderOverlayAnnotations();
        selectAnnotation(newImageAnn.id);
        setTool('select');

        recordHistory({
          type: 'snip-move-object',
          page: currentPageNumber,
          maskAnn: { ...maskRedact },
          imageAnn: { ...newImageAnn }
        });
      }
    } else if (activeTool === 'select') {
      deselectAll();
    }
    return;
  }

  if (activeTool === 'draw' || activeTool === 'highlight') {
    if (currentStroke && currentStroke.points.length > 1) {
      addAnnotation(currentStroke);
    }
    currentStroke = null;
    overlay.releasePointerCapture(e.pointerId);
  } else if (activeTool === 'shape') {
    renderDrawCanvas();
    const x = Math.min(startX, endX);
    const y = Math.min(startY, endY);
    const width = Math.max(0.02, Math.abs(endX - startX));
    const height = Math.max(0.02, Math.abs(endY - startY));

    const newShape = {
      id: `shape_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      type: 'shape',
      shapeType: shapeSettings.type,
      x,
      y,
      width,
      height,
      strokeColor: shapeSettings.strokeColor,
      strokeWidth: shapeSettings.strokeWidth,
      fill: shapeSettings.fill,
      dash: shapeSettings.dash || 'solid',
      opacity: 1.0
    };
    addAnnotation(newShape);
    overlay.releasePointerCapture(e.pointerId);
    setTool('select');
  } else if (activeTool === 'redact') {
    renderDrawCanvas();
    const x = Math.min(startX, endX);
    const y = Math.min(startY, endY);
    const width = Math.max(0.02, Math.abs(endX - startX));
    const height = Math.max(0.015, Math.abs(endY - startY));

    const newRedact = {
      id: `redact_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      type: 'redact',
      x,
      y,
      width,
      height,
      color: redactColor,
      opacity: 1.0
    };
    addAnnotation(newRedact);
    overlay.releasePointerCapture(e.pointerId);
    setTool('select');
  }
});

// Image Upload
imageUploadInput.addEventListener('change', (e) => {
  if (!e.target.files || !e.target.files[0]) return;
  const file = e.target.files[0];
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const aspect = img.width / img.height;
      const initialWidth = 0.32;
      const initialHeight = initialWidth / aspect;

      const newImageAnn = {
        id: `img_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
        type: 'image',
        x: 0.34,
        y: 0.34,
        width: initialWidth,
        height: initialHeight,
        src: reader.result,
        opacity: 1.0
      };
      addAnnotation(newImageAnn);
      setTool('select');
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
  imageUploadInput.value = '';
});

// ==========================================
// Signature Pad & Modal
// ==========================================

function openSignatureDialog() {
  sigDialog.showModal();
  clearSignaturePad();
}

sigCloseBtn.addEventListener('click', () => {
  sigDialog.close();
  setTool('edit-text');
});

sigTabBtns.forEach((btn) => {
  btn.addEventListener('click', () => {
    sigTabBtns.forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
    sigTab = btn.dataset.tab;
    sigTabDraw.hidden = sigTab !== 'draw';
    sigTabType.hidden = sigTab !== 'type';
  });
});

sigColorChips.forEach((chip) => {
  chip.addEventListener('click', () => {
    sigColorChips.forEach((c) => c.classList.remove('active'));
    chip.classList.add('active');
    sigColor = chip.dataset.sigColor;
  });
});

function clearSignaturePad() {
  const ctx = sigCanvas.getContext('2d');
  ctx.clearRect(0, 0, sigCanvas.width, sigCanvas.height);
}

sigClearBtn.addEventListener('click', clearSignaturePad);

sigCanvas.addEventListener('pointerdown', (e) => {
  isSigDrawing = true;
  const rect = sigCanvas.getBoundingClientRect();
  sigLastX = e.clientX - rect.left;
  sigLastY = e.clientY - rect.top;
  sigCanvas.setPointerCapture(e.pointerId);
});

sigCanvas.addEventListener('pointermove', (e) => {
  if (!isSigDrawing) return;
  const rect = sigCanvas.getBoundingClientRect();
  const currX = e.clientX - rect.left;
  const currY = e.clientY - rect.top;

  const ctx = sigCanvas.getContext('2d');
  ctx.beginPath();
  ctx.moveTo(sigLastX, sigLastY);
  ctx.lineTo(currX, currY);
  ctx.strokeStyle = sigColor;
  ctx.lineWidth = 2.5;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke();

  sigLastX = currX;
  sigLastY = currY;
});

sigCanvas.addEventListener('pointerup', (e) => {
  if (isSigDrawing) {
    isSigDrawing = false;
    sigCanvas.releasePointerCapture(e.pointerId);
  }
});

sigTypeInput.addEventListener('input', (e) => {
  sigTypePreview.textContent = e.target.value.trim() || (isAr ? 'التوقيع سيظهر هنا' : 'Signature preview will appear here');
});

sigInsertBtn.addEventListener('click', () => {
  let sigDataUrl = null;
  if (sigTab === 'draw') {
    sigDataUrl = sigCanvas.toDataURL('image/png');
  } else {
    const text = sigTypeInput.value.trim();
    if (!text) {
      showAlert(isAr ? 'يرجى كتابة اسمك أولاً.' : 'Please enter your name.', { type: 'warning' });
      return;
    }
    const offCanvas = document.createElement('canvas');
    offCanvas.width = 460;
    offCanvas.height = 180;
    const ctx = offCanvas.getContext('2d');
    ctx.font = '54px "Amiri", cursive, serif';
    ctx.fillStyle = sigColor;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, offCanvas.width / 2, offCanvas.height / 2);
    sigDataUrl = offCanvas.toDataURL('image/png');
  }

  const newSig = {
    id: `sig_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
    type: 'image',
    x: 0.38,
    y: 0.72,
    width: 0.24,
    height: 0.09,
    src: sigDataUrl,
    opacity: 1.0
  };
  addAnnotation(newSig);

  if (sigSaveStorageCheckbox?.checked && sigDataUrl) {
    try {
      localStorage.setItem('kz_pdf_saved_sig', sigDataUrl);
      if (quickSigBtn) quickSigBtn.style.display = 'inline-flex';
    } catch (e) {
      console.warn('Could not save signature to localStorage', e);
    }
  }

  sigDialog.close();
  setTool('select');
});

// Quick Saved Signature Button
if (quickSigBtn) {
  try {
    if (localStorage.getItem('kz_pdf_saved_sig')) {
      quickSigBtn.style.display = 'inline-flex';
    }
  } catch (e) {}

  quickSigBtn.addEventListener('click', () => {
    let saved = null;
    try {
      saved = localStorage.getItem('kz_pdf_saved_sig');
    } catch (e) {}

    if (!saved) {
      showAlert(isAr ? 'لا يوجد توقيع محفوظ حالياً. افتح نافذة التوقيع وحدد خيار الحفظ.' : 'No saved signature found. Create one and check save option.', { type: 'info' });
      return;
    }

    const newQuickSig = {
      id: `sig_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      type: 'image',
      x: 0.38,
      y: 0.72,
      width: 0.24,
      height: 0.09,
      src: saved,
      opacity: 1.0
    };
    addAnnotation(newQuickSig);
    setTool('select');
  });
}

// ==========================================
// Zoom & Fullscreen Controls
// ==========================================

function updateZoomDisplay() {
  zoomValueLabel.textContent = `${Math.round(currentZoom * 100)}%`;
}

async function setZoom(val) {
  currentZoom = Math.max(0.3, Math.min(3.0, val));
  updateZoomDisplay();
  await renderCurrentPage();
}

async function fitWidth() {
  if (!currentPdf) return;
  const page = await currentPdf.getPage(currentPageNumber);
  const unscaledViewport = page.getViewport({ scale: 1.0 });
  const comp = window.getComputedStyle(viewportContainer);
  const padLeft = parseFloat(comp.paddingLeft) || 28;
  const padRight = parseFloat(comp.paddingRight) || 28;
  const padTop = parseFloat(comp.paddingTop) || 32;
  const padBottom = parseFloat(comp.paddingBottom) || 64;

  let containerInnerWidth = Math.max(260, viewportContainer.clientWidth - padLeft - padRight);
  const containerInnerHeight = Math.max(260, viewportContainer.clientHeight - padTop - padBottom);

  const hasVerticalScrollbar = viewportContainer.scrollHeight > viewportContainer.clientHeight;
  const approxHeightAtFit = unscaledViewport.height * (containerInnerWidth / unscaledViewport.width);

  if (!hasVerticalScrollbar && approxHeightAtFit > containerInnerHeight) {
    containerInnerWidth = Math.max(260, containerInnerWidth - 16);
  }

  const availableWidth = Math.max(240, containerInnerWidth - 12);
  currentZoom = Math.max(0.3, Math.min(2.5, availableWidth / unscaledViewport.width));
  updateZoomDisplay();
  await renderCurrentPage();
}

async function fitPage() {
  if (!currentPdf) return;
  const page = await currentPdf.getPage(currentPageNumber);
  const unscaledViewport = page.getViewport({ scale: 1.0 });
  const comp = window.getComputedStyle(viewportContainer);
  const padLeft = parseFloat(comp.paddingLeft) || 28;
  const padRight = parseFloat(comp.paddingRight) || 28;
  const padTop = parseFloat(comp.paddingTop) || 32;
  const padBottom = parseFloat(comp.paddingBottom) || 64;

  const scrollbarY = Math.max(0, viewportContainer.offsetWidth - viewportContainer.clientWidth);
  const scrollbarX = Math.max(0, viewportContainer.offsetHeight - viewportContainer.clientHeight);

  const containerInnerWidth = Math.max(260, (viewportContainer.clientWidth + scrollbarY) - padLeft - padRight);
  const containerInnerHeight = Math.max(260, (viewportContainer.clientHeight + scrollbarX) - padTop - padBottom);

  const availableWidth = Math.max(240, containerInnerWidth - 16);
  const availableHeight = Math.max(240, containerInnerHeight - 16);

  const scaleH = availableHeight / unscaledViewport.height;
  const scaleW = availableWidth / unscaledViewport.width;
  currentZoom = Math.max(0.3, Math.min(2.5, Math.min(scaleH, scaleW)));
  updateZoomDisplay();
  await renderCurrentPage();
}

fitWidthBtn.addEventListener('click', fitWidth);
fitPageBtn.addEventListener('click', fitPage);
zoomOutBtn.addEventListener('click', () => setZoom(currentZoom - 0.15));
zoomInBtn.addEventListener('click', () => setZoom(currentZoom + 0.15));

fullscreenBtn.addEventListener('click', async () => {
  const studioLayout = document.querySelector('.pe-studio-layout');
  if (studioLayout) {
    studioLayout.style.removeProperty('height');
  }

  const isCurrentlyFullscreen = !!document.fullscreenElement || workspace.classList.contains('is-fullscreen');
  if (!isCurrentlyFullscreen) {
    workspace.classList.add('is-fullscreen');
    try {
      if (document.fullscreenEnabled && workspace.requestFullscreen) {
        await workspace.requestFullscreen();
      }
    } catch (_) {}
  } else {
    workspace.classList.remove('is-fullscreen');
    try {
      if (document.fullscreenElement && document.exitFullscreen) {
        await document.exitFullscreen();
      }
    } catch (_) {}
  }
  setTimeout(fitPage, 160);
});

document.addEventListener('fullscreenchange', () => {
  const studioLayout = document.querySelector('.pe-studio-layout');
  if (studioLayout) {
    studioLayout.style.removeProperty('height');
  }
  if (!document.fullscreenElement) {
    workspace.classList.remove('is-fullscreen');
  } else {
    workspace.classList.add('is-fullscreen');
  }
  setTimeout(fitPage, 160);
});

// ==========================================
// Page Navigation & Thumbnails
// ==========================================

function updatePageNavControls() {
  currentPageInput.value = currentPageNumber;
  prevBtn.disabled = currentPageNumber <= 1;
  nextBtn.disabled = currentPageNumber >= totalPages;
}

async function goToPage(pageNum) {
  const target = Math.max(1, Math.min(totalPages, pageNum));
  if (target === currentPageNumber) return;
  currentPageNumber = target;
  deselectAll();
  currentActiveTextId = null;
  await renderCurrentPage();
}

prevBtn.addEventListener('click', () => goToPage(currentPageNumber - 1));
nextBtn.addEventListener('click', () => goToPage(currentPageNumber + 1));
currentPageInput.addEventListener('change', (e) => goToPage(Number(e.target.value)));

sidebarCloseBtn.addEventListener('click', () => sidebar.classList.add('is-collapsed'));
toggleSidebarBtn.addEventListener('click', () => sidebar.classList.toggle('is-collapsed'));

// ==========================================
// Page Operations (Rotate, Duplicate, Add, Delete)
// ==========================================

function shiftPageDataAfter(fromPage, delta) {
  const shiftMap = (map) => {
    const entries = Array.from(map.entries()).sort((a, b) => (delta > 0 ? b[0] - a[0] : a[0] - b[0]));
    for (const [p, val] of entries) {
      if (p > fromPage) {
        map.delete(p);
        map.set(p + delta, val);
      }
    }
  };
  shiftMap(pageAnnotations);
  shiftMap(pageModifiedTexts);
  shiftMap(pageDocData);
}

function updatePageNumbersUI() {
  if (sidebarTotal) sidebarTotal.textContent = totalPages;
  if (totalPagesDisplay) totalPagesDisplay.textContent = totalPages;
  if (currentPageInput) {
    currentPageInput.max = totalPages;
    currentPageInput.value = currentPageNumber;
  }
  updatePageNavControls();
}

async function rotateCurrentPage(clockwise = true) {
  if (!originalBytes || !currentPdf) return;
  try {
    const pdfDoc = await PDFDocument.load(originalBytes, { ignoreEncryption: true });
    const page = pdfDoc.getPage(currentPageNumber - 1);
    const currentAngle = page.getRotation().angle;
    const delta = clockwise ? 90 : -90;
    const newAngle = (currentAngle + delta + 360) % 360;
    page.setRotation(degrees(newAngle));

    originalBytes = new Uint8Array(await pdfDoc.save());
    pageDocData.delete(currentPageNumber);

    currentPdf = await pdfjs.getDocument(getPdfDocumentParams(originalBytes)).promise;
    await renderThumbnails();
    await renderCurrentPage();
  } catch (err) {
    console.error('Rotate page error:', err);
    showAlert(isAr ? 'تعذر تدوير الصفحة.' : 'Failed to rotate page.', { type: 'error' });
  }
}

async function duplicateCurrentPage() {
  if (!originalBytes || !currentPdf) return;
  try {
    const pdfDoc = await PDFDocument.load(originalBytes, { ignoreEncryption: true });
    const [copied] = await pdfDoc.copyPages(pdfDoc, [currentPageNumber - 1]);
    pdfDoc.insertPage(currentPageNumber, copied);

    originalBytes = new Uint8Array(await pdfDoc.save());
    currentPdf = await pdfjs.getDocument(getPdfDocumentParams(originalBytes)).promise;
    totalPages = currentPdf.numPages;

    shiftPageDataAfter(currentPageNumber, 1);

    const curAnns = pageAnnotations.get(currentPageNumber);
    if (curAnns && curAnns.length) {
      pageAnnotations.set(currentPageNumber + 1, JSON.parse(JSON.stringify(curAnns)));
    }

    updatePageNumbersUI();
    await renderThumbnails();
    await goToPage(currentPageNumber + 1);
  } catch (err) {
    console.error('Duplicate page error:', err);
    showAlert(isAr ? 'تعذر نسخ الصفحة.' : 'Failed to duplicate page.', { type: 'error' });
  }
}

async function addBlankPage() {
  if (!originalBytes || !currentPdf) return;
  try {
    const pdfDoc = await PDFDocument.load(originalBytes, { ignoreEncryption: true });
    const curPage = pdfDoc.getPage(currentPageNumber - 1);
    const w = curPage.getWidth();
    const h = curPage.getHeight();
    pdfDoc.insertPage(currentPageNumber, [w, h]);

    originalBytes = new Uint8Array(await pdfDoc.save());
    currentPdf = await pdfjs.getDocument(getPdfDocumentParams(originalBytes)).promise;
    totalPages = currentPdf.numPages;

    shiftPageDataAfter(currentPageNumber, 1);

    updatePageNumbersUI();
    await renderThumbnails();
    await goToPage(currentPageNumber + 1);
  } catch (err) {
    console.error('Add blank page error:', err);
    showAlert(isAr ? 'تعذر إضافة صفحة جديدة.' : 'Failed to add blank page.', { type: 'error' });
  }
}

async function deleteCurrentPage() {
  if (!originalBytes || !currentPdf) return;
  if (totalPages <= 1) {
    showAlert(isAr ? 'لا يمكن حذف الصفحة الوحيدة في المستند.' : 'Cannot delete the only page in the document.', { type: 'warning' });
    return;
  }

  const confirmed = await showConfirm(
    isAr ? `هل أنت متأكد من حذف الصفحة رقم ${currentPageNumber}؟` : `Are you sure you want to delete page ${currentPageNumber}?`,
    { type: 'danger' }
  );
  if (!confirmed) return;

  try {
    const pdfDoc = await PDFDocument.load(originalBytes, { ignoreEncryption: true });
    pdfDoc.removePage(currentPageNumber - 1);

    originalBytes = new Uint8Array(await pdfDoc.save());
    currentPdf = await pdfjs.getDocument(getPdfDocumentParams(originalBytes)).promise;

    pageAnnotations.delete(currentPageNumber);
    pageModifiedTexts.delete(currentPageNumber);
    pageDocData.delete(currentPageNumber);
    shiftPageDataAfter(currentPageNumber, -1);

    totalPages = currentPdf.numPages;
    currentPageNumber = Math.min(currentPageNumber, totalPages);

    updatePageNumbersUI();
    await renderThumbnails();
    await renderCurrentPage();
  } catch (err) {
    console.error('Delete page error:', err);
    showAlert(isAr ? 'تعذر حذف الصفحة.' : 'Failed to delete page.', { type: 'error' });
  }
}

if (rotatePageBtn) rotatePageBtn.addEventListener('click', () => rotateCurrentPage(true));
if (duplicatePageBtn) duplicatePageBtn.addEventListener('click', duplicateCurrentPage);
if (addPageBtn) addPageBtn.addEventListener('click', addBlankPage);
if (deletePageBtn) deletePageBtn.addEventListener('click', deleteCurrentPage);

async function renderThumbnails() {
  thumbnailsList.replaceChildren();
  if (!currentPdf) return;

  for (let i = 1; i <= totalPages; i++) {
    const item = document.createElement('div');
    item.className = 'pe-thumbnail-item';
    item.dataset.page = i;
    if (i === currentPageNumber) item.classList.add('active');

    const thumbCanvas = document.createElement('canvas');
    thumbCanvas.className = 'pe-thumbnail-canvas';

    const numSpan = document.createElement('span');
    numSpan.className = 'pe-thumbnail-num';
    numSpan.textContent = `${isAr ? 'صفحة' : 'Page'} ${i}`;

    item.append(thumbCanvas, numSpan);
    item.addEventListener('click', () => goToPage(i));
    thumbnailsList.append(item);

    (async (canvas, pageNum) => {
      try {
        const page = await currentPdf.getPage(pageNum);
        const vp = page.getViewport({ scale: 0.22 });
        canvas.width = Math.round(vp.width);
        canvas.height = Math.round(vp.height);
        const tctx = canvas.getContext('2d');
        await page.render({ canvasContext: tctx, viewport: vp }).promise;
      } catch (e) {
        console.warn('Thumbnail render skipped:', e);
      }
    })(thumbCanvas, i);
  }
}

function highlightActiveThumbnail() {
  thumbnailsList.querySelectorAll('.pe-thumbnail-item').forEach((item) => {
    item.classList.toggle('active', Number(item.dataset.page) === currentPageNumber);
  });
}

function hexToRgb01(hex) {
  if (!hex || typeof hex !== 'string') return { r: 1, g: 1, b: 1 };
  let h = hex.trim();
  if (h.startsWith('#')) h = h.slice(1);
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6) return { r: 1, g: 1, b: 1 };
  const num = parseInt(h, 16);
  if (isNaN(num)) return { r: 1, g: 1, b: 1 };
  const r = ((num >> 16) & 255) / 255;
  const g = ((num >> 8) & 255) / 255;
  const b = (num & 255) / 255;
  if ((r * 255) >= 238 && (g * 255) >= 238 && (b * 255) >= 238) {
    return { r: 1, g: 1, b: 1 };
  }
  return { r, g, b };
}

// ==========================================
// High-Resolution Export via pdf-lib
// ==========================================

exportBtn.addEventListener('click', async () => {
  if (!originalBytes) return;
  if (typeof finalizeActiveTextEdit === 'function' && activeTextEditContext) {
    finalizeActiveTextEdit();
  }

  try {
    exportBtn.disabled = true;
    exportBtnText.textContent = isAr ? 'جارٍ المعالجة والحفظ...' : 'Processing & Saving...';

    const pdfDoc = await PDFDocument.load(originalBytes, { ignoreEncryption: true });

    // Flatten any interactive form fields to eliminate viewer default widget selection borders
    try {
      const form = pdfDoc.getForm();
      if (form) {
        form.flatten();
      }
    } catch (_) {}

    for (let p = 1; p <= totalPages; p++) {
      const annotations = pageAnnotations.get(p) || [];
      const modifiedTexts = pageModifiedTexts.get(p) ? Array.from(pageModifiedTexts.get(p).values()) : [];

      if (!annotations.length && !modifiedTexts.length) continue;

      const pdfLibPage = pdfDoc.getPage(p - 1);
      const ptWidth = pdfLibPage.getWidth();
      const ptHeight = pdfLibPage.getHeight();

      // Render high-res offscreen canvas (scale factor 3.0 for crisp vector quality)
      const scaleFactor = 3.0;
      const offscreen = document.createElement('canvas');
      offscreen.width = Math.round(ptWidth * scaleFactor);
      offscreen.height = Math.round(ptHeight * scaleFactor);
      const ctx = offscreen.getContext('2d');

      const renderW = offscreen.width;
      const renderH = offscreen.height;
      let hasOffscreenContent = false;

      // 1. In-Place Text Replacements & Clean Native Vector Background Masks
      for (const mod of modifiedTexts) {
        // Native vector rectangle masking original text directly on PDF page (zero stroke, zero raster halo)
        const maskNormX = (mod.isMoved && mod.origNormX !== undefined) ? mod.origNormX : mod.x;
        const maskNormY = (mod.isMoved && mod.origNormY !== undefined) ? mod.origNormY : mod.y;
        const maskNormW = (mod.isMoved && mod.origNormW !== undefined) ? mod.origNormW : mod.width;
        const maskNormH = (mod.isMoved && mod.origNormH !== undefined) ? mod.origNormH : mod.height;

        const maskX = maskNormX * ptWidth;
        const maskW = maskNormW * ptWidth;
        const maskH = maskNormH * ptHeight;
        const maskY = ptHeight - (maskNormY * ptHeight + maskH);
        const bgRgb = hexToRgb01(mod.bg || '#ffffff');

        pdfLibPage.drawRectangle({
          x: Math.max(0, maskX - 1),
          y: Math.max(0, maskY - 1),
          width: maskW + 2,
          height: maskH + 2,
          color: rgb(bgRgb.r, bgRgb.g, bgRgb.b),
          borderWidth: 0
        });

        // Draw new text glyphs onto offscreen canvas (NO background fill box on canvas to eliminate any alpha edge fringe)
        if (!mod.isDeleted && mod.newStr) {
          hasOffscreenContent = true;
          ctx.save();
          const mx = Math.round(mod.x * renderW);
          const my = Math.round(mod.y * renderH);
          const mw = Math.round(mod.width * renderW);
          const mh = Math.round(mod.height * renderH);

          const fontSize = Math.round(mod.fontSize * scaleFactor * 0.95);
          const fontStyle = mod.isItalic ? 'italic ' : '';
          const fontWeight = mod.isBold ? '700 ' : '400 ';
          ctx.font = `${fontStyle}${fontWeight}${fontSize}px ${mod.fontFamily || 'Cairo, sans-serif'}`;
          ctx.fillStyle = mod.color || '#111827';
          ctx.textBaseline = 'top';
          ctx.direction = 'inherit';

          let drawX = mx;
          if (mod.align === 'center') {
            ctx.textAlign = 'center';
            drawX = mx + mw / 2;
          } else if (mod.align === 'right' || /[\u0600-\u06FF]/.test(mod.newStr)) {
            ctx.textAlign = 'right';
            drawX = mx + mw - 2;
          } else {
            ctx.textAlign = 'left';
            drawX = mx + 2;
          }

          ctx.fillText(mod.newStr, drawX, my + 2);

          if (mod.isUnderline) {
            ctx.beginPath();
            ctx.strokeStyle = mod.color || '#111827';
            ctx.lineWidth = Math.max(1, scaleFactor);
            ctx.moveTo(mx, my + mh - 2);
            ctx.lineTo(mx + mw, my + mh - 2);
            ctx.stroke();
          }

          if (mod.isStrike) {
            ctx.beginPath();
            ctx.strokeStyle = mod.color || '#111827';
            ctx.lineWidth = Math.max(1, scaleFactor);
            ctx.moveTo(mx, my + mh / 2);
            ctx.lineTo(mx + mw, my + mh / 2);
            ctx.stroke();
          }
          ctx.restore();
        }
      }

      // 2. Redactions & Snip Masks (Clean vector fills on pdfLibPage)
      for (const ann of annotations) {
        if (ann.type === 'redact') {
          const rX = ann.x * ptWidth;
          const rW = ann.width * ptWidth;
          const rH = ann.height * ptHeight;
          const rY = ptHeight - (ann.y * ptHeight + rH);
          const rgbCol = hexToRgb01(ann.color || '#ffffff');
          pdfLibPage.drawRectangle({
            x: Math.max(0, rX - 1),
            y: Math.max(0, rY - 1),
            width: rW + 2,
            height: rH + 2,
            color: rgb(rgbCol.r, rgbCol.g, rgbCol.b),
            borderWidth: 0
          });
        }
      }

      // 3. Shapes
      for (const ann of annotations) {
        if (ann.type === 'shape') {
          if (ann.isOriginalMask) {
            // Original mask: cover on pdfLibPage with native vector rectangle, zero stroke
            const mX = ann.x * ptWidth;
            const mW = ann.width * ptWidth;
            const mH = ann.height * ptHeight;
            const mY = ptHeight - (ann.y * ptHeight + mH);
            const rgbCol = hexToRgb01(ann.fillColor || ann.color || ann.strokeColor || '#ffffff');
            pdfLibPage.drawRectangle({
              x: Math.max(0, mX - 1),
              y: Math.max(0, mY - 1),
              width: mW + 2,
              height: mH + 2,
              color: rgb(rgbCol.r, rgbCol.g, rgbCol.b),
              borderWidth: 0
            });
            continue;
          }

          hasOffscreenContent = true;
          ctx.save();
          ctx.globalAlpha = ann.opacity !== undefined ? ann.opacity : 1.0;
          const sx = ann.x * renderW;
          const sy = ann.y * renderH;
          const sw = ann.width * renderW;
          const sh = ann.height * renderH;
          const lineWidth = Math.max(0, (ann.strokeWidth || 0) * scaleFactor);

          const hasStroke = lineWidth > 0 && ann.strokeColor && ann.strokeColor !== 'transparent' && ann.strokeColor !== 'none';
          if (hasStroke) {
            ctx.strokeStyle = ann.strokeColor;
            ctx.lineWidth = lineWidth;
            if (ann.dash === 'dashed') ctx.setLineDash([6 * scaleFactor, 4 * scaleFactor]);
            else if (ann.dash === 'dotted') ctx.setLineDash([2 * scaleFactor, 3 * scaleFactor]);
            else ctx.setLineDash([]);
          }

          if (ann.fill === 'solid') ctx.fillStyle = ann.fillColor || ann.strokeColor;
          else if (ann.fill === 'semi') ctx.fillStyle = `${ann.strokeColor}40`;

          if (ann.shapeType === 'rect') {
            if (ann.fill !== 'none') ctx.fillRect(sx, sy, sw, sh);
            if (hasStroke) ctx.strokeRect(sx, sy, sw, sh);
          } else if (ann.shapeType === 'rounded-rect') {
            const rad = Math.min(12 * scaleFactor, sw / 4);
            ctx.beginPath();
            if (ctx.roundRect) ctx.roundRect(sx, sy, sw, sh, rad);
            else ctx.rect(sx, sy, sw, sh);
            if (ann.fill !== 'none') ctx.fill();
            if (hasStroke) ctx.stroke();
          } else if (ann.shapeType === 'circle') {
            ctx.beginPath();
            ctx.ellipse(sx + sw / 2, sy + sh / 2, sw / 2, sh / 2, 0, 0, Math.PI * 2);
            if (ann.fill !== 'none') ctx.fill();
            if (hasStroke) ctx.stroke();
          } else if (ann.shapeType === 'line') {
            ctx.beginPath();
            ctx.moveTo(sx, sy + sh / 2);
            ctx.lineTo(sx + sw, sy + sh / 2);
            if (hasStroke) ctx.stroke();
          } else if (ann.shapeType === 'arrow') {
            ctx.beginPath();
            ctx.moveTo(sx, sy + sh / 2);
            ctx.lineTo(sx + sw - 12 * scaleFactor, sy + sh / 2);
            if (hasStroke) ctx.stroke();

            ctx.beginPath();
            ctx.fillStyle = ann.strokeColor;
            ctx.moveTo(sx + sw, sy + sh / 2);
            ctx.lineTo(sx + sw - 14 * scaleFactor, sy + sh / 2 - 7 * scaleFactor);
            ctx.lineTo(sx + sw - 14 * scaleFactor, sy + sh / 2 + 7 * scaleFactor);
            ctx.closePath();
            ctx.fill();
          } else if (ann.shapeType === 'star') {
            const cx = sx + sw / 2;
            const cy = sy + sh / 2;
            const outerR = Math.min(sw, sh) / 2;
            const innerR = outerR * 0.45;
            ctx.beginPath();
            for (let i = 0; i < 10; i++) {
              const r = i % 2 === 0 ? outerR : innerR;
              const angle = (i * Math.PI) / 5 - Math.PI / 2;
              const px = cx + Math.cos(angle) * r;
              const py = cy + Math.sin(angle) * r;
              if (i === 0) ctx.moveTo(px, py);
              else ctx.lineTo(px, py);
            }
            ctx.closePath();
            if (ann.fill !== 'none') ctx.fill();
            if (hasStroke) ctx.stroke();
          } else if (ann.shapeType === 'checkbox') {
            if (ann.fill !== 'none') ctx.fillRect(sx, sy, sw, sh);
            if (hasStroke) ctx.strokeRect(sx, sy, sw, sh);
            ctx.beginPath();
            ctx.moveTo(sx + sw * 0.22, sy + sh * 0.52);
            ctx.lineTo(sx + sw * 0.44, sy + sh * 0.76);
            ctx.lineTo(sx + sw * 0.78, sy + sh * 0.24);
            ctx.stroke();
          }
          ctx.restore();
        }
      }

      // 4. Freehand Drawings & Highlights
      for (const ann of annotations) {
        if (ann.type === 'stroke' || ann.type === 'highlight') {
          if (!ann.points || ann.points.length < 2) continue;
          hasOffscreenContent = true;
          ctx.save();
          ctx.globalAlpha = ann.opacity !== undefined ? ann.opacity : 1.0;
          ctx.beginPath();
          ctx.moveTo(ann.points[0].x * renderW, ann.points[0].y * renderH);
          for (let i = 1; i < ann.points.length; i++) {
            ctx.lineTo(ann.points[i].x * renderW, ann.points[i].y * renderH);
          }
          ctx.strokeStyle = ann.color;
          ctx.lineWidth = ann.size * scaleFactor;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          if (ann.type === 'highlight') ctx.globalCompositeOperation = 'multiply';
          ctx.stroke();
          ctx.restore();
        }
      }

      // 5. Stamps
      for (const ann of annotations) {
        if (ann.type === 'stamp') {
          hasOffscreenContent = true;
          ctx.save();
          ctx.globalAlpha = ann.opacity !== undefined ? ann.opacity : 0.88;
          const stX = ann.x * renderW;
          const stY = ann.y * renderH;
          const stW = ann.width * renderW;
          const stH = ann.height * renderH;
          const centerX = stX + stW / 2;
          const centerY = stY + stH / 2;

          ctx.translate(centerX, centerY);
          ctx.rotate((-3.5 * Math.PI) / 180);

          // Double border stamp box
          ctx.fillStyle = `${ann.color}18`;
          ctx.fillRect(-stW / 2, -stH / 2, stW, stH);

          ctx.strokeStyle = ann.color;
          ctx.lineWidth = 3.5 * scaleFactor;
          ctx.strokeRect(-stW / 2 + 2 * scaleFactor, -stH / 2 + 2 * scaleFactor, stW - 4 * scaleFactor, stH - 4 * scaleFactor);

          ctx.lineWidth = 1.2 * scaleFactor;
          ctx.strokeRect(-stW / 2 + 6 * scaleFactor, -stH / 2 + 6 * scaleFactor, stW - 12 * scaleFactor, stH - 12 * scaleFactor);

          const fontSize = Math.round(stH * 0.44);
          ctx.font = `900 ${fontSize}px "Cairo", sans-serif`;
          ctx.fillStyle = ann.color;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(ann.text, 0, 0);
          ctx.restore();
        }
      }

      // 6. Form Marks (Check, Cross, Bullet, Circle, Line)
      for (const ann of annotations) {
        if (ann.type === 'mark') {
          hasOffscreenContent = true;
          ctx.save();
          ctx.globalAlpha = ann.opacity !== undefined ? ann.opacity : 1.0;
          const mkX = ann.x * renderW;
          const mkY = ann.y * renderH;
          const mkH = ann.height * renderH;

          const fontSize = Math.round(mkH * 0.95);
          ctx.font = `900 ${fontSize}px sans-serif`;
          ctx.fillStyle = ann.color;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(ann.mark, mkX + (ann.width * renderW) / 2, mkY + mkH / 2);
          ctx.restore();
        }
      }

      // 7. Sticky Notes
      for (const ann of annotations) {
        if (ann.type === 'note') {
          hasOffscreenContent = true;
          ctx.save();
          ctx.globalAlpha = ann.opacity !== undefined ? ann.opacity : 0.95;
          const ntX = ann.x * renderW;
          const ntY = ann.y * renderH;
          const ntW = ann.width * renderW;
          const ntH = ann.height * renderH;

          ctx.fillStyle = ann.color;
          ctx.fillRect(ntX, ntY, ntW, ntH);

          ctx.fillStyle = 'rgba(0,0,0,0.08)';
          ctx.fillRect(ntX, ntY, ntW, 16 * scaleFactor);

          const fontSize = Math.round(14 * scaleFactor);
          ctx.font = `${fontSize}px "Cairo", sans-serif`;
          ctx.fillStyle = '#1e293b';
          ctx.textBaseline = 'top';
          ctx.direction = 'inherit';

          const lines = ann.text.split('\n');
          let textY = ntY + 20 * scaleFactor;
          for (const line of lines) {
            ctx.fillText(line, ntX + 8 * scaleFactor, textY);
            textY += fontSize * 1.35;
          }
          ctx.restore();
        }
      }

      // 8. Images & Signatures (Direct PDF Page Embedding - Zero Canvas Border, Crisp & Transparent)
      for (const ann of annotations) {
        if (ann.type === 'image') {
          try {
            let src = ann.src;
            // Clean outer background if lifted from canvas or containing background
            if (ann.isFromSlice || ann.id?.startsWith('img-') || ann.id?.startsWith('image_')) {
              src = await cleanImagePerimeterBackground(src);
            }

            const imgBytes = await fetch(src).then((res) => res.arrayBuffer());
            let embeddedImg;
            if (src.startsWith('data:image/jpeg') || src.startsWith('data:image/jpg')) {
              embeddedImg = await pdfDoc.embedJpg(imgBytes);
            } else {
              embeddedImg = await pdfDoc.embedPng(imgBytes);
            }

            const imX = ann.x * ptWidth;
            const imW = ann.width * ptWidth;
            const imH = ann.height * ptHeight;
            const imY = ptHeight - (ann.y * ptHeight + imH);

            pdfLibPage.drawImage(embeddedImg, {
              x: imX,
              y: imY,
              width: imW,
              height: imH,
              opacity: ann.opacity !== undefined ? ann.opacity : 1.0,
              rotate: ann.rotation ? degrees(ann.rotation) : undefined
            });
          } catch (imgErr) {
            console.warn('Direct image embedding failed:', imgErr);
          }
        }
      }

      // 9. User Added Texts
      for (const ann of annotations) {
        if (ann.type === 'text') {
          hasOffscreenContent = true;
          ctx.save();
          ctx.globalAlpha = ann.opacity !== undefined ? ann.opacity : 1.0;
          const fontSize = Math.round(ann.fontSize * scaleFactor * (ptWidth / 800) * 1.3);
          const fontStyle = ann.italic ? 'italic ' : '';
          const fontWeight = ann.bold ? '700 ' : '400 ';
          ctx.font = `${fontStyle}${fontWeight}${fontSize}px ${ann.fontFamily}`;
          ctx.fillStyle = ann.color;
          ctx.textBaseline = 'top';
          ctx.direction = 'inherit';

          const lines = ann.text.split('\n');
          const lineHeight = fontSize * 1.35;
          let textY = ann.y * renderH + 4 * scaleFactor;

          for (const line of lines) {
            ctx.fillText(line, ann.x * renderW + 4 * scaleFactor, textY);
            textY += lineHeight;
          }
          ctx.restore();
        }
      }

      // Embed high-res canvas PNG into pdf-lib page only if there is offscreen content to overlay
      if (hasOffscreenContent) {
        const pngDataUrl = offscreen.toDataURL('image/png');
        const pngImageBytes = await fetch(pngDataUrl).then((res) => res.arrayBuffer());
        const embeddedPng = await pdfDoc.embedPng(pngImageBytes);

        pdfLibPage.drawImage(embeddedPng, {
          x: 0,
          y: 0,
          width: ptWidth,
          height: ptHeight
        });
      }
    }

    const modifiedPdfBytes = await pdfDoc.save();
    const downloadName = currentFilename.replace(/\.pdf$/i, '_edited.pdf');
    saveBlob(new Blob([modifiedPdfBytes], { type: 'application/pdf' }), downloadName);

    trackEvent('tool_completed', 'pdf-editor', {
      pages: totalPages,
      modifiedPages: pageModifiedTexts.size + pageAnnotations.size
    });
  } catch (err) {
    console.error('Export failed:', err);
    trackEvent('tool_failed', 'pdf-editor');
    showAlert(isAr ? `حدث خطأ أثناء تصدير الملف: ${err.message}` : `Export failed: ${err.message}`, { type: 'error' });
  } finally {
    exportBtn.disabled = false;
    exportBtnText.textContent = isAr ? 'حفظ وتنزيل PDF' : 'Save & Download PDF';
  }
});


// ==========================================
// In-Memory Word DOCX Flow Model & Export
// ==========================================

function createDocxTableFromBlock(block) {
  const docxRows = block.rows.map((row, rowIdx) => {
    const cells = row.cells.map((cell, colIdx) => {
      const text = cell.text || '';
      const isArabic = /[\u0600-\u06FF]/.test(text);
      const hexColor = (cell.color || '#111827').replace('#', '');
      const hexBg = cell.bg && cell.bg !== '#ffffff' ? cell.bg.replace('#', '') : (rowIdx === 0 ? 'F8FAFC' : undefined);
      const font = cell.docxFont || 'Traditional Arabic';

      let align = DocxAlignmentType.CENTER;
      if (cell.align === 'right' || isArabic) align = DocxAlignmentType.RIGHT;
      else if (cell.align === 'left') align = DocxAlignmentType.LEFT;

      const colPct = cell.colPct || block.colPctList?.[colIdx] || Math.round(100 / Math.max(1, row.cells.length));
      const cellWidthDxa = Math.round((colPct / 100) * 9200);

      return new DocxTableCell({
        width: {
          size: cellWidthDxa,
          type: DocxWidthType.DXA
        },
        borders: {
          top: { style: DocxBorderStyle.SINGLE, size: 2, color: '1E293B' },
          bottom: { style: DocxBorderStyle.SINGLE, size: 2, color: '1E293B' },
          left: { style: DocxBorderStyle.SINGLE, size: 2, color: '1E293B' },
          right: { style: DocxBorderStyle.SINGLE, size: 2, color: '1E293B' }
        },
        shading: hexBg ? { fill: hexBg } : undefined,
        children: [
          new DocxParagraph({
            alignment: align,
            bidirectional: isArabic,
            children: [
              new DocxTextRun({
                text: text,
                font: font,
                size: Math.round((cell.fontSize || 14) * 1.6),
                bold: cell.isBold,
                italics: cell.isItalic,
                strike: cell.isStrike,
                underline: cell.isUnderline ? {} : undefined,
                color: hexColor,
                rightToLeft: isArabic
              })
            ]
          })
        ]
      });
    });

    return new DocxTableRow({
      children: cells
    });
  });

  return new DocxTable({
    rows: docxRows
  });
}

function createDocxBorderlessRowFromBlock(block) {
  const cellCount = Math.max(1, block.cells.length);
  const cellWidthDxa = Math.round(9200 / cellCount);

  const docxCells = block.cells.map((cell) => {
    const text = cell.text || '';
    const isArabic = /[\u0600-\u06FF]/.test(text);
    const hexColor = (cell.color || '#111827').replace('#', '');
    const font = cell.docxFont || 'Traditional Arabic';

    let align = isArabic ? DocxAlignmentType.RIGHT : DocxAlignmentType.LEFT;
    if (cell.align === 'center') align = DocxAlignmentType.CENTER;
    if (cell.align === 'right') align = DocxAlignmentType.RIGHT;
    if (cell.align === 'left') align = DocxAlignmentType.LEFT;

    return new DocxTableCell({
      width: {
        size: cellWidthDxa,
        type: DocxWidthType.DXA
      },
      borders: {
        top: { style: DocxBorderStyle.NONE },
        bottom: { style: DocxBorderStyle.NONE },
        left: { style: DocxBorderStyle.NONE },
        right: { style: DocxBorderStyle.NONE }
      },
      children: [
        new DocxParagraph({
          alignment: align,
          bidirectional: isArabic,
          children: [
            new DocxTextRun({
              text: text,
              font: font,
              size: Math.round((cell.fontSize || 14) * 1.6),
              bold: cell.isBold,
              italics: cell.isItalic,
              strike: cell.isStrike,
              underline: cell.isUnderline ? {} : undefined,
              color: hexColor,
              rightToLeft: isArabic
            })
          ]
        })
      ]
    });
  });

  return new DocxTable({
    rows: [
      new DocxTableRow({
        children: docxCells
      })
    ]
  });
}

async function exportAsDocx() {
  if (!currentPdf || !originalBytes) return;
  if (typeof finalizeActiveTextEdit === 'function' && activeTextEditContext) {
    finalizeActiveTextEdit();
  }

  try {
    if (exportDocxBtn) exportDocxBtn.disabled = true;
    if (exportDocxBtnText) exportDocxBtnText.textContent = isAr ? 'جارٍ تحويل المستند إلى Word...' : 'Generating Word (.docx)...';

    const sections = [];

    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      let docData = pageDocData.get(pageNum);

      // If page has not been cached, extract text and build docData on the fly
      if (!docData) {
        const page = await currentPdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1.0 });
        const textContent = await page.getTextContent();
        const rawItems = textContent.items || [];
        const styles = textContent.styles || {};
        docData = buildPageDocModel(rawItems, viewport, styles, 800, 1100);
      }

      // Synchronize in-place user modifications for this page into docData
      const pageMods = pageModifiedTexts.get(pageNum);
      if (pageMods && pageMods.size > 0) {
        for (const block of docData.blocks) {
          if (block.type === 'table' && block.rows) {
            for (const row of block.rows) {
              for (const cell of row.cells) {
                const mod = pageMods.get(cell.id) || (cell.text && Array.from(pageMods.values()).find((m) => m.origStr === cell.text));
                if (mod) {
                  if (mod.isDeleted) cell.text = '';
                  else {
                    cell.text = mod.newStr;
                    cell.isBold = mod.isBold;
                    cell.isItalic = mod.isItalic;
                    cell.fontSize = mod.fontSize;
                    cell.color = mod.color;
                  }
                }
              }
            }
          } else if (['header-row', 'footer-row', 'info-row'].includes(block.type) && block.cells) {
            for (const cell of block.cells) {
              const mod = pageMods.get(cell.id) || (cell.text && Array.from(pageMods.values()).find((m) => m.origStr === cell.text));
              if (mod) {
                if (mod.isDeleted) cell.text = '';
                else {
                  cell.text = mod.newStr;
                  cell.isBold = mod.isBold;
                  cell.isItalic = mod.isItalic;
                  cell.fontSize = mod.fontSize;
                  cell.color = mod.color;
                }
              }
            }
          } else {
            const mod = pageMods.get(block.id) || (block.text && Array.from(pageMods.values()).find((m) => m.origStr === block.text));
            if (mod) {
              if (mod.isDeleted) {
                block.text = '';
              } else {
                block.text = mod.newStr;
                block.isBold = mod.isBold;
                block.isItalic = mod.isItalic;
                block.fontSize = mod.fontSize;
                block.color = mod.color;
                block.align = mod.align;
                if (mod.fontFamily) {
                  const fontLower = mod.fontFamily.toLowerCase();
                  if (fontLower.includes('traditional')) block.docxFont = 'Traditional Arabic';
                  else if (fontLower.includes('simplified')) block.docxFont = 'Simplified Arabic';
                  else if (fontLower.includes('cairo')) block.docxFont = 'Cairo';
                  else if (fontLower.includes('amiri')) block.docxFont = 'Amiri';
                  else if (fontLower.includes('tahoma')) block.docxFont = 'Tahoma';
                  else if (fontLower.includes('arial')) block.docxFont = 'Arial';
                  else if (fontLower.includes('times')) block.docxFont = 'Times New Roman';
                }
              }
            }
          }
        }
      }

      for (const block of docData.blocks) {
        if (block.type === 'table') {
          children.push(createDocxTableFromBlock(block));
        } else if (block.type === 'header-row' || block.type === 'footer-row' || block.type === 'info-row') {
          children.push(createDocxBorderlessRowFromBlock(block));
        } else {
          const text = block.text || '';
          if (!text) continue;
          const isArabic = /[\u0600-\u06FF]/.test(text);
          const hexColor = (block.color || '#111827').replace('#', '');
          const font = block.docxFont || 'Traditional Arabic';
          let align = isArabic ? DocxAlignmentType.RIGHT : DocxAlignmentType.LEFT;
          if (block.align === 'center') align = DocxAlignmentType.CENTER;
          if (block.align === 'right') align = DocxAlignmentType.RIGHT;
          if (block.align === 'left') align = DocxAlignmentType.LEFT;

          children.push(new DocxParagraph({
            alignment: align,
            bidirectional: isArabic,
            children: [
              new DocxTextRun({
                text: text,
                font: font,
                size: Math.round((block.fontSize || 14) * 1.6),
                bold: block.isBold,
                italics: block.isItalic,
                strike: block.isStrike,
                underline: block.isUnderline ? {} : undefined,
                color: hexColor,
                rightToLeft: isArabic
              })
            ]
          }));
        }
      }

      // Include user-added text annotations in docx output
      const pageAnns = pageAnnotations.get(pageNum) || [];
      for (const ann of pageAnns) {
        if (ann.type === 'text' && ann.text && ann.text.trim()) {
          const isArabic = /[\u0600-\u06FF]/.test(ann.text);
          children.push(new DocxParagraph({
            alignment: isArabic ? DocxAlignmentType.RIGHT : DocxAlignmentType.LEFT,
            bidirectional: isArabic,
            children: [
              new DocxTextRun({
                text: ann.text,
                font: 'Traditional Arabic',
                size: Math.round((ann.fontSize || 14) * 1.6),
                bold: !!ann.bold,
                italics: !!ann.italic,
                color: (ann.color || '#111827').replace('#', ''),
                rightToLeft: isArabic
              })
            ]
          }));
        }
      }

      sections.push({
        properties: {
          page: {
            margin: {
              top: 720,
              right: 720,
              bottom: 720,
              left: 720
            }
          }
        },
        children
      });
    }

    const doc = new DocxDocument({
      sections
    });

    const blob = await DocxPacker.toBlob(doc);
    const downloadName = currentFilename.replace(/\.pdf$/i, '.docx');
    saveBlob(blob, downloadName);

    trackEvent('tool_completed', 'pdf-editor', {
      pages: totalPages,
      modifiedPages: pageDocData.size,
      format: 'docx'
    });
  } catch (err) {
    console.error('Failed to export DOCX:', err);
    trackEvent('tool_failed', 'pdf-editor');
    showAlert(isAr ? `حدث خطأ أثناء تصدير Word: ${err.message}` : `Failed to export Word: ${err.message}`, { type: 'error' });
  } finally {
    if (exportDocxBtn) exportDocxBtn.disabled = false;
    if (exportDocxBtnText) exportDocxBtnText.textContent = isAr ? 'حفظ كـ Word (.docx)' : 'Save as Word (.docx)';
  }

}

if (exportDocxBtn) {
  exportDocxBtn.addEventListener('click', exportAsDocx);
}
